// The Direct Laser Source — Step-by-Step Builder JS

document.addEventListener('DOMContentLoaded', function () {

    // ==========================================
    // State
    // ==========================================
    const state = {
        currentStep: 1,
        totalSteps: 5,
        category: null,
        garment: null,
        color: null,
        decoration: 'dtf',        // 'dtf' = Full Color Print internally
        activeLocation: 'front',   // which location tab is selected
        viewSide: 'front',         // 'front' | 'back' — driven by activeLocation
        locations: {
            'front':         { logoFile: null, logoDataUrl: null, logoMaskUrl: null, renderedLogos: {}, scale: 1, rotation: 0, decoration: null, posLeft: null, posTop: null, printSize: null },
            'back':          { logoFile: null, logoDataUrl: null, logoMaskUrl: null, renderedLogos: {}, scale: 1, rotation: 0, decoration: null, posLeft: null, posTop: null, printSize: null },
            'left-sleeve':   { logoFile: null, logoDataUrl: null, logoMaskUrl: null, renderedLogos: {}, scale: 1, rotation: 0, decoration: null, posLeft: null, posTop: null, printSize: null },
            'right-sleeve':  { logoFile: null, logoDataUrl: null, logoMaskUrl: null, renderedLogos: {}, scale: 1, rotation: 0, decoration: null, posLeft: null, posTop: null, printSize: null },
        },
        notes: '',
        sizes: { XS: 0, S: 0, M: 0, L: 0, XL: 0, '2XL': 0, '3XL': 0 },
        desiredSizes: {},  // remembers pre-clamp quantities for restore on color switch
        waiveTapeFee: false,  // true = customer already has their logo digitized
        sanmarInventoryCache: {},
        sanmarInventoryInFlight: {},
        ssInventoryCache: {},
        ssInventoryInFlight: {},
        ssStyleInventoryCache: {},
        ssStyleInventoryInFlight: {},
        ssPriceCache: {},       // styleID -> { prices: {size: cost}, basePrice: num }
        ssPriceInFlight: {}
    };

    // Helper: get active location's data
    function activeLoc() { return state.locations[state.activeLocation]; }

    // Helper: check if a location has artwork (file upload OR AI-generated)
    function locHasArtwork(loc) { return !!(loc && (loc.logoFile || (loc.logoDataUrl && loc.aiMeta))); }

    function getSelectedColorObject(garment, colorHex, colorName) {
        if (!garment) return null;
        if (colorName) {
            var byName = garment.colors.find(function (c) { return c.name === colorName; });
            if (byName) return byName;
        }
        if (colorHex) {
            var byHex = garment.colors.find(function (c) { return c.hex === colorHex; });
            if (byHex) return byHex;
        }
        return garment.colors[0] || null;
    }

    function getLocationAvailability(garment, colorHex, colorName) {
        var targetGarment = garment || state.garment;
        var colorObj = getSelectedColorObject(targetGarment, colorHex || state.color, colorName || state.colorName);
        var hasFront = !!((colorObj && colorObj.front) || (targetGarment && targetGarment.styleImage));
        var hasBack = !!(colorObj && colorObj.back);
        // Beanies: allow back even when no back image — we'll mirror the front
        var cat = state.category || (targetGarment && targetGarment.icon) || '';
        if (!hasBack && cat === 'beanies' && hasFront && colorObj && colorObj.front) {
            hasBack = true;
        }
        return {
            'front': hasFront,
            'back': hasBack,
            'left-sleeve': !!(colorObj && colorObj.side),
            'right-sleeve': !!(colorObj && colorObj.side),
        };
    }

    function isLocationAvailable(locKey, garment, colorHex, colorName) {
        return !!getLocationAvailability(garment, colorHex, colorName)[locKey];
    }

    function getActiveLocationEntries(sourceLocations) {
        var locations = sourceLocations || state.locations;
        return Object.entries(locations).filter(function (entry) {
            var locKey = entry[0], data = entry[1];
            return isLocationAvailable(locKey) && locHasArtwork(data);
        });
    }

    function getActiveLocationsObject(sourceLocations) {
        return getActiveLocationEntries(sourceLocations).reduce(function (acc, entry) {
            acc[entry[0]] = entry[1];
            return acc;
        }, {});
    }

    function garmentHasRenderableImages(garment) {
        if (!garment) return false;
        if (garment.styleImage) return true;
        return (garment.colors || []).some(function (colorObj) {
            return !!(colorObj && (colorObj.front || colorObj.back || colorObj.side));
        });
    }

    function isSanmarBrand(brand) {
        return brand === 'Nike' || brand === 'Carhartt' || brand === 'Port & Company' || brand === 'New Era';
    }

    function isSSBrand(brand) {
        return !isSanmarBrand(brand);
    }

    function getBuilderApiBase() {
        var uploader = window.__BUILDER_UPLOAD;
        return uploader && uploader.getApiBase ? uploader.getApiBase() : '';
    }

    // Size-key aliases shared by inventory + pricing (supplier APIs sometimes
    // return XXL instead of 2XL, lowercase, trailing whitespace, etc.)
    // OSFM (One-Size-Fits-Most) in catalog → OSFA in SanMar pricing API response.
    // ADJUSTABLE → OSFA: adjustable-strap caps are single-price; the S&S API
    // returns "Adjustable" as the size key, which round-trips via this alias.
    var SIZE_ALIASES = {
        'XXL': '2XL',
        'XXXL': '3XL',
        'XXXXL': '4XL',
        'XXXXXL': '5XL',
        'XXXXXXL': '6XL',
        '2X': '2XL',
        '3X': '3XL',
        '4X': '4XL',
        '5X': '5XL',
        '6X': '6XL',
        'OSFM': 'OSFA',
        'ONESIZE': 'OSFA',
        'OS': 'OSFA',
        'ADJUSTABLE': 'OSFA'
    };
    function canonSize(size) {
        if (size === null || size === undefined) return '';
        var cleaned = String(size).trim().replace(/\s+/g, '').toUpperCase();
        return SIZE_ALIASES[cleaned] || cleaned;
    }

    // Supplier APIs (both S&S and SanMar) misbehave on color strings with
    // format drift. Normalize only for the outbound request — keep catalog /
    // state names untouched so the UI + cache keys stay stable.
    //   • SanMar rejects space-after-slash ("Black/ White" → "Black/White").
    //   • S&S silently returns partial pricing (only XS) for colors with a
    //     " (disc)" / " (Disc)" / " (discontinued)" suffix — strip it.
    //   • S&S silently returns partial pricing for trailing / doubled
    //     internal whitespace ("Khaki/Legion Blue CMB  ") — collapse it.
    function normalizeSupplierColor(name) {
        if (!name) return '';
        return String(name)
            .replace(/\s*\(\s*(?:disc|discontinued|retired|disco)\s*\)\s*/gi, '')
            .replace(/\s*\/\s*/g, '/')
            .replace(/\s+/g, ' ')
            .trim();
    }

    function normalizeInventoryMap(rawInventory, knownSizes) {
        if (!rawInventory || typeof rawInventory !== 'object') return null;

        var normalized = {};

        Object.keys(rawInventory).forEach(function (key) {
            var qty = parseInt(rawInventory[key], 10);
            if (isNaN(qty)) return;
            normalized[canonSize(key)] = Math.max(0, qty);
        });

        if (knownSizes && knownSizes.length) {
            knownSizes.forEach(function (sizeLabel) {
                var canonical = canonSize(sizeLabel);
                if (normalized[canonical] === undefined) {
                    normalized[canonical] = 0;
                }
            });
        }

        return normalized;
    }

    function getColorInventoryTotal(colorObj) {
        if (!colorObj || !colorObj.inventory) return null;
        var hasValue = false;
        var total = Object.values(colorObj.inventory).reduce(function (sum, value) {
            var qty = parseInt(value, 10);
            if (isNaN(qty)) return sum;
            hasValue = true;
            return sum + Math.max(0, qty);
        }, 0);
        return hasValue ? total : null;
    }

    function isColorKnownOutOfStock(colorObj) {
        return !!(colorObj && colorObj._inventoryLiveLoaded) && getColorInventoryTotal(colorObj) === 0;
    }

    function applyAvailabilityToColorControl(control, colorObj) {
        if (!control || !colorObj) return;
        var loading = !!colorObj._inventoryLoading;
        var outOfStock = !loading && isColorKnownOutOfStock(colorObj);
        var disabled = loading || outOfStock;
        var title = colorObj.name;

        if (loading) title += ' (Checking availability)';
        if (outOfStock) title += ' (Out of Stock)';

        control.classList.toggle('swatch-oos', outOfStock);
        control.classList.toggle('swatch-loading', loading);
        control.classList.toggle('qs-color-oos', outOfStock);
        control.classList.toggle('qs-color-loading', loading);
        control.disabled = disabled;
        control.setAttribute('aria-disabled', disabled ? 'true' : 'false');
        control.title = title;
    }

    function refreshColorControlAvailability(garment, colorName) {
        garment = garment || state.garment;
        if (!garment || !colorName) return;
        var colorObj = garment.colors.find(function (c) { return c.name === colorName; });
        if (!colorObj) return;
        document.querySelectorAll('.color-swatch[data-name="' + CSS.escape(colorName) + '"], .qs-color-dot[data-name="' + CSS.escape(colorName) + '"]').forEach(function (control) {
            applyAvailabilityToColorControl(control, colorObj);
        });
    }

    function refreshAllColorControlAvailability(garment) {
        garment = garment || state.garment;
        if (!garment || !(garment.colors || []).length) return;
        garment.colors.forEach(function (colorObj) {
            refreshColorControlAvailability(garment, colorObj.name);
        });
    }

    function applyInventoryToColor(garment, colorName, inventoryMap) {
        if (!garment || !colorName || !inventoryMap) return;
        var colorObj = garment.colors.find(function (c) { return c.name === colorName; });
        if (!colorObj) return;

        var applied = {};
        (garment.sizes || []).forEach(function (sizeLabel) {
            var canonical = canonSize(sizeLabel);
            var qty = inventoryMap[canonical];
            applied[sizeLabel] = qty === undefined ? 0 : qty;
        });

        colorObj.inventory = applied;
        colorObj._inventoryLoading = false;
        colorObj._inventoryLiveLoaded = true;
        refreshColorControlAvailability(garment, colorName);
        var currentColorBelongsToGarment = !!(
            state.colorName &&
            (garment.colors || []).some(function (c) { return c.name === state.colorName; })
        );
        if (state.garment === garment && !currentColorBelongsToGarment && getColorInventoryTotal(colorObj) > 0) {
            selectColor(colorName);
            return;
        }
        if (state.garment === garment && state.colorName === colorName) {
            updateStockBadges();
        }
    }

    function applyInventoryToSelectedColor(inventoryMap) {
        applyInventoryToColor(state.garment, state.colorName, inventoryMap);
    }

    /** Update a single color swatch's OOS visual state after live inventory arrives */
    function updateSwatchOOSState(colorName, inventory) {
        if (!state.garment || !colorName || !inventory) return;
        applyInventoryToColor(state.garment, colorName, inventory);
    }

    function getInventoryColorCacheKey(garment, colorName) {
        if (!garment || !colorName) return '';
        if (isSanmarBrand(garment.brand)) {
            var style = garment.styleID || garment.styleName || '';
            return style ? (style + '|' + colorName).toLowerCase() : '';
        }
        var styleID = garment.styleID || '';
        return styleID ? (styleID + '|' + colorName).toLowerCase() : '';
    }

    function getInventoryCacheForGarment(garment) {
        return isSanmarBrand(garment && garment.brand) ? state.sanmarInventoryCache : state.ssInventoryCache;
    }

    function getInventoryInFlightForGarment(garment) {
        return isSanmarBrand(garment && garment.brand) ? state.sanmarInventoryInFlight : state.ssInventoryInFlight;
    }

    function getSSStyleInventoryCacheKey(garment) {
        var styleID = garment && garment.styleID;
        return styleID ? String(styleID).toLowerCase() : '';
    }

    function getInventoryColorNameKey(name) {
        return normalizeSupplierColor(name).toLowerCase().replace(/\s+/g, '');
    }

    function getInventoryFromColorMap(inventoryByColor, colorName) {
        if (!inventoryByColor || !colorName) return null;
        if (inventoryByColor[colorName]) return inventoryByColor[colorName];

        var targetKey = getInventoryColorNameKey(colorName);
        var matchedKey = Object.keys(inventoryByColor).find(function (name) {
            return getInventoryColorNameKey(name) === targetKey;
        });
        return matchedKey ? inventoryByColor[matchedKey] : null;
    }

    function applySSStyleInventoryToGarment(garment, inventoryByColor) {
        if (!garment || !inventoryByColor) return;
        var cache = getInventoryCacheForGarment(garment);

        (garment.colors || []).forEach(function (colorObj) {
            if (!colorObj || colorObj._inventoryLiveLoaded) return;

            var rawInventory = getInventoryFromColorMap(inventoryByColor, colorObj.name) || {};
            var normalized = normalizeInventoryMap(rawInventory, garment.sizes || []);
            if (!normalized) return;

            var cacheKey = getInventoryColorCacheKey(garment, colorObj.name);
            if (cacheKey) cache[cacheKey] = normalized;
            applyInventoryToColor(garment, colorObj.name, normalized);
        });
    }

    function canFetchLiveInventoryForColor(garment, colorObj) {
        if (!garment || !colorObj || colorObj._inventoryLiveLoaded) return false;
        return !!getInventoryColorCacheKey(garment, colorObj.name);
    }

    function markVisibleColorsChecking(garment) {
        if (!garment || !(garment.colors || []).length) return;
        if (!getBuilderApiBase()) return;
        garment.colors.forEach(function (colorObj) {
            if (!colorHasRenderableImages(garment, colorObj)) return;
            if (!canFetchLiveInventoryForColor(garment, colorObj)) return;
            var cacheKey = getInventoryColorCacheKey(garment, colorObj.name);
            var cache = getInventoryCacheForGarment(garment);
            if (cacheKey && cache[cacheKey]) return;
            colorObj._inventoryLoading = true;
        });
    }

    function fetchInventoryForColor(garment, colorName) {
        if (!garment || !colorName) return;

        var apiBase = getBuilderApiBase();
        if (!apiBase) return;

        var colorObj = garment.colors.find(function (c) { return c.name === colorName; });
        if (!colorObj || colorObj._inventoryLiveLoaded) return;

        var isSanmar = isSanmarBrand(garment.brand);
        var style = isSanmar ? (garment.styleID || garment.styleName || '') : '';
        var styleID = isSanmar ? '' : (garment.styleID || '');
        if (isSanmar && !style) return;
        if (!isSanmar && !styleID) return;

        var cacheKey = getInventoryColorCacheKey(garment, colorName);
        if (!cacheKey) return;

        var cache = getInventoryCacheForGarment(garment);
        var inFlight = getInventoryInFlightForGarment(garment);
        if (cache[cacheKey]) {
            applyInventoryToColor(garment, colorName, cache[cacheKey]);
            return;
        }

        if (!isSanmar) {
            var styleCacheKey = getSSStyleInventoryCacheKey(garment);
            var styleInventory = styleCacheKey ? state.ssStyleInventoryCache[styleCacheKey] : null;
            if (styleInventory) {
                var rawFromStyle = getInventoryFromColorMap(styleInventory, colorName) || {};
                var normalizedFromStyle = normalizeInventoryMap(rawFromStyle, garment.sizes || []);
                if (normalizedFromStyle) {
                    cache[cacheKey] = normalizedFromStyle;
                    applyInventoryToColor(garment, colorName, normalizedFromStyle);
                    return;
                }
            }
            if (styleCacheKey && state.ssStyleInventoryInFlight[styleCacheKey]) {
                colorObj._inventoryLoading = true;
                refreshColorControlAvailability(garment, colorName);
                return;
            }
        }

        if (inFlight[cacheKey]) return;

        inFlight[cacheKey] = true;
        colorObj._inventoryLoading = true;
        refreshColorControlAvailability(garment, colorName);

        var uploader = window.__BUILDER_UPLOAD;
        var authToken = uploader && uploader.getAuthToken ? uploader.getAuthToken() : '';
        var apiColor = normalizeSupplierColor(colorName);
        var sizes = (garment.sizes || []).join(',');
        var url = isSanmar
            ? apiBase + '/api/sanmar-inventory?style=' + encodeURIComponent(style)
            + '&color=' + encodeURIComponent(apiColor)
            + '&sizes=' + encodeURIComponent(sizes)
            : apiBase + '/api/ss-inventory?styleID=' + encodeURIComponent(styleID)
            + '&color=' + encodeURIComponent(apiColor)
            + '&sizes=' + encodeURIComponent(sizes);

        var xhr = new XMLHttpRequest();
        xhr.open('GET', url, true);
        if (authToken) {
            xhr.setRequestHeader('X-Builder-Token', authToken);
        }
        xhr.onreadystatechange = function () {
            if (xhr.readyState !== 4) return;
            delete inFlight[cacheKey];
            colorObj._inventoryLoading = false;

            if (xhr.status < 200 || xhr.status >= 300) {
                refreshColorControlAvailability(garment, colorName);
                return;
            }

            var data;
            try {
                data = JSON.parse(xhr.responseText || '{}');
            } catch (e) {
                refreshColorControlAvailability(garment, colorName);
                return;
            }

            var rawInventory = data.inventory || data.sizes || (data.response && data.response.inventory);
            var normalized = normalizeInventoryMap(rawInventory, garment.sizes || []);
            if (!normalized) {
                refreshColorControlAvailability(garment, colorName);
                return;
            }

            cache[cacheKey] = normalized;
            applyInventoryToColor(garment, colorName, normalized);
        };
        xhr.send();
    }

    function fetchSSInventoryForVisibleColors(garment) {
        if (!garment || !(garment.colors || []).length) return;
        var apiBase = getBuilderApiBase();
        if (!apiBase) return;

        var styleCacheKey = getSSStyleInventoryCacheKey(garment);
        if (!styleCacheKey) return;

        if (state.ssStyleInventoryCache[styleCacheKey]) {
            applySSStyleInventoryToGarment(garment, state.ssStyleInventoryCache[styleCacheKey]);
            return;
        }

        garment.colors.forEach(function (colorObj) {
            if (!colorHasRenderableImages(garment, colorObj)) return;
            if (!canFetchLiveInventoryForColor(garment, colorObj)) return;
            var cacheKey = getInventoryColorCacheKey(garment, colorObj.name);
            var cache = getInventoryCacheForGarment(garment);
            if (cacheKey && cache[cacheKey]) return;
            colorObj._inventoryLoading = true;
            refreshColorControlAvailability(garment, colorObj.name);
        });

        if (state.ssStyleInventoryInFlight[styleCacheKey]) return;
        state.ssStyleInventoryInFlight[styleCacheKey] = true;

        var uploader = window.__BUILDER_UPLOAD;
        var authToken = uploader && uploader.getAuthToken ? uploader.getAuthToken() : '';
        var url = apiBase + '/api/ss-inventory?styleID=' + encodeURIComponent(garment.styleID || '');

        var xhr = new XMLHttpRequest();
        xhr.open('GET', url, true);
        if (authToken) {
            xhr.setRequestHeader('X-Builder-Token', authToken);
        }
        xhr.onreadystatechange = function () {
            if (xhr.readyState !== 4) return;
            delete state.ssStyleInventoryInFlight[styleCacheKey];

            if (xhr.status < 200 || xhr.status >= 300) {
                garment.colors.forEach(function (colorObj) {
                    colorObj._inventoryLoading = false;
                    refreshColorControlAvailability(garment, colorObj.name);
                });
                return;
            }

            var data;
            try {
                data = JSON.parse(xhr.responseText || '{}');
            } catch (e) {
                garment.colors.forEach(function (colorObj) {
                    colorObj._inventoryLoading = false;
                    refreshColorControlAvailability(garment, colorObj.name);
                });
                return;
            }

            var inventoryByColor = data.inventoryByColor || {};
            state.ssStyleInventoryCache[styleCacheKey] = inventoryByColor;
            applySSStyleInventoryToGarment(garment, inventoryByColor);
        };
        xhr.send();
    }

    function fetchInventoryForVisibleColors(garment) {
        if (!garment || !(garment.colors || []).length) return;
        if (!getBuilderApiBase()) return;
        if (isSSBrand(garment.brand)) {
            fetchSSInventoryForVisibleColors(garment);
            return;
        }
        garment.colors.forEach(function (colorObj) {
            if (!colorHasRenderableImages(garment, colorObj)) return;
            if (!canFetchLiveInventoryForColor(garment, colorObj)) return;
            var cacheKey = getInventoryColorCacheKey(garment, colorObj.name);
            var cache = getInventoryCacheForGarment(garment);
            if (cacheKey && cache[cacheKey]) return;
            colorObj._inventoryLoading = true;
            refreshColorControlAvailability(garment, colorObj.name);
        });
        garment.colors.forEach(function (colorObj, index) {
            if (!colorHasRenderableImages(garment, colorObj)) return;
            setTimeout(function () {
                if (state.garment !== garment) return;
                fetchInventoryForColor(garment, colorObj.name);
            }, index * 80);
        });
    }

    function fetchSanmarInventoryForSelection() {
        if (!state.garment || !state.colorName || !isSanmarBrand(state.garment.brand)) return;
        fetchInventoryForColor(state.garment, state.colorName);
    }

    // ── Live per-size pricing from S&S ─────────────────────────
    function fetchSSPricingForGarment(_retry) {
        if (!state.garment) return;
        if (!isSSBrand(state.garment.brand)) return;

        var apiBase = getBuilderApiBase();
        if (!apiBase) return;

        var uploader = window.__BUILDER_UPLOAD;
        var authToken = uploader && uploader.getAuthToken ? uploader.getAuthToken() : '';

        var styleID = String(state.garment.styleID || '');
        if (!styleID) return;

        var color = state.colorName || '';
        var apiColor = normalizeSupplierColor(color);
        var cacheKey = styleID + '|' + color;

        if (state.ssPriceCache[cacheKey]) {
            applySizePrices(state.ssPriceCache[cacheKey]);
            return;
        }

        if (state.ssPriceInFlight[cacheKey]) return;
        state.ssPriceInFlight[cacheKey] = true;
        state.garment._livePriceStatus = 'loading';

        var url = apiBase + '/api/ss-pricing?styleID=' + encodeURIComponent(styleID);
        if (apiColor) url += '&color=' + encodeURIComponent(apiColor);

        var xhr = new XMLHttpRequest();
        xhr.open('GET', url, true);
        if (authToken) {
            xhr.setRequestHeader('X-Builder-Token', authToken);
        }
        xhr.onreadystatechange = function () {
            if (xhr.readyState !== 4) return;
            delete state.ssPriceInFlight[cacheKey];

            var ok = xhr.status >= 200 && xhr.status < 300;
            var data = null;
            if (ok) {
                try { data = JSON.parse(xhr.responseText || '{}'); }
                catch (e) { data = null; }
            }

            if (!ok || !data || !data.success || !data.prices) {
                // Retry once on transient failure, then mark failed.
                if (!_retry) {
                    setTimeout(function () { fetchSSPricingForGarment(1); }, 1200);
                } else if (state.garment) {
                    state.garment._livePriceStatus = 'failed';
                    if (typeof updateStockBadges === 'function') updateStockBadges();
                }
                return;
            }

            state.ssPriceCache[cacheKey] = data;

            if (state.garment && String(state.garment.styleID) === styleID) {
                applySizePrices(data);
            }
        };
        xhr.send();
    }

    function applySizePrices(priceData) {
        if (!state.garment || !priceData || !priceData.prices) return;

        // Normalize keys to match the canonical sizes in state.garment.sizes.
        // Supplier APIs have returned 'XXL', '2Xl', ' 2XL', '2X' — all of which
        // would silently miss lookup and cause fallback to base price.
        var raw = priceData.prices || {};
        var normalized = {};
        Object.keys(raw).forEach(function (key) {
            var cost = parseFloat(raw[key]);
            if (!isFinite(cost) || cost <= 0) return;
            var canonical = canonSize(key);
            if (canonical) normalized[canonical] = cost;
        });

        state.garment._livePrices = normalized;
        state.garment._livePriceColor = state.colorName || '';
        state.garment._livePriceStatus = 'ok';
        updatePricing();
        populateReview();
        if (typeof updateStockBadges === 'function') updateStockBadges();
    }

    // True when live per-size pricing is loaded AND covers this size for the
    // currently selected color. Used by the cart-submit guard and the size
    // matrix to prevent silent fallback to base price on 2XL/3XL/etc.
    function hasLivePriceForSize(size, g) {
        var garment = g || state.garment;
        if (!garment) return false;
        var live = garment._livePrices;
        if (!live) return false;
        // If prices were fetched for a different color, treat as unavailable
        if (garment._livePriceColor && state.colorName && garment._livePriceColor !== state.colorName) return false;
        var canonical = canonSize(size);
        var cost = live[canonical];
        return isFinite(cost) && cost > 0;
    }

    // ── Live per-size pricing from SanMar ──────────────────────
    function fetchSanmarPricingForGarment(_retry) {
        if (!state.garment) return;
        if (!isSanmarBrand(state.garment.brand)) return;

        var apiBase = getBuilderApiBase();
        if (!apiBase) return;

        var uploader = window.__BUILDER_UPLOAD;
        var authToken = uploader && uploader.getAuthToken ? uploader.getAuthToken() : '';

        var style = String(state.garment.styleName || state.garment.styleID || '');
        if (!style) return;

        var color = state.colorName || '';
        var apiColor = normalizeSupplierColor(color);
        var cacheKey = 'sm|' + style + '|' + color;

        if (state.ssPriceCache[cacheKey]) {
            applySizePrices(state.ssPriceCache[cacheKey]);
            return;
        }

        if (state.ssPriceInFlight[cacheKey]) return;
        state.ssPriceInFlight[cacheKey] = true;
        state.garment._livePriceStatus = 'loading';

        var url = apiBase + '/api/sanmar-pricing?style=' + encodeURIComponent(style);
        if (apiColor) url += '&color=' + encodeURIComponent(apiColor);

        var xhr = new XMLHttpRequest();
        xhr.open('GET', url, true);
        if (authToken) {
            xhr.setRequestHeader('X-Builder-Token', authToken);
        }
        xhr.onreadystatechange = function () {
            if (xhr.readyState !== 4) return;
            delete state.ssPriceInFlight[cacheKey];

            var ok = xhr.status >= 200 && xhr.status < 300;
            var data = null;
            if (ok) {
                try { data = JSON.parse(xhr.responseText || '{}'); }
                catch (e) { data = null; }
            }

            if (!ok || !data || !data.success || !data.prices) {
                if (!_retry) {
                    setTimeout(function () { fetchSanmarPricingForGarment(1); }, 1200);
                } else if (state.garment) {
                    state.garment._livePriceStatus = 'failed';
                    if (typeof updateStockBadges === 'function') updateStockBadges();
                }
                return;
            }

            state.ssPriceCache[cacheKey] = data;

            var currentStyle = String(state.garment.styleName || state.garment.styleID || '');
            if (state.garment && currentStyle === style) {
                applySizePrices(data);
            }
        };
        xhr.send();
    }

    // ── Fetch live pricing for any garment (dispatches to S&S or SanMar) ──
    function fetchLivePricingForGarment() {
        if (!state.garment) return;
        if (isSanmarBrand(state.garment.brand)) {
            fetchSanmarPricingForGarment();
        } else {
            fetchSSPricingForGarment();
        }
    }

    function fetchSSInventoryForSelection() {
        if (!state.garment || !state.colorName || !isSSBrand(state.garment.brand)) return;
        fetchInventoryForColor(state.garment, state.colorName);
    }

    function getGarmentFallbackUrl(garment) {
        if (!garment) return '';
        var colorObj = (garment.colors || []).find(function (color) {
            return !!(color && (color.front || color.back || color.side));
        });
        if (!colorObj) return '';
        return colorObj.front || colorObj.back || colorObj.side || '';
    }

    function getGarmentThumbUrl(garment) {
        if (!garment) return '';
        return garment.styleImage || getGarmentFallbackUrl(garment);
    }

    function colorHasRenderableImages(garment, colorObj) {
        if (!colorObj) return false;
        return !!(garment && garment.styleImage) || !!(colorObj.front || colorObj.back || colorObj.side);
    }

    function getDefaultColorName(garment) {
        if (!garment || !(garment.colors || []).length) return null;
        var previewableColor = garment.colors.find(function (colorObj) {
            return colorHasRenderableImages(garment, colorObj) && !isColorKnownOutOfStock(colorObj);
        });
        var fallbackColor = garment.colors.find(function (colorObj) {
            return colorHasRenderableImages(garment, colorObj);
        });
        return (previewableColor || fallbackColor || garment.colors[0]).name;
    }

    // How many locations have artwork
    function artworkCount() {
        return getActiveLocationEntries().length;
    }

    // Map location → view side
    const locationViewSide = {
        'front': 'front',
        'back': 'back',
        'left-sleeve': 'left-sleeve',
        'right-sleeve': 'right-sleeve',
    };

    // Map location → print zone key
    const locationZoneKey = {
        'front': 'front-center',
        'back': 'back',
        'left-sleeve': 'sleeve',
        'right-sleeve': 'right-sleeve',
    };

    // Sync decoration pill highlight to a given method
    function syncDecorationCards(method) {
        document.querySelectorAll('.deco-pill').forEach(p => {
            p.classList.toggle('active', p.dataset.method === method);
        });
    }

    // Short decoration labels for badges
    const decoShortLabels = { 'dtf': 'FCP', 'embroidery': 'EMB', 'leather-patch': 'LTH' };

    // Map location → display label (default; overridden per category)
    const locationDisplayLabels = {
        'front': 'Front',
        'back': 'Back',
        'left-sleeve': 'Left Sleeve',
        'right-sleeve': 'Right Sleeve',
    };

    // Category-specific location labels
    const categoryLocationLabels = {
        hats: { 'front': 'Front Panel', 'back': 'Back Panel', 'left-sleeve': 'Left Side', 'right-sleeve': 'Right Side' },
        beanies: { 'front': 'Front Cuff', 'back': 'Back', 'left-sleeve': 'Left Side', 'right-sleeve': 'Right Side' },
        bags: { 'front': 'Front', 'back': 'Back', 'left-sleeve': 'Left Side', 'right-sleeve': 'Right Side' },
        vests: { 'front': 'Front', 'back': 'Back', 'left-sleeve': 'Left Chest', 'right-sleeve': 'Right Chest' },
        tanks: { 'front': 'Front', 'back': 'Back', 'left-sleeve': 'Left Strap', 'right-sleeve': 'Right Strap' },
        shorts: { 'front': 'Front', 'back': 'Back', 'left-sleeve': 'Left Leg', 'right-sleeve': 'Right Leg' },
        workpants: { 'front': 'Front', 'back': 'Back', 'left-sleeve': 'Left Leg', 'right-sleeve': 'Right Leg' },
        scrubs: { 'front': 'Front', 'back': 'Back', 'left-sleeve': 'Left Chest', 'right-sleeve': 'Right Chest' },
    };

    // Category-specific allowed decoration methods
    // Hats & beanies: embroidery + leather patch
    // Everything else (apparel & bags): full color print + embroidery
    const categoryDecoMethods = {
        hats:    ['embroidery', 'leather-patch'],
        beanies: ['embroidery', 'leather-patch'],
    };
    const defaultDecoMethods = ['dtf', 'embroidery'];  // apparel & bags

    // Get allowed methods for current category
    function getAllowedMethods(category) {
        return categoryDecoMethods[category] || defaultDecoMethods;
    }

    // Update decoration pill visibility + auto-select valid method
    function updateDecoMethodsForCategory() {
        var allowed = getAllowedMethods(state.category);
        document.querySelectorAll('.deco-pill').forEach(function (pill) {
            var method = pill.dataset.method;
            pill.style.display = allowed.includes(method) ? '' : 'none';
        });
        // Also update quick-select dropdown
        var qsDeco = document.getElementById('qsDecoration');
        if (qsDeco) {
            Array.from(qsDeco.options).forEach(function (opt) {
                if (opt.value) {
                    opt.style.display = allowed.includes(opt.value) ? '' : 'none';
                    opt.disabled = !allowed.includes(opt.value);
                }
            });
        }
        // If current decoration isn't allowed in this category, switch to first allowed
        if (!allowed.includes(state.decoration)) {
            state.decoration = allowed[0];
            syncDecorationCards(state.decoration);
            if (qsDeco) qsDeco.value = state.decoration;
        }
        // Also fix any per-location decorations that are no longer valid
        Object.keys(state.locations).forEach(function (k) {
            var loc = state.locations[k];
            if (loc.decoration && !allowed.includes(loc.decoration)) {
                loc.decoration = allowed[0];
                // Re-render if this location has a logo
                if (loc.logoDataUrl) {
                    renderDecorationEffect(loc.logoDataUrl, loc.decoration, function (url, maskUrl) {
                        loc.renderedLogos[loc.decoration] = url;
                        loc.logoMaskUrl = maskUrl;
                        if (k === state.activeLocation) loadLocationOntoPreview();
                    });
                }
            }
        });
    }

    // Update all location label text in tabs, summary, and status bar
    function updateLocationLabels() {
        const labels = categoryLocationLabels[state.category] || locationDisplayLabels;
        // Merge any category labels into the display labels so other code picks them up
        Object.assign(locationDisplayLabels, {
            'front': labels['front'] || 'Front',
            'back': labels['back'] || 'Back',
            'left-sleeve': labels['left-sleeve'] || 'Left Sleeve',
            'right-sleeve': labels['right-sleeve'] || 'Right Sleeve',
        });
        // Update loc-tab span text
        document.querySelectorAll('.loc-tab').forEach(tab => {
            const loc = tab.dataset.location;
            const span = tab.querySelector('span');
            if (span && locationDisplayLabels[loc]) span.textContent = locationDisplayLabels[loc];
        });
        // Update loc-summary-item name text
        document.querySelectorAll('.loc-summary-item').forEach(item => {
            const loc = item.dataset.loc;
            const nameEl = item.querySelector('.loc-summary-name');
            if (nameEl && locationDisplayLabels[loc]) nameEl.textContent = locationDisplayLabels[loc];
        });
        // Update loc-status-item text
        const statusLabels = { 'front': locationDisplayLabels['front'].toLowerCase(), 'back': locationDisplayLabels['back'].toLowerCase(), 'left-sleeve': locationDisplayLabels['left-sleeve'].replace(/ /g, '\u00a0'), 'right-sleeve': locationDisplayLabels['right-sleeve'].replace(/ /g, '\u00a0') };
        document.querySelectorAll('.loc-status-item').forEach(item => {
            const loc = item.dataset.loc;
            if (statusLabels[loc]) item.textContent = statusLabels[loc];
        });
    }

    // Check which view sides have images and disable tabs that don't
    function updateViewAvailability() {
        if (!state.garment || !state.color) return;
        var availability = getLocationAvailability();
        // Disable loc-tabs
        document.querySelectorAll('.loc-tab').forEach(tab => {
            const loc = tab.dataset.location;
            const avail = availability[loc] !== false;
            tab.classList.toggle('disabled', !avail);
            tab.style.opacity = avail ? '1' : '0.35';
            tab.style.pointerEvents = avail ? '' : 'none';
        });
        // Disable loc-summary-items
        document.querySelectorAll('.loc-summary-item').forEach(item => {
            const loc = item.dataset.loc;
            const avail = availability[loc] !== false;
            item.classList.toggle('disabled', !avail);
            item.style.opacity = avail ? '1' : '0.35';
            item.style.pointerEvents = avail ? '' : 'none';
        });
        // Disable loc-status-items
        document.querySelectorAll('.loc-status-item').forEach(item => {
            const loc = item.dataset.loc;
            const avail = availability[loc] !== false;
            item.style.opacity = avail ? '1' : '0.35';
            item.style.pointerEvents = avail ? '' : 'none';
        });
        // If current view is unavailable, switch to front
        if (!availability[state.activeLocation]) {
            switchToLocation('front');
        }
    }

    // ==========================================
    // Save / Restore State (localStorage + import/export)
    // ==========================================
    const SAVE_KEY = 'tdls_builder_state';

    function serializeState() {
        const locs = {};
        Object.keys(state.locations).forEach(k => {
            const l = state.locations[k];
            locs[k] = {
                logoDataUrl: l.logoDataUrl,
                scale: l.scale,
                rotation: l.rotation,
                decoration: l.decoration,
                posLeft: l.posLeft,
                posTop: l.posTop,
                printSize: l.printSize,
                fileName: l.logoFile ? l.logoFile.name : null,
                fileSize: l.logoFile ? l.logoFile.size : null,
                fileType: l.logoFile ? l.logoFile.type : null,
                aiMeta: l.aiMeta || null,
            };
        });
        return {
            v: 1,
            currentStep: state.currentStep,
            category: state.category,
            garmentId: state.garment ? state.garment.id : null,
            color: state.color,
            colorName: state.colorName || null,
            decoration: state.decoration,
            activeLocation: state.activeLocation,
            notes: state.notes,
            sizes: Object.assign({}, state.sizes),
            locations: locs,
            waiveTapeFee: state.waiveTapeFee || false,
            savedAt: new Date().toISOString(),
        };
    }

    function autoSave() {
        try {
            localStorage.setItem(SAVE_KEY, JSON.stringify(serializeState()));
        } catch (e) { /* quota exceeded — silently ignore */ }
    }

    function clearSave() {
        localStorage.removeItem(SAVE_KEY);
    }

    function getSavedState() {
        try {
            const raw = localStorage.getItem(SAVE_KEY);
            return raw ? JSON.parse(raw) : null;
        } catch (e) { return null; }
    }

    function dataUrlToFile(dataUrl, name, type) {
        var arr = dataUrl.split(',');
        var mime = type || arr[0].match(/:(.*?);/)[1];
        var bstr = atob(arr[1]);
        var n = bstr.length;
        var u8arr = new Uint8Array(n);
        while (n--) u8arr[n] = bstr.charCodeAt(n);
        return new File([u8arr], name || 'logo.png', { type: mime });
    }

    function restoreFromSaved(saved) {
        _suppressSave = true;
        // 1. Category
        if (saved.category) {
            state.category = saved.category;
            document.querySelectorAll('.category-card').forEach(c =>
                c.classList.toggle('selected', c.dataset.category === saved.category));
            populateGarments(saved.category);
        }

        // 2. Garment
        if (saved.garmentId && saved.category) {
            var items = garments[saved.category] || [];
            var garment = items.find(g => g.id === saved.garmentId);
            if (garment) {
                state.garment = garment;
                document.querySelectorAll('.garment-card').forEach(c =>
                    c.classList.toggle('selected', c.dataset.garmentId === garment.id));
                // Build color swatches
                var section = document.getElementById('colorPickerSection');
                var container = document.getElementById('colorSwatches');
                section.style.display = 'block';
                container.innerHTML = '';
                markVisibleColorsChecking(garment);
                garment.colors.forEach(function (colorObj) {
                    var swatch = document.createElement('button');
                    swatch.className = 'color-swatch';
                    swatch.style.backgroundColor = colorObj.hex;
                    swatch.dataset.color = colorObj.hex;
                    swatch.dataset.name = colorObj.name;
                    swatch.title = colorObj.name;
                    var swUrl = colorObj.swatch && colorObj.swatch.indexOf('sw.jpg') === -1 ? colorObj.swatch : colorObj.front;
                    if (swUrl) {
                        swatch.style.backgroundImage = 'url(' + swUrl + ')';
                        swatch.style.backgroundSize = 'cover';
                        swatch.style.backgroundPosition = 'center';
                    }
                    applyAvailabilityToColorControl(swatch, colorObj);
                    swatch.addEventListener('click', function () {
                        if (swatch.disabled || isColorKnownOutOfStock(colorObj)) return;
                        selectColor(colorObj.name);
                    });
                    container.appendChild(swatch);
                });
                // Build dynamic size matrix for this garment
                rebuildSizeMatrix(garment.sizes);

                // Fetch live per-size pricing from supplier API
                fetchLivePricingForGarment();
                fetchInventoryForVisibleColors(garment);
            }
        }

        // 3. Color
        if (saved.colorName || saved.color) {
            state.colorName = saved.colorName || null;
            state.color = saved.color;
            var matchKey = saved.colorName || saved.color;
            var matchAttr = saved.colorName ? 'name' : 'color';
            document.querySelectorAll('.color-swatch').forEach(s =>
                s.classList.toggle('selected', s.dataset[matchAttr] === matchKey));
            // Trigger supplier fetches for the restored color. restoreFromSaved
            // sets state directly (bypasses selectColor), so without this the
            // edit-from-cart flow loads without inventory data (no OOS clamp
            // on S&S garments) and the live-price fetch at step 2 above may
            // have hit the API with a stale/empty colorName.
            if (state.garment && state.colorName) {
                // Invalidate any stale live prices from the step-2 fetch
                if (state.garment._livePriceColor && state.garment._livePriceColor !== state.colorName) {
                    state.garment._livePrices = null;
                    state.garment._livePriceColor = '';
                    state.garment._livePriceStatus = 'loading';
                }
                fetchSanmarInventoryForSelection();
                fetchSSInventoryForSelection();
                fetchLivePricingForGarment();
            }
        }

        // 4. Decoration
        if (saved.decoration) {
            state.decoration = saved.decoration;
            syncDecorationCards(saved.decoration);
        }

        // 5. Notes
        if (saved.notes) {
            state.notes = saved.notes;
            var notesEl = document.getElementById('orderNotes');
            if (notesEl) notesEl.value = saved.notes;
        }

        // 5b. Waive digitizing fee
        if (saved.waiveTapeFee) {
            state.waiveTapeFee = true;
        }

        // 6. Sizes — restore quantities into dynamic rows
        if (saved.sizes) {
            Object.keys(saved.sizes).forEach(function (size) {
                if (state.sizes.hasOwnProperty(size)) {
                    state.sizes[size] = saved.sizes[size];
                }
            });
            document.querySelectorAll('.qty-input').forEach(function (input) {
                var sz = input.dataset.size;
                if (saved.sizes[sz] !== undefined) input.value = saved.sizes[sz];
            });
            updateTotals();
        }

        // 7. Locations — restore logos from dataUrls
        if (saved.locations) {
            Object.keys(saved.locations).forEach(function (k) {
                var sl = saved.locations[k];
                var loc = state.locations[k];
                if (!loc) return;
                loc.scale = sl.scale || 1;
                loc.rotation = sl.rotation || 0;
                loc.decoration = sl.decoration || null;
                loc.posLeft = sl.posLeft != null ? sl.posLeft : null;
                loc.posTop = sl.posTop != null ? sl.posTop : null;
                loc.printSize = sl.printSize || null;
                loc.aiMeta = sl.aiMeta || null;
                if (sl.logoDataUrl) {
                    loc.logoDataUrl = sl.logoDataUrl;
                    if (!sl.aiMeta) {
                        loc.logoFile = dataUrlToFile(sl.logoDataUrl, sl.fileName || 'restored.png', sl.fileType || 'image/png');
                    }
                }
            });
        }

        // 8. Active location
        if (saved.activeLocation) {
            state.activeLocation = saved.activeLocation;
            state.viewSide = locationViewSide[saved.activeLocation];
        }

        // 8b. Restore category labels + view availability + allowed deco methods
        updateLocationLabels();
        updateDecoMethodsForCategory();
        updateViewAvailability();

        // 9. Navigate to saved step + refresh all UI
        updatePreview();
        loadLocationOntoPreview();
        updateLocFileBar();
        updateLocationStatus();
        updateLocationSummary();
        updateSelectionsBar();
        syncQuickSelectFromState();
        goToStep(saved.currentStep || 1);

        // 10. Kick off decoration renders for all locations with logos
        Object.keys(state.locations).forEach(function (k) {
            var loc = state.locations[k];
            if (loc.logoDataUrl) {
                var methods = ['embroidery', 'leather-patch', 'dtf'];
                methods.forEach(function (m) {
                    renderDecorationEffect(loc.logoDataUrl, m, function (rendUrl, maskUrl) {
                        loc.renderedLogos[m] = rendUrl;
                        if (m === 'embroidery') loc.logoMaskUrl = maskUrl;
                        // Refresh if this is the active location
                        if (k === state.activeLocation) {
                            var activeMethod = loc.decoration || state.decoration;
                            if (activeMethod === m) {
                                logoImg.src = rendUrl;
                            }
                            if (m === 'embroidery') {
                                logoOverlay.style.setProperty('--logo-mask-url', 'url(' + maskUrl + ')');
                            }
                        }
                    });
                });
            }
        });

        _suppressSave = false;

        // Dismiss the loading overlay once the garment image has loaded
        var overlay = document.getElementById('builderLoadingOverlay');
        if (overlay) {
            var waitForImage = function () {
                if (garmentImg.complete && garmentImg.naturalWidth > 0) {
                    overlay.classList.add('fade-out');
                    setTimeout(function () { overlay.remove(); }, 400);
                } else {
                    garmentImg.addEventListener('load', function () {
                        overlay.classList.add('fade-out');
                        setTimeout(function () { overlay.remove(); }, 400);
                    }, { once: true });
                    // Safety fallback — remove after 4s max
                    setTimeout(function () {
                        if (overlay.parentNode) {
                            overlay.classList.add('fade-out');
                            setTimeout(function () { overlay.remove(); }, 400);
                        }
                    }, 4000);
                }
            };
            // Let the DOM settle after goToStep + RAF reflows
            requestAnimationFrame(waitForImage);
        }
    }

    // Export mockup images — composite garment + logo per location onto canvas
    function exportMockupImages() {
        if (!state.garment || !state.color) {
            alert('Please select a garment and color first.');
            return;
        }
        var locsWithArt = getActiveLocationEntries().map(function (entry) { return entry[0]; });
        if (!locsWithArt.length) {
            alert('Upload artwork to at least one location first.');
            return;
        }

        var pending = locsWithArt.length;
        locsWithArt.forEach(function (locKey) {
            var loc = state.locations[locKey];
            var viewSide = locationViewSide[locKey];
            var zoneKey = locationZoneKey[locKey];
            var garmentSrc = getGarmentImageUrl(state.garment, state.color, viewSide);
            if (!garmentSrc) { pending--; return; }

            // Resolve logo positions
            var cat = state.category || 'tshirts';
            var catPositions = logoPositions[cat] || logoPositions['tshirts'];
            var sidePositions = catPositions[viewSide] || catPositions['front'];
            var pos = sidePositions[zoneKey];
            if (!pos) { pending--; return; }

            // Use saved drag position or defaults
            var posLeft = loc.posLeft != null ? loc.posLeft : pos.left;
            var posTop = loc.posTop != null ? loc.posTop : pos.top;

            // Get the decoration-rendered logo or original
            var decoMethod = loc.decoration || state.decoration;
            var logoSrc = loc.renderedLogos[decoMethod] || loc.logoDataUrl;

            // Load garment image
            // Load garment image as blob to avoid canvas taint on file://
            function loadAsBlob(src, cb) {
                var xhr = new XMLHttpRequest();
                xhr.open('GET', src, true);
                xhr.responseType = 'blob';
                xhr.onload = function () {
                    if (xhr.status === 200 || xhr.status === 0) {
                        cb(URL.createObjectURL(xhr.response));
                    }
                };
                xhr.send();
            }

            loadAsBlob(garmentSrc, function (garmentBlobUrl) {
            var gImg = new Image();
            gImg.onload = function () {
                URL.revokeObjectURL(garmentBlobUrl);
                // Output canvas = garment natural dimensions
                var cW = gImg.naturalWidth;
                var cH = gImg.naturalHeight;
                var canvas = document.createElement('canvas');
                canvas.width = cW;
                canvas.height = cH;
                var ctx = canvas.getContext('2d');

                // Draw garment
                ctx.drawImage(gImg, 0, 0, cW, cH);

                // Logo is already a data URL — won't taint
                var lImg = new Image();
                lImg.onload = function () {
                    // Convert percentage positions to px on canvas
                    var lLeft = parseFloat(posLeft) / 100 * cW;
                    var lTop = parseFloat(posTop) / 100 * cH;
                    var lWidth = parseFloat(pos.width) / 100 * cW;
                    var lHeight = parseFloat(pos.height) / 100 * cH;

                    // Preserve logo aspect ratio
                    var logoRatio = lImg.naturalWidth / lImg.naturalHeight;
                    var drawW, drawH;
                    if (logoRatio > lWidth / lHeight) {
                        drawW = lWidth;
                        drawH = lWidth / logoRatio;
                    } else {
                        drawH = lHeight;
                        drawW = lHeight * logoRatio;
                    }
                    // Center within the zone box
                    var dx = lLeft + (lWidth - drawW) / 2;
                    var dy = lTop + (lHeight - drawH) / 2;

                    // Apply scale and rotation
                    var scale = loc.scale || 1;
                    var rotation = loc.rotation || 0;
                    var cx = dx + drawW / 2;
                    var cy = dy + drawH / 2;

                    ctx.save();
                    ctx.translate(cx, cy);
                    ctx.scale(scale, scale);
                    ctx.rotate(rotation * Math.PI / 180);
                    ctx.drawImage(lImg, -drawW / 2, -drawH / 2, drawW, drawH);
                    ctx.restore();

                    // Download
                    canvas.toBlob(function (blob) {
                        var url = URL.createObjectURL(blob);
                        var a = document.createElement('a');
                        a.href = url;
                        var label = locationDisplayLabels[locKey] || locKey;
                        a.download = 'mockup-' + label.toLowerCase().replace(/\s+/g, '-') + '.png';
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(url);
                    }, 'image/png');
                };
                lImg.src = logoSrc;
            };
            gImg.src = garmentBlobUrl;
            });
        });
    }

    // Generate a plain garment image (no logo) for a given view side.
    // Returns a Promise<Blob|null>.
    function generateGarmentOnlyBlob(viewSide) {
        return new Promise(function (resolve) {
            if (!state.garment || !state.color) return resolve(null);
            var garmentSrc = getGarmentImageUrl(state.garment, state.color, viewSide);
            if (!garmentSrc) return resolve(null);
            var flip = shouldFlipSleeve(state.garment, viewSide) || (viewSide === 'back' && isBackMirrored(state.garment, state.color));

            fetchAsBlob(garmentSrc, function (blobUrl) {
                if (!blobUrl) return resolve(null);
                var gImg = new Image();
                gImg.onload = function () {
                    URL.revokeObjectURL(blobUrl);
                    var MAX = 600;
                    var cW = gImg.naturalWidth, cH = gImg.naturalHeight;
                    var scale = Math.min(1, MAX / Math.max(cW, cH));
                    var outW = Math.round(cW * scale), outH = Math.round(cH * scale);
                    var canvas = document.createElement('canvas');
                    canvas.width = outW; canvas.height = outH;
                    var ctx = canvas.getContext('2d');
                    if (flip) { ctx.translate(outW, 0); ctx.scale(-1, 1); }
                    ctx.drawImage(gImg, 0, 0, outW, outH);
                    canvas.toBlob(function (blob) { resolve(blob); }, 'image/jpeg', 0.85);
                };
                gImg.onerror = function () { URL.revokeObjectURL(blobUrl); resolve(null); };
                gImg.src = blobUrl;
            });
        });
    }

    // Generate a garment+logo preview as a Blob (for cart thumbnail).
    // Returns a Promise<Blob|null>.
    // @param {string} [overrideLocKey] — specific location key; defaults to first with artwork.
    function generatePreviewBlob(overrideLocKey) {
        return new Promise(function (resolve) {
            if (!state.garment || !state.color) return resolve(null);

            // Use the given location or fall back to first with artwork
            var activeLocationEntries = getActiveLocationEntries();
            var locKey = overrideLocKey || (activeLocationEntries[0] && activeLocationEntries[0][0]);
            if (!locKey || !state.locations[locKey] || !state.locations[locKey].logoDataUrl) return resolve(null);
            if (!isLocationAvailable(locKey)) return resolve(null);

            var loc = state.locations[locKey];
            var viewSide = locationViewSide[locKey];
            var zoneKey = locationZoneKey[locKey];
            var garmentSrc = getGarmentImageUrl(state.garment, state.color, viewSide);
            if (!garmentSrc) return resolve(null);

            var cat = state.category || 'tshirts';
            var catPositions = logoPositions[cat] || logoPositions['tshirts'];
            var sidePositions = catPositions[viewSide] || catPositions['front'];
            var pos = sidePositions[zoneKey];
            if (!pos) return resolve(null);

            var posLeft = loc.posLeft != null ? loc.posLeft : pos.left;
            var posTop = loc.posTop != null ? loc.posTop : pos.top;
            var decoMethod = loc.decoration || state.decoration;
            var logoSrc = loc.renderedLogos[decoMethod] || loc.logoDataUrl;

            // Try loading garment via XHR blob (avoids canvas taint).
            // If direct fetch fails (CORS-blocked CDN like SanMar),
            // retry through our Netlify image proxy.
            fetchAsBlob(garmentSrc, function (blobUrl) {
                if (!blobUrl) { renderLogoOnly(); return; }
                var gImg = new Image();
                gImg.onload = function () {
                    URL.revokeObjectURL(blobUrl);
                    drawComposite(gImg);
                };
                gImg.onerror = function () { URL.revokeObjectURL(blobUrl); renderLogoOnly(); };
                gImg.src = blobUrl;
            });

            function drawComposite(gImg) {
                var MAX = 600;
                var cW = gImg.naturalWidth, cH = gImg.naturalHeight;
                var scale = Math.min(1, MAX / Math.max(cW, cH));
                var outW = Math.round(cW * scale), outH = Math.round(cH * scale);
                var canvas = document.createElement('canvas');
                canvas.width = outW; canvas.height = outH;
                var ctx = canvas.getContext('2d');
                if (shouldFlipSleeve(state.garment, viewSide) || (viewSide === 'back' && isBackMirrored(state.garment, state.color))) { ctx.translate(outW, 0); ctx.scale(-1, 1); }
                ctx.drawImage(gImg, 0, 0, outW, outH);
                if (shouldFlipSleeve(state.garment, viewSide) || (viewSide === 'back' && isBackMirrored(state.garment, state.color))) { ctx.setTransform(1, 0, 0, 1, 0, 0); }
                drawLogoOnCanvas(ctx, outW, outH, canvas);
            }

            function renderLogoOnly() {
                var lImg = new Image();
                lImg.onload = function () {
                    var outW = 600, outH = 600;
                    var canvas = document.createElement('canvas');
                    canvas.width = outW; canvas.height = outH;
                    var ctx = canvas.getContext('2d');
                    ctx.fillStyle = '#f0f0f0';
                    ctx.fillRect(0, 0, outW, outH);
                    var logoRatio = lImg.naturalWidth / lImg.naturalHeight;
                    var drawW, drawH;
                    if (logoRatio > 1) { drawW = outW * 0.6; drawH = drawW / logoRatio; }
                    else { drawH = outH * 0.6; drawW = drawH * logoRatio; }
                    ctx.drawImage(lImg, (outW - drawW) / 2, (outH - drawH) / 2, drawW, drawH);
                    canvas.toBlob(function (blob) { resolve(blob); }, 'image/jpeg', 0.85);
                };
                lImg.onerror = function () { resolve(null); };
                lImg.src = logoSrc;
            }

            function drawLogoOnCanvas(ctx, outW, outH, canvas) {
                var lImg = new Image();
                lImg.onload = function () {
                    var lLeft = parseFloat(posLeft) / 100 * outW;
                    var lTop = parseFloat(posTop) / 100 * outH;
                    var lWidth = parseFloat(pos.width) / 100 * outW;
                    var lHeight = parseFloat(pos.height) / 100 * outH;
                    var logoRatio = lImg.naturalWidth / lImg.naturalHeight;
                    var drawW, drawH;
                    if (logoRatio > lWidth / lHeight) {
                        drawW = lWidth; drawH = lWidth / logoRatio;
                    } else {
                        drawH = lHeight; drawW = lHeight * logoRatio;
                    }
                    var dx = lLeft + (lWidth - drawW) / 2;
                    var dy = lTop + (lHeight - drawH) / 2;
                    var s = loc.scale || 1;
                    var r = loc.rotation || 0;
                    var cx = dx + drawW / 2;
                    var cy = dy + drawH / 2;
                    ctx.save();
                    ctx.translate(cx, cy);
                    ctx.scale(s, s);
                    ctx.rotate(r * Math.PI / 180);
                    ctx.drawImage(lImg, -drawW / 2, -drawH / 2, drawW, drawH);
                    ctx.restore();
                    canvas.toBlob(function (blob) { resolve(blob); }, 'image/jpeg', 0.85);
                };
                lImg.onerror = function () { resolve(null); };
                lImg.src = logoSrc;
            }
        });
    }

    /**
     * Fetch an image as a blob URL for canvas drawing.
     * Tries direct XHR first; if CORS-blocked, retries via our Netlify proxy.
     * Calls callback(blobUrl) on success, callback(null) on failure.
     */
    function fetchAsBlob(url, callback) {
        var xhr = new XMLHttpRequest();
        xhr.open('GET', url, true);
        xhr.responseType = 'blob';
        xhr.onload = function () {
            if (xhr.status >= 200 && xhr.status < 300) {
                callback(URL.createObjectURL(xhr.response));
            } else {
                tryProxy(url, callback);
            }
        };
        xhr.onerror = function () { tryProxy(url, callback); };
        xhr.send();
    }

    function tryProxy(url, callback) {
        var uploader = window.__BUILDER_UPLOAD;
        var apiBase = uploader && uploader.getApiBase ? uploader.getApiBase() : '';
        if (!apiBase) { callback(null); return; }
        var proxyUrl = apiBase + '/api/image-proxy?url=' + encodeURIComponent(url);
        var xhr2 = new XMLHttpRequest();
        xhr2.open('GET', proxyUrl, true);
        xhr2.responseType = 'blob';
        xhr2.onload = function () {
            if (xhr2.status >= 200 && xhr2.status < 300) {
                callback(URL.createObjectURL(xhr2.response));
            } else {
                callback(null);
            }
        };
        xhr2.onerror = function () { callback(null); };
        xhr2.send();
    }

    // Export state as downloadable JSON file
    function exportState() {
        var data = serializeState();
        var blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        var url = URL.createObjectURL(blob);
        var a = document.createElement('a');
        a.href = url;
        a.download = 'tdls-order-' + new Date().toISOString().slice(0, 10) + '.json';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    // Import state from JSON file
    function importState() {
        var input = document.createElement('input');
        input.type = 'file';
        input.accept = '.json';
        input.addEventListener('change', function () {
            if (!input.files.length) return;
            var reader = new FileReader();
            reader.onload = function (e) {
                try {
                    var saved = JSON.parse(e.target.result);
                    if (!saved || !saved.v) {
                        alert('Invalid save file.');
                        return;
                    }
                    resetBuilderState();
                    restoreFromSaved(saved);
                    autoSave();
                } catch (err) {
                    alert('Could not read save file.');
                }
            };
            reader.readAsText(input.files[0]);
        });
        input.click();
    }

    // Reset builder to factory defaults
    function resetBuilderState() {
        state.currentStep = 1;
        state.category = null;
        state.garment = null;
        state.color = null;
        state.colorName = null;
        state.decoration = 'dtf';
        state.activeLocation = 'front';
        state.viewSide = 'front';
        state.notes = '';
        Object.keys(state.sizes).forEach(function (s) { state.sizes[s] = 0; });
        Object.keys(state.locations).forEach(function (k) {
            var loc = state.locations[k];
            loc.logoFile = null;
            loc.logoDataUrl = null;
            loc.logoMaskUrl = null;
            loc.renderedLogos = {};
            loc.scale = 1;
            loc.rotation = 0;
            loc.decoration = null;
            loc.posLeft = null;
            loc.posTop = null;
        });

        // Reset DOM
        document.querySelectorAll('.category-card').forEach(c => c.classList.remove('selected'));
        document.querySelectorAll('.garment-card').forEach(c => c.classList.remove('selected'));
        document.querySelectorAll('.color-swatch').forEach(s => s.classList.remove('selected'));
        var notesEl = document.getElementById('orderNotes');
        if (notesEl) notesEl.value = '';
        document.querySelectorAll('.qty-input').forEach(function (input) { input.value = 0; });
        removeLogoFromPreview();
        syncDecorationCards('dtf');
        clearSave();
        updateLocationLabels();
        updateViewAvailability();
        updatePreview();
        updateLocFileBar();
        updateLocationStatus();
        updateLocationSummary();
        updateSelectionsBar();
        syncQuickSelectFromState();
        goToStep(1);
    }

    // ==========================================
    // Canvas Decoration Renderer
    // ==========================================
    // Renders a decoration-method-specific version of the logo using
    // per-pixel Blinn-Phong lighting on a combined height map.
    // Each method gets its own texture pattern and lighting parameters.
    // Returns { rendered, mask } data URLs via callback.

    // Method-specific presets
    var DECO_PRESETS = {
        // ── Embroidery: cross-hatch stitch texture, strong 3D, muted color ──
        embroidery: {
            maxDim: 300,
            blurScale: 0.008,
            azimuth: 130, elevation: 45,
            bevelStr: 2.8, ambient: 0.85, diffStr: 0.22, specStr: 0.14,
            shininess: 20, satFactor: 0.90, lumAdaptive: true,
            edgeW: 0.3, texW: 0.7,
            fabricBlend: 0,  // sits on top of fabric — no weave bleed
            texture: function (x, y, W, H) {
                var TILE = Math.max(5, Math.min(14, Math.round(Math.max(W, H) / 80)));
                var tx = x % TILE, ty = y % TILE;
                var t1 = ((tx + ty) % TILE) / TILE;
                var t2 = ((tx - ty + TILE * 2) % TILE) / TILE;
                var h1 = (1 - Math.abs(2 * t1 - 1)) * 0.6 + Math.sin(t1 * Math.PI) * 0.4;
                var h2 = ((1 - Math.abs(2 * t2 - 1)) * 0.6 + Math.sin(t2 * Math.PI) * 0.4) * 0.75;
                return Math.max(h1, h2);
            }
        },
        // ── Leather Patch: laser-engraved on copper leather — monochrome burn effect ──
        'leather-patch': {
            maxDim: 300,
            blurScale: 0.003,          // tighter blur → crisper burn edges
            azimuth: 130, elevation: 55,
            bevelStr: 1.8, ambient: 0.90, diffStr: 0.14, specStr: 0.18,
            shininess: 32, satFactor: 0, lumAdaptive: false,
            edgeW: 0.40, texW: 0.60,
            fabricBlend: 0,
            // Warm copper/amber base → very dark burn (matched to real patches)
            leatherBase: [190, 120, 68],   // warm copper-amber leather
            leatherBurn: [22, 10, 5],      // near-black char burn
            burnContrast: 2.0,             // steep curve for hard crisp edges
            // Leather grain overlay strength (0–1), modulates final surface color
            leatherGrainStr: 0.18,
            texture: function (x, y, W, H) {
                // Multi-frequency leather grain for height map (bevel + lighting)
                var S = Math.max(W, H);
                var px = x / S, py = y / S;
                // Large pore pattern
                var pore = Math.sin(px * 97.3 + py * 43.7) * Math.sin(px * 31.1 - py * 67.9);
                // Fine crease lines
                var crease = Math.sin(px * 211.0 + py * 89.3) * 0.3 + Math.sin(py * 177.0 - px * 59.0) * 0.2;
                // Directional pull (horizontal leather stretch)
                var pull = Math.sin(py * 47.0) * 0.15;
                var h = 0.5 + pore * 0.35 + crease * 0.25 + pull;
                return Math.max(0, Math.min(1, h));
            }
        },
        // ── DTF / Full Color Print: fabric weave clearly visible through the transfer ──
        dtf: {
            maxDim: 300,
            blurScale: 0.004,
            azimuth: 110, elevation: 60,
            bevelStr: 1.2, ambient: 0.94, diffStr: 0.08, specStr: 0.02,
            shininess: 8, satFactor: 1.0, lumAdaptive: false,
            edgeW: 0.25, texW: 0.75,
            fabricBlend: 0.18,  // noticeable fabric texture bleeding through
            texture: function (x, y, W, H) {
                // Fabric weave — alternating warp/weft, bigger tiles for visibility
                var TILE = Math.max(3, Math.min(8, Math.round(Math.max(W, H) / 70)));
                var tx = x % TILE, ty = y % TILE;
                var warpUp = (Math.floor(x / TILE) + Math.floor(y / TILE)) % 2 === 0;
                if (warpUp) {
                    return Math.sin((ty / TILE) * Math.PI) * 0.5 + 0.5;
                } else {
                    return Math.sin((tx / TILE) * Math.PI) * 0.5 + 0.5;
                }
            }
        }
    };

    function renderDecorationEffect(logoDataUrl, method, callback) {
        var P = DECO_PRESETS[method];
        if (!P) { callback(null, null); return; }

        var img = new Image();
        img.onload = function () {
            var natW = img.naturalWidth;
            var natH = img.naturalHeight;

            var MAX_DIM = P.maxDim;
            var scale = 1;
            if (Math.max(natW, natH) > MAX_DIM) {
                scale = MAX_DIM / Math.max(natW, natH);
            }
            var W = Math.round(natW * scale);
            var H = Math.round(natH * scale);

            var canvas = document.createElement('canvas');
            canvas.width = W;
            canvas.height = H;
            var ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, W, H);
            var src = ctx.getImageData(0, 0, W, H).data;

            var cutoff = 30;
            var total = W * H;
            var PI = Math.PI;

            // ---- 1. Thresholded alpha ----
            var alpha = new Float32Array(total);
            for (var i = 0; i < total; i++) {
                var a = src[i * 4 + 3];
                alpha[i] = a < cutoff ? 0 : (a - cutoff) / (255 - cutoff);
            }

            // ---- 2. Edge bevel height map (blurred alpha) ----
            var alphaCanvas = document.createElement('canvas');
            alphaCanvas.width = W;
            alphaCanvas.height = H;
            var alphaCtx = alphaCanvas.getContext('2d');
            var alphaImg = alphaCtx.createImageData(W, H);
            for (var i = 0; i < total; i++) {
                var v = Math.round(alpha[i] * 255);
                alphaImg.data[i * 4] = v;
                alphaImg.data[i * 4 + 1] = v;
                alphaImg.data[i * 4 + 2] = v;
                alphaImg.data[i * 4 + 3] = 255;
            }
            alphaCtx.putImageData(alphaImg, 0, 0);

            var blurR = Math.max(2, Math.round(Math.max(W, H) * P.blurScale));
            var blurCanvas = document.createElement('canvas');
            blurCanvas.width = W;
            blurCanvas.height = H;
            var blurCtx = blurCanvas.getContext('2d');
            blurCtx.filter = 'blur(' + blurR + 'px)';
            blurCtx.drawImage(alphaCanvas, 0, 0);
            var blurData = blurCtx.getImageData(0, 0, W, H).data;

            var edgeHeight = new Float32Array(total);
            for (var i = 0; i < total; i++) {
                edgeHeight[i] = blurData[i * 4] / 255;
            }

            // ---- 3. Method-specific texture height map ----
            var texHeight = new Float32Array(total);
            for (var y = 0; y < H; y++) {
                for (var x = 0; x < W; x++) {
                    texHeight[y * W + x] = P.texture(x, y, W, H);
                }
            }

            // ---- 4. Combine height maps ----
            var combined = new Float32Array(total);
            for (var i = 0; i < total; i++) {
                combined[i] = edgeHeight[i] * P.edgeW + texHeight[i] * P.texW;
            }

            // ---- 5. Lighting (Blinn-Phong) ----
            var az = P.azimuth * PI / 180;
            var el = P.elevation * PI / 180;
            var lx = Math.cos(el) * Math.cos(az);
            var ly = -Math.cos(el) * Math.sin(az);
            var lz = Math.sin(el);
            var hx = lx, hy = ly, hz = lz + 1;
            var hlen = Math.sqrt(hx * hx + hy * hy + hz * hz);
            hx /= hlen; hy /= hlen; hz /= hlen;

            // ---- 6. Pixel render ----
            // Pre-compute fabric weave pattern for DTF/screen-print blending
            var fabricW = 0;
            var fabricTex = null;
            if (P.fabricBlend > 0) {
                fabricW = P.fabricBlend;
                fabricTex = new Float32Array(total);
                var fTILE = Math.max(2, Math.min(6, Math.round(Math.max(W, H) / 80)));
                for (var y = 0; y < H; y++) {
                    for (var x = 0; x < W; x++) {
                        // Plain weave: alternating warp/weft ridges
                        var ftx = x % fTILE, fty = y % fTILE;
                        var up = (Math.floor(x / fTILE) + Math.floor(y / fTILE)) % 2 === 0;
                        var fh = up
                            ? Math.sin((fty / fTILE) * PI)
                            : Math.sin((ftx / fTILE) * PI);
                        // 0.7–1.0 range: darkens in the valleys, neutral on peaks
                        fabricTex[y * W + x] = 0.7 + fh * 0.3;
                    }
                }
            }

            var output = ctx.createImageData(W, H);
            var dst = output.data;
            var maskCanvas2 = document.createElement('canvas');
            maskCanvas2.width = W;
            maskCanvas2.height = H;
            var maskCtx2 = maskCanvas2.getContext('2d');
            var maskImg = maskCtx2.createImageData(W, H);
            var msk = maskImg.data;

            for (var y = 0; y < H; y++) {
                for (var x = 0; x < W; x++) {
                    var idx = (y * W + x) * 4;
                    var hmI = y * W + x;
                    var a = alpha[hmI];

                    if (a <= 0) {
                        dst[idx] = dst[idx + 1] = dst[idx + 2] = dst[idx + 3] = 0;
                        msk[idx] = msk[idx + 1] = msk[idx + 2] = 255;
                        msk[idx + 3] = 0;
                        continue;
                    }

                    var newAlpha = Math.round(a * 255);

                    // Normal from height map
                    var hL = x > 0     ? combined[hmI - 1] : combined[hmI];
                    var hR = x < W - 1 ? combined[hmI + 1] : combined[hmI];
                    var hU = y > 0     ? combined[hmI - W] : combined[hmI];
                    var hD = y < H - 1 ? combined[hmI + W] : combined[hmI];

                    var nx = (hL - hR) * P.bevelStr;
                    var ny = (hU - hD) * P.bevelStr;
                    var nz = 1.0;
                    var nlen = Math.sqrt(nx * nx + ny * ny + nz * nz);
                    nx /= nlen; ny /= nlen; nz /= nlen;

                    var diff = Math.max(0, nx * lx + ny * ly + nz * lz);
                    var spec = Math.pow(Math.max(0, nx * hx + ny * hy + nz * hz), P.shininess);

                    var light = P.ambient + diff * P.diffStr;
                    var specAdd = spec * P.specStr * 255;

                    // Color adjustment
                    var r = src[idx], g = src[idx + 1], b = src[idx + 2];
                    if (P.satFactor !== 1.0) {
                        var gray = 0.299 * r + 0.587 * g + 0.114 * b;
                        r = r * P.satFactor + gray * (1 - P.satFactor);
                        g = g * P.satFactor + gray * (1 - P.satFactor);
                        b = b * P.satFactor + gray * (1 - P.satFactor);
                    }

                    // Leather patch: remap to copper↔dark-brown based on luminance
                    // Dark pixels in logo = deep laser burn (near black)
                    // Light pixels = warm copper leather surface
                    if (P.leatherBase) {
                        var lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
                        // Invert: dark logo areas → deep burn
                        var burnT = 1.0 - lum;
                        // Steepen curve with configurable contrast for crisp burn edges
                        var contrast = P.burnContrast || 1.0;
                        burnT = Math.min(1, Math.max(0, (burnT - 0.5) * contrast + 0.5));
                        burnT = burnT * burnT * (3 - 2 * burnT);  // smoothstep
                        var lb = P.leatherBase, lk = P.leatherBurn;
                        r = lb[0] + (lk[0] - lb[0]) * burnT;
                        g = lb[1] + (lk[1] - lb[1]) * burnT;
                        b = lb[2] + (lk[2] - lb[2]) * burnT;

                        // Leather grain overlay — modulates surface to show pores/creases
                        var grainStr = P.leatherGrainStr || 0;
                        if (grainStr > 0) {
                            // Re-use the texture height value as grain intensity
                            var grain = texHeight[hmI]; // 0–1 from texture function
                            // Grain shows more on leather base, less on deep burns
                            var grainAmount = grainStr * (1.0 - burnT * 0.6);
                            var grainMod = 1.0 - grainAmount + grainAmount * grain;
                            r *= grainMod;
                            g *= grainMod;
                            b *= grainMod;
                        }
                    }

                    // Luminance-adaptive contrast (embroidery only)
                    var adjLight = light;
                    if (P.lumAdaptive) {
                        var lum = (r * 0.299 + g * 0.587 + b * 0.114) / 255;
                        var lumBoost = 1.0 + lum * 1.0;
                        adjLight = 1.0 + (light - 1.0) * lumBoost;
                    }

                    dst[idx]     = Math.max(0, Math.min(255, Math.round(r * adjLight + specAdd)));
                    dst[idx + 1] = Math.max(0, Math.min(255, Math.round(g * adjLight + specAdd)));
                    dst[idx + 2] = Math.max(0, Math.min(255, Math.round(b * adjLight + specAdd)));

                    // Fabric weave blending — darken in weave valleys to show textile
                    if (fabricTex) {
                        var fw = fabricTex[hmI];
                        var blend = 1.0 - fabricW + fabricW * fw;
                        dst[idx]     = Math.round(dst[idx] * blend);
                        dst[idx + 1] = Math.round(dst[idx + 1] * blend);
                        dst[idx + 2] = Math.round(dst[idx + 2] * blend);
                    }

                    dst[idx + 3] = newAlpha;

                    msk[idx] = msk[idx + 1] = msk[idx + 2] = 255;
                    msk[idx + 3] = newAlpha;
                }
            }

            ctx.putImageData(output, 0, 0);
            maskCtx2.putImageData(maskImg, 0, 0);

            callback(
                canvas.toDataURL('image/png'),
                maskCanvas2.toDataURL('image/png')
            );
        };
        img.src = logoDataUrl;
    }

    // ==========================================
    // Garment Database (mock)
    // ==========================================
    // Garment catalog — merged from S&S Activewear + SanMar (Nike, Carhartt)
    // Each garment: { id, styleID, brand, styleName, name, title, icon, styleImage, price, sizes:[], colors:[{name, hex, hex2, swatch, front, back, side}] }
    const SANMAR_STYLE_SIZE_OVERRIDES = {
        // SanMar headwear styles where the generated catalog had apparel buckets.
        '333114': ['OSFA'],
        'NKFB6446': ['M/L'],
        'NKFB5675': ['M/L'],
        'NKFB6449': ['M/L'],
        'NKFB6444': ['M/L'],
        'NKFB6447': ['M/L'],
        'NKFB6450': ['M/L'],
        'NKFD9709': ['M/L'],
        'NKFD9718': ['S/M', 'M/L', 'L/XL'],
        'NKFB5677': ['M/L'],
        'NKFB6448': ['S/M', 'M/L', 'L/XL'],
        'NKFB6445': ['M/L'],
        'NKBFN6319': ['S', 'M', 'L'],
        'NKFB5666': ['M/L'],
        'NKFN9893': ['M/L'],
        // SanMar tall, women's XXL, workwear waist/inseam, and split-size vest styles.
        'CT102291': ['3030', '3032', '3230', '3232', '3234', '3330', '3332', '3430', '3432', '3434', '3630', '3632', '3634', '3830', '3832', '3834', '4030', '4032', '4034', '4230', '4232', '4430', '4432'],
        'CT102776': ['3030', '3032', '3230', '3232', '3234', '3430', '3432', '3434', '3436', '3630', '3632', '3634', '3636', '3830', '3832', '3834', '3836', '4030', '4032', '4034', '4036', '4230', '4232', '4234', '4430', '4432', '4434', '4630', '4632', '4830', '4832'],
        'CT102804': ['3030', '3032', '3130', '3132', '3230', '3232', '3234', '3330', '3332', '3334', '3430', '3432', '3434', '3436', '3530', '3532', '3534', '3628', '3630', '3632', '3634', '3636', '3830', '3832', '3834', '3836', '4030', '4032', '4034', '4230', '4232', '4234', '4430', '4432', '4630', '4632'],
        'CT102808': ['3030', '3032', '3130', '3132', '3230', '3232', '3234', '3330', '3332', '3334', '3430', '3432', '3434', '3436', '3530', '3532', '3534', '3630', '3632', '3634', '3636', '3830', '3832', '3834', '3836', '4030', '4032', '4034', '4230', '4232', '4234', '4430', '4432', '4630', '4632'],
        'CT103574': ['3030', '3032', '3130', '3132', '3230', '3232', '3234', '3330', '3332', '3334', '3430', '3432', '3434', '3436', '3630', '3632', '3634', '3636', '3830', '3832', '3834', '3836', '4030', '4032', '4034', '4230', '4232', '4234', '4430', '4432', '4630', '4632'],
        'CT106171': ['3/4X', 'M/L', 'X/2X', 'XS/S'],
        'CTB11': ['3030', '3032', '3130', '3132', '3230', '3232', '3234', '3330', '3332', '3334', '3430', '3432', '3434', '3436', '3530', '3532', '3534', '3630', '3632', '3634', '3636', '3830', '3832', '3834', '3836', '4030', '4032', '4034', '4036', '4230', '4232', '4234', '4430', '4432', '4434', '4630', '4632', '4830', '4832'],
        'CTS106672': ['2XLS', '3XLS', 'LS', 'MS', 'XLS'],
        'CTT103828': ['2XLT', '3XLT', 'LT', 'XLT'],
        'CTT104050': ['2XLT', '3XLT', '4XLT', 'LT', 'XLT'],
        'CTT104293': ['2XLT', '3XLT', 'LT', 'XLT'],
        'CTT106672': ['2XLT', '3XLT', 'LT', 'MT', 'XLT'],
        'CTT106674': ['2XLT', '3XLT', 'LT', 'XLT'],
        'CTT106677': ['2XLT', '3XLT', '4XLT', 'LT', 'MT', 'XLT'],
        'CTT106678': ['2XLT', '3XLT', '4XLT', 'LT', 'XLT'],
        'CTTK121': ['2XLT', '3XLT', 'LT', 'XLT'],
        'CTTK87': ['2XLT', '3XLT', 'LT', 'XLT'],
        'KP55T': ['2XLT', '3XLT', '4XLT', 'LT', 'XLT'],
        'LKP155': ['3XL', '4XL', 'L', 'M', 'S', 'XL', 'XS', 'XXL'],
        'LPC098V': ['3XL', '4XL', 'L', 'M', 'S', 'XL', 'XS', 'XXL'],
        'LPC099V': ['3XL', '4XL', 'L', 'M', 'S', 'XL', 'XS', 'XXL'],
        'LPC140P': ['3XL', '4XL', 'L', 'M', 'S', 'XL', 'XS', 'XXL'],
        'LPC140V': ['3XL', '4XL', 'L', 'M', 'S', 'XL', 'XS', 'XXL'],
        'LPC147V': ['3XL', '4XL', 'L', 'M', 'S', 'XL', 'XS', 'XXL'],
        'LPC330V': ['3XL', '4XL', 'L', 'M', 'S', 'XL', 'XS', 'XXL'],
        'LPC380': ['3XL', '4XL', 'L', 'M', 'S', 'XL', 'XS', 'XXL'],
        'LPC381V': ['3XL', '4XL', 'L', 'M', 'S', 'XL', 'XS', 'XXL'],
        'LPC450': ['3XL', '4XL', 'L', 'M', 'S', 'XL', 'XS', 'XXL'],
        'LPC450V': ['3XL', '4XL', 'L', 'M', 'S', 'XL', 'XS', 'XXL'],
        'LPC450VLS': ['3XL', '4XL', 'L', 'M', 'S', 'XL', 'XS', 'XXL'],
        'LPC455V': ['3XL', '4XL', 'L', 'M', 'S', 'XL', 'XS', 'XXL'],
        'LPC54': ['3XL', '4XL', 'L', 'M', 'S', 'XL', 'XS', 'XXL'],
        'LPC54LS': ['3XL', '4XL', 'L', 'M', 'S', 'XL', 'XS', 'XXL'],
        'LPC54V': ['3XL', '4XL', 'L', 'M', 'S', 'XL', 'XS', 'XXL'],
        'LPC55': ['3XL', '4XL', 'L', 'M', 'S', 'XL', 'XS', 'XXL'],
        'LPC61': ['3XL', '4XL', 'L', 'M', 'S', 'XL', 'XS', 'XXL'],
        'LPC78H': ['3XL', '4XL', 'L', 'M', 'S', 'XL', 'XS', 'XXL'],
        'LPC78ZH': ['3XL', '4XL', 'L', 'M', 'S', 'XL', 'XS', 'XXL'],
        'PC145': ['2XL', '3XL', '4XL', 'L', 'M', 'S', 'XL'],
        'PC54T': ['2XLT', '3XLT', '4XLT', 'LT', 'XLT'],
        'PC55LST': ['2XLT', '3XLT', '4XLT', 'LT', 'XLT'],
        'PC55PT': ['2XLT', '3XLT', '4XLT', 'LT', 'XLT'],
        'PC55T': ['2XLT', '3XLT', '4XLT', 'LT', 'XLT'],
        'PC61LSPT': ['2XLT', '3XLT', '4XLT', 'LT', 'XLT'],
        'PC61LST': ['2XLT', '3XLT', '4XLT', 'LT', 'XLT'],
        'PC61PT': ['2XLT', '3XLT', '4XLT', 'LT', 'XLT'],
        'PC61T': ['2XLT', '3XLT', '4XLT', 'LT', 'XLT'],
        'PC78HT': ['2XLT', '3XLT', '4XLT', 'LT', 'XLT'],
        'PC78Q': ['2XL', '3XL', '4XL', 'L', 'M', 'S', 'XL'],
        'PC90HT': ['2XLT', '3XLT', '4XLT', 'LT', 'XLT'],
        'PC90T': ['2XLT', '3XLT', '4XLT', 'LT', 'XLT'],
        'PC90ZHT': ['2XLT', '3XLT', '4XLT', 'LT', 'XLT']
    };

    function normalizeCatalogGarment(category, garment) {
        if (!garment) return garment;
        var styleID = String(garment.styleID || garment.styleName || '');

        // SanMar exposes non-apparel inventory under its true one-size or
        // fitted-cap size keys. Some generated records have apparel buckets
        // (S/M/L/XL/2XL), which makes live inventory look like zero.
        if (SANMAR_STYLE_SIZE_OVERRIDES[styleID]) {
            garment.sizes = SANMAR_STYLE_SIZE_OVERRIDES[styleID].slice();
        } else if (category === 'bags' || category === 'beanies') {
            garment.sizes = ['OSFA'];
        } else if (category === 'hats' && garment.brand === 'Carhartt') {
            garment.sizes = ['OSFA'];
        }

        return garment;
    }

    const garments = (function mergeCatalogs() {
        var ss = window.__SS_CATALOG || {};
        var sm = window.__SANMAR_CATALOG || {};
        var merged = {};
        // Copy all S&S categories
        for (var cat in ss) {
            merged[cat] = (merged[cat] || []).concat(ss[cat]);
        }
        // Append SanMar categories
        for (var cat in sm) {
            merged[cat] = (merged[cat] || []).concat((sm[cat] || []).map(function (g) {
                return normalizeCatalogGarment(cat, g);
            }));
        }
        return merged;
    })();

    // Look up color name from the current garment's color objects
    function getColorName(hex) {
        // Prefer stored colorName (unique); fall back to hex lookup
        if (state.colorName) return state.colorName;
        if (!state.garment) return hex;
        var c = state.garment.colors.find(function (co) { return co.hex === hex; });
        return c ? c.name : hex;
    }

    // Perceived brightness of a hex color (0 = black, 255 = white)
    function hexBrightness(hex) {
        hex = (hex || '').replace('#', '');
        if (hex.length === 3) hex = hex[0]+hex[0]+hex[1]+hex[1]+hex[2]+hex[2];
        var r = parseInt(hex.substring(0, 2), 16) || 0;
        var g = parseInt(hex.substring(2, 4), 16) || 0;
        var b = parseInt(hex.substring(4, 6), 16) || 0;
        return 0.299 * r + 0.587 * g + 0.114 * b;
    }

    // Set logo blend mode based on garment color brightness
    function updateLogoBlendMode() {
        var hex = state.color;
        if (!hex) { logoImg.style.mixBlendMode = ''; return; }
        var bright = hexBrightness(hex);
        // Dark fabrics (< 90): screen lets light colors show through
        // Medium fabrics (90-170): multiply is subtle and shows texture
        // Light fabrics (> 170): multiply is most effective
        if (bright < 90) {
            logoImg.style.mixBlendMode = 'screen';
            logoImg.style.opacity = '0.88';
        } else {
            logoImg.style.mixBlendMode = 'multiply';
            logoImg.style.opacity = bright > 170 ? '0.92' : '0.90';
        }
    }

    // Resolve garment image URL from catalog color data (replaces garmentManifest)
    function getGarmentImageUrl(garment, colorHex, viewSide) {
        if (!garment) return '';
        var colorObj = getSelectedColorObject(garment, colorHex, state.colorName);
        if (!colorObj) return garment.styleImage || '';
        if (viewSide === 'back') {
            if (colorObj.back) return colorObj.back;
            // Beanies: mirror front when no back image exists
            var cat = state.category || garment.icon || '';
            if (cat === 'beanies' && colorObj.front) return colorObj.front;
            return '';
        }
        if (viewSide === 'left-sleeve' || viewSide === 'right-sleeve') return colorObj.side || '';
        return colorObj.front || garment.styleImage || '';
    }

    // Check if garment image should be flipped for the opposite sleeve view.
    // We flip for right-sleeve (the API only provides one side photo).
    function shouldFlipSleeve(garment, viewSide) {
        if (viewSide !== 'right-sleeve') return false;
        return true;
    }

    // Check if back view is using mirrored front (beanies with no back image)
    function isBackMirrored(garment, colorHex) {
        if (!garment) return false;
        var cat = state.category || garment.icon || '';
        if (cat !== 'beanies') return false;
        var colorObj = getSelectedColorObject(garment, colorHex, state.colorName);
        return !!(colorObj && !colorObj.back && colorObj.front);
    }

    const methodLabels = {
        'dtf': 'Full Color Print', 'embroidery': 'Embroidery',
        'leather-patch': 'Leather Patch'
    };

    // ── Garment base-price markup ──────────────────────────────
    // Catalog prices are wholesale cost; apply retail markup (20% over supplier cost)
    const GARMENT_MARKUP = 1.20;
    function markedUpGarmentPrice(g) {
        return Math.round((g || state.garment).price * GARMENT_MARKUP * 100) / 100;
    }

    // Per-size garment price.
    // Live supplier prices take priority (keyed by canonical size); catalog
    // base price is only used for base/small sizes (XS/S/M) to avoid silently
    // undercharging larger sizes (2XL/3XL) when the live API missed that key.
    // For larger sizes without a live price we return 0 so the UI + cart
    // guard can catch it instead of leaking margin.
    function garmentPriceForSize(size, g) {
        var garment = g || state.garment;
        if (!garment) return 0;
        var canonical = canonSize(size);
        var live = garment._livePrices;
        var liveCost = live ? live[canonical] : undefined;
        if (isFinite(liveCost) && liveCost > 0) {
            return Math.round(liveCost * GARMENT_MARKUP * 100) / 100;
        }
        // Safe fallback only for base sizes. Upsize (2XL+) must come from live
        // pricing — returning 0 forces the guard to block submit instead of
        // silently charging the S/M price for a 3XL.
        //
        // Additional safe cases:
        //   • Single-size garments (Adjustable caps, M/L-only hats, XL-only
        //     garments, numeric waist-only pants): the catalog price IS the
        //     correct price — no upsize exists.
        //   • S/M and M/L dual-slot sizes: these are the base tier in dual-slot
        //     hat lines (e.g. Flexfit 6277, Richardson 112); L/XL and XL/2XL in
        //     the same garment correctly fall through to the live-price guard.
        if (isBaseSafeSize(size, garment)) {
            return Math.round(garment.price * GARMENT_MARKUP * 100) / 100;
        }
        return 0;
    }

    // Whether a size can safely use the catalog base price when live pricing
    // is not available. MUST match garmentPriceForSize's fallback logic so
    // that validateOrder / updateStockBadges don't block orders on sizes
    // that would actually charge correctly from the catalog.
    function isBaseSafeSize(size, g) {
        var garment = g || state.garment;
        if (!garment) return false;
        var canonical = canonSize(size);
        var singleSizeGarment = garment.sizes && garment.sizes.length === 1;
        return (
            singleSizeGarment ||
            canonical === 'XS' || canonical === 'S' || canonical === 'M' ||
            canonical === 'OSFA' || canonical === 'ONESIZE' ||
            canonical === 'S/M' || canonical === 'M/L'
        );
    }

    // Weighted average garment price across selected sizes
    function weightedGarmentPrice() {
        if (!state.garment) return 0;
        var totalQty = 0;
        var totalCost = 0;
        Object.entries(state.sizes).forEach(function (e) {
            var size = e[0], qty = e[1];
            if (qty > 0) {
                totalQty += qty;
                totalCost += qty * garmentPriceForSize(size);
            }
        });
        return totalQty > 0 ? Math.round(totalCost / totalQty * 100) / 100 : markedUpGarmentPrice();
    }

    // ── DTF decoration pricing ──────────────────────────────────
    // Price per unit by: size tier × location combo × order quantity
    const DTF_TIERS = {
        sm: { // Standard DTF print — up to 12×12 print
            front: [{ min: 1, fee: 6.00 }, { min: 12, fee: 4.75 }, { min: 24, fee: 3.75 }, { min: 48, fee: 3.00 }, { min: 99, fee: 2.00 }],
            back:  [{ min: 1, fee: 8.50 }, { min: 12, fee: 6.75 }, { min: 24, fee: 5.50 }, { min: 48, fee: 4.50 }, { min: 99, fee: 3.25 }],
            both:  [{ min: 1, fee: 12.50 }, { min: 12, fee: 11.00 }, { min: 24, fee: 9.25 }, { min: 48, fee: 7.50 }, { min: 99, fee: 5.25 }]
        },
        lg: { // Large + XL garments (L, XL, 2XL, 3XL) — up to 16×16 print
            front: [{ min: 1, fee: 12.00 }, { min: 12, fee: 9.00 }, { min: 24, fee: 7.25 }, { min: 48, fee: 6.00 }, { min: 99, fee: 4.75 }],
            back:  [{ min: 1, fee: 14.50 }, { min: 12, fee: 11.50 }, { min: 24, fee: 9.25 }, { min: 48, fee: 7.75 }, { min: 99, fee: 6.00 }],
            both:  [{ min: 1, fee: 26.50 }, { min: 12, fee: 20.50 }, { min: 24, fee: 16.50 }, { min: 48, fee: 13.75 }, { min: 99, fee: 10.75 }]
        }
    };

    // Non-DTF decoration fee tiers (per location, tiered by qty) — from Hector
    const decoFeeTiers = {
        'embroidery':     [{ min: 1, fee: 12.00 }, { min: 12, fee: 9.50 }, { min: 24, fee: 7.60 }, { min: 48, fee: 6.25 }, { min: 100, fee: 5.50 }, { min: 250, fee: 4.75 }],
        'leather-patch':  [{ min: 1, fee: 12.00 }, { min: 12, fee: 9.50 }, { min: 24, fee: 7.60 }, { min: 48, fee: 6.25 }, { min: 100, fee: 5.50 }, { min: 250, fee: 4.75 }]
    };

    // Add-on location surcharges (flat per unit, not qty-tiered)
    const ADDON_FEES = {
        'left-sleeve': 5.00,
        'right-sleeve': 5.00,
        'back': 8.00,
        'side-hat': 4.00
    };

    function getAddonFee(loc, decoration) {
        // DTF front/back uses dedicated DTF tier matrix; do not stack back add-on there.
        if (loc === 'back' && decoration === 'dtf') return 0;

        // Hats/beanies side locations use side-hat fee.
        if ((state.category === 'hats' || state.category === 'beanies') && (loc === 'left-sleeve' || loc === 'right-sleeve')) {
            return ADDON_FEES['side-hat'];
        }

        return ADDON_FEES[loc] || 0;
    }

    // Embroidery tape charge — one-time fee, waived at 24+ units
    const EMBROIDERY_TAPE_FEE = 25.00;
    const EMBROIDERY_TAPE_WAIVE_QTY = 24;

    // Look up tiered fee from a tiers array at a given quantity
    function lookupTier(tiers, qty) {
        if (!tiers) return 0;
        let fee = tiers[0].fee;
        for (const t of tiers) { if (qty >= t.min) fee = t.fee; }
        return fee;
    }

    // Legacy wrapper (used by non-DTF methods)
    function getDecoFee(method, qty) {
        return lookupTier(decoFeeTiers[method], qty);
    }

    // Classify garment sizes into pricing tiers
    function getSizeTier(size) {
        // Exact matches first, then check for large indicators in combo sizes (e.g. "L/XL", "XL/2XL")
        var lgSizes = ['L', 'XL', '2XL', '3XL', '4XL', '5XL', '6XL', 'XXL', '3X', '4X'];
        if (lgSizes.includes(size)) return 'lg';
        // Combo hat sizes: split on "/" and check each part
        var parts = String(size).split('/');
        for (var i = 0; i < parts.length; i++) {
            if (lgSizes.includes(parts[i].trim())) return 'lg';
        }
        return 'sm';
    }

    // Count quantities per size tier
    function getQtyByTier() {
        let sm = 0, lg = 0;
        Object.entries(state.sizes).forEach(function (e) {
            if (getSizeTier(e[0]) === 'lg') lg += e[1]; else sm += e[1];
        });
        return { sm: sm, lg: lg, total: sm + lg };
    }

    // Determine DTF location combo from active locations
    function getDtfCombo() {
        const activeLocations = getActiveLocationsObject();
        const f = activeLocations.front, b = activeLocations.back;
        const frontDtf = f && locHasArtwork(f) && f.decoration === 'dtf';
        const backDtf  = b && locHasArtwork(b) && b.decoration === 'dtf';
        if (frontDtf && backDtf) return 'both';
        if (frontDtf) return 'front';
        if (backDtf) return 'back';
        return 'none';
    }

    // Determine DTF print size tier from active locations
    // Returns 'sm' (Standard, up to 12×12") or 'lg' (Oversized, up to 16×16") based on the
    // largest print size selected across all DTF locations.
    function getDtfPrintSizeTier() {
        var activeLocations = getActiveLocationsObject();
        var tier = 'sm';
        Object.values(activeLocations).forEach(function (d) {
            if (d && locHasArtwork(d) && d.decoration === 'dtf') {
                if (getEffectivePrintSize(d) === 'lg') tier = 'lg';
            }
        });
        return tier;
    }

    // Full order pricing breakdown
    function calcOrderPricing() {
        var totalQty = getTotalUnits();
        if (!state.garment || totalQty === 0) {
            return { totalQty: 0, decoUnit: 0,
                     unitPrice: 0, avgUnit: 0, tapeCharge: 0,
                     printSizeTier: 'sm',
                     garmentPrice: state.garment ? markedUpGarmentPrice() : 0, total: 0 };
        }

        var decoUnit = 0;
        var activeLocations = getActiveLocationsObject();

        // DTF pricing — uses logo print size tier (not garment size)
        var dtfCombo = getDtfCombo();
        var printSizeTier = getDtfPrintSizeTier();
        if (dtfCombo !== 'none') {
            decoUnit += lookupTier(DTF_TIERS[printSizeTier][dtfCombo], totalQty);
        }

        // Non-DTF front/back locations (embroidery, leather-patch — per location)
        ['front', 'back'].forEach(function (loc) {
            var d = activeLocations[loc];
            if (d && locHasArtwork(d) && d.decoration && d.decoration !== 'dtf') {
                var fee = getDecoFee(d.decoration, totalQty);
                decoUnit += fee;
                if (loc === 'back') {
                    decoUnit += getAddonFee('back', d.decoration);
                }
            }
        });

        // Add-on locations (sleeves — base decoration fee + flat surcharge per unit)
        Object.entries(activeLocations).forEach(function (e) {
            var loc = e[0], d = e[1];
            if (loc !== 'front' && loc !== 'back' && locHasArtwork(d) && d.decoration) {
                if (d.decoration !== 'dtf') {
                    decoUnit += getDecoFee(d.decoration, totalQty);
                } else if (dtfCombo === 'none') {
                    // DTF on addon location with no front/back DTF — use front tier as base
                    decoUnit += lookupTier(DTF_TIERS[printSizeTier]['front'], totalQty);
                }
                decoUnit += getAddonFee(loc, d.decoration);
            }
        });

        // Per-unit price (garment + decoration)
        // Use weighted average garment price if per-size pricing is available
        var gp = weightedGarmentPrice();
        var unitPrice = gp + decoUnit;

        // Order total (use exact per-size garment costs for accuracy)
        var garmentTotal = 0;
        Object.entries(state.sizes).forEach(function (e) {
            if (e[1] > 0) garmentTotal += e[1] * garmentPriceForSize(e[0]);
        });
        var orderTotal = garmentTotal + (totalQty * decoUnit);

        // Embroidery tape charge (one-time digitizing fee, waived at 24+ or if already digitized)
        var tapeCharge = 0;
        var hasEmb = Object.values(activeLocations).some(function (l) { return locHasArtwork(l) && l.decoration === 'embroidery'; });
        if (hasEmb && totalQty < EMBROIDERY_TAPE_WAIVE_QTY && !state.waiveTapeFee) {
            tapeCharge = EMBROIDERY_TAPE_FEE;
            orderTotal += tapeCharge;
        }

        return {
            totalQty: totalQty,
            decoUnit: decoUnit,
            unitPrice: unitPrice,
            avgUnit: orderTotal / totalQty,
            tapeCharge: tapeCharge,
            printSizeTier: printSizeTier,
            garmentPrice: gp,
            total: orderTotal
        };
    }

    // ==========================================
    // DOM References
    // ==========================================
    const stepEls = document.querySelectorAll('.builder-step');
    const progressSteps = document.querySelectorAll('.progress-step');
    const progressLines = document.querySelectorAll('.progress-line');
    const btnNext = document.getElementById('stepNext');
    const btnBack = document.getElementById('stepBack');
    const btnSubmit = document.getElementById('stepSubmit');

    // ==========================================
    // Step Navigation
    // ==========================================
    function canAdvance() {
        switch (state.currentStep) {
            case 1: return !!state.category;
            case 2: return !!(state.garment && state.color);
            case 3: {
                const locsWithArt = Object.values(state.locations).filter(l => locHasArtwork(l));
                return locsWithArt.length > 0 && locsWithArt.every(l => !!l.decoration);
            }
            case 4: return getTotalUnits() >= 1;
            default: return false;
        }
    }

    var _suppressSave = false;

    function updateNextBtn() {
        btnNext.disabled = !canAdvance();
        if (!_suppressSave) autoSave();
    }

    var _initialLoad = true; // skip auto-scroll on the very first goToStep(1)

    function goToStep(n) {
        if (n < 1 || n > state.totalSteps) return;
        state.currentStep = n;

        // Hide floating color picker when not on step 2 (or no garment selected)
        const colorBar = document.getElementById('colorPickerSection');
        if (n !== 2 || !state.garment) {
            colorBar.style.display = 'none';
        } else {
            colorBar.style.display = 'block';
        }
        updatePreviewFabOffset();
        updatePreviewFabVisibility();
        updatePreviewDrawerOffset();

        // Track current step on layout for CSS hooks
        document.querySelector('.builder-layout').dataset.step = n;

        // Show/hide step panels
        stepEls.forEach(el => {
            el.classList.toggle('active', parseInt(el.dataset.step) === n);
        });

        // Update progress bar
        progressSteps.forEach((el, i) => {
            const stepNum = parseInt(el.dataset.step);
            el.classList.toggle('active', stepNum === n);
            el.classList.toggle('completed', stepNum < n);
        });

        // Update bottom bar current-step highlight
        highlightCurrentStepChip();

        progressLines.forEach((line, i) => {
            line.classList.toggle('filled', i < n - 1);
        });

        // Auto-scroll progress bar so the active step is visible
        const activeStep = document.querySelector('.progress-step.active');
        const scrollContainer = document.querySelector('.builder-progress');
        if (activeStep && scrollContainer) {
            const containerRect = scrollContainer.getBoundingClientRect();
            const stepRect = activeStep.getBoundingClientRect();
            const target = scrollContainer.scrollLeft + (stepRect.left + stepRect.width / 2) - (containerRect.left + containerRect.width / 2);
            scrollContainer.scrollTo({ left: target, behavior: 'smooth' });
        }

        // Show/hide nav buttons
        btnBack.style.display = n === 1 ? 'none' : '';
        btnNext.style.display = n === state.totalSteps ? 'none' : '';
        btnSubmit.style.display = n === state.totalSteps ? '' : 'none';
        updateFloatingStepNav();

        // If going to review step, validate decorations then populate
        if (n === state.totalSteps) {
            var allowed = getAllowedMethods(state.category);
            if (!allowed.includes(state.decoration)) {
                state.decoration = allowed[0];
                syncDecorationCards(state.decoration);
            }
            Object.keys(state.locations).forEach(function (k) {
                var loc = state.locations[k];
                if (locHasArtwork(loc) && !loc.decoration) {
                    loc.decoration = state.decoration;
                }
                if (locHasArtwork(loc) && loc.decoration && !allowed.includes(loc.decoration)) {
                    loc.decoration = allowed[0];
                }
            });
            populateReview();
        }

        // Lock preview on review step (no drag/resize/controls)
        var isReview = (n === state.totalSteps);
        previewPanel.classList.toggle('preview-locked', isReview);
        if (isReview) logoOverlay.classList.remove('draggable');
        var titleText = document.querySelector('.preview-title-text');
        if (titleText) titleText.textContent = isReview ? 'Preview Locked' : 'Live Preview';

        // Mobile: move preview inline on step 3/5, back to grid on others.
        // Must run before scroll-to-toolbar so body is unlocked from position:fixed.
        handleInlinePreview(n);

        // Scroll so the toolbar + step title are visible
        // Skip on initial page load so users can read the hero first
        if (!_initialLoad) {
            var toolbar = document.getElementById('sessionToolbar');
            if (toolbar) {
                var y = toolbar.getBoundingClientRect().top + window.pageYOffset - 30;
                window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
            }
        }
        _initialLoad = false;

        // Refresh volume hint & totals when (re-)entering the qty step
        if (n === 4) updateTotals();

        // Enable/disable Next based on current step validation
        updateNextBtn();

        // After layout reflow, re-sync logo position so it stays consistent
        // across grid changes (e.g. step 2 → 3 widens the preview panel)
        requestAnimationFrame(function () {
            loadLocationOntoPreview();
            updateLogoPosition();
            updatePrintZones();
            syncControlsVisibility();
            applyLogoTransform();
        });
    }

    function updatePreviewFabOffset() {
        var fab = document.getElementById('previewFab');
        if (!fab) return;

        var colorBar = document.getElementById('colorPickerSection');
        var colorBarVisible = !!(colorBar && colorBar.style.display !== 'none' && state.currentStep === 2 && state.garment);

        // Keep the mobile preview FAB above the fixed color picker bar in step 2.
        if (isMobile() && colorBarVisible) {
            fab.style.bottom = '150px';
            return;
        }

        fab.style.bottom = '24px';
    }

    function updatePreviewFabVisibility() {
        var fab = document.getElementById('previewFab');
        if (!fab) return;

        // Hide preview launcher on step 1 and on inline-preview steps.
        var shouldShow = isMobile() && state.currentStep > 1 && state.currentStep !== 3 && state.currentStep !== 5;
        fab.style.display = shouldShow ? '' : 'none';
    }

    function updatePreviewDrawerOffset() {
        if (!previewPanel) return;

        var colorBar = document.getElementById('colorPickerSection');
        var colorBarVisible = !!(colorBar && colorBar.style.display !== 'none' && state.currentStep === 2 && state.garment);

        if (isMobile() && colorBarVisible) {
            previewPanel.style.bottom = (colorBar.offsetHeight + 12) + 'px';
            return;
        }

        previewPanel.style.bottom = '10px';
    }

    btnNext.addEventListener('click', () => goToStep(state.currentStep + 1));
    btnBack.addEventListener('click', () => goToStep(state.currentStep - 1));

    // Review "Edit" button — go back to step 3 (Decoration) to edit artwork
    var reviewEditBtn = document.getElementById('reviewEditBtn');
    if (reviewEditBtn) reviewEditBtn.addEventListener('click', () => goToStep(3));

    // Clickable progress steps (only completed or current)
    progressSteps.forEach(el => {
        el.addEventListener('click', () => {
            const target = parseInt(el.dataset.step);
            if (target <= state.currentStep) {
                goToStep(target);
            }
        });
    });

    // ==========================================
    // Step 1: Category Selection
    // ==========================================
    document.querySelectorAll('.category-card').forEach(card => {
        card.addEventListener('click', function () {
            document.querySelectorAll('.category-card').forEach(c => c.classList.remove('selected'));
            this.classList.add('selected');
            state.category = this.dataset.category;
            populateGarments(state.category);
            updateLocationLabels();
            updateDecoMethodsForCategory();
            updateLogoPosition();
            updatePreview();
            updateNextBtn();
            // Auto-advance to step 2
            goToStep(2);
        });
    });

    // ==========================================
    // Step 2: Garment Grid + Color Picker
    // ==========================================
    function populateGarments(category) {
        const grid = document.getElementById('garmentGrid');
        const items = (garments[category] || []).filter(garmentHasRenderableImages);
        grid.innerHTML = '';

        function getSortedBrands(list) {
            const BRAND_ORDER = ['Nike', 'Carhartt', 'Bella+Canvas', 'Gildan', 'Adidas', 'Under Armour', 'Columbia'];
            return [...new Set(list)].filter(Boolean).sort((a, b) => {
                const ai = BRAND_ORDER.indexOf(a), bi = BRAND_ORDER.indexOf(b);
                if (ai !== -1 && bi !== -1) return ai - bi;
                if (ai !== -1) return -1;
                if (bi !== -1) return 1;
                return a.localeCompare(b);
            });
        }

        function getAvailableBrandsFromGrid() {
            return getSortedBrands(Array.from(grid.querySelectorAll('.garment-card')).map(card => card.dataset.brand));
        }

        items.forEach(g => {
            const card = document.createElement('button');
            card.className = 'garment-card';
            card.dataset.garmentId = g.id;
            card.dataset.brand = g.brand;
            var thumbUrl = getGarmentThumbUrl(g);
            var fallbackUrl = getGarmentFallbackUrl(g);
            var thumbContent = thumbUrl
                ? `<img src="${thumbUrl}" alt="${g.name}" loading="lazy"${fallbackUrl && fallbackUrl !== thumbUrl ? ` data-fallback="${fallbackUrl}"` : ''}>`
                : `<i class="fas ${g.icon}"></i>`;
            card.innerHTML = `
                <div class="garment-thumb">${thumbContent}</div>
                <div class="garment-info">
                    <h4>${g.name}</h4>
                    <span class="garment-brand">${g.brand}</span>
                    <span class="garment-price">From $${markedUpGarmentPrice(g).toFixed(2)}</span>
                </div>
            `;
            var thumbImg = card.querySelector('img');
            if (thumbImg) {
                thumbImg.onerror = function () {
                    var fallback = this.dataset.fallback;
                    if (fallback && fallback !== this.src) {
                        this.dataset.fallback = '';
                        this.src = fallback;
                        return;
                    }
                    card.remove();
                    syncBrandChipVisibility();
                };
            }
            card.addEventListener('click', () => selectGarment(g, { userInitiated: true }));
            grid.appendChild(card);
        });

        // Build dynamic brand filter chips
        const chipContainer = document.querySelector('.filter-chips');
        chipContainer.innerHTML = '';
        const allChip = document.createElement('button');
        allChip.className = 'chip active';
        allChip.dataset.brand = 'all';
        allChip.textContent = 'All Brands';
        chipContainer.appendChild(allChip);

        const brands = getSortedBrands(items.map(g => g.brand));
        brands.forEach(b => {
            const chip = document.createElement('button');
            chip.className = 'chip';
            chip.dataset.brand = b;
            chip.textContent = b;
            chipContainer.appendChild(chip);
        });

        function syncBrandChipVisibility() {
            const availableBrands = getAvailableBrandsFromGrid();
            const activeChip = chipContainer.querySelector('.chip.active');
            const activeBrand = activeChip ? activeChip.dataset.brand : 'all';

            chipContainer.querySelectorAll('.chip').forEach(chip => {
                if (chip.dataset.brand === 'all') {
                    chip.style.display = availableBrands.length ? '' : 'none';
                    return;
                }
                chip.style.display = availableBrands.includes(chip.dataset.brand) ? '' : 'none';
            });

            if (activeBrand !== 'all' && !availableBrands.includes(activeBrand)) {
                chipContainer.querySelectorAll('.chip').forEach(c => c.classList.remove('active'));
                allChip.classList.add('active');
                document.querySelectorAll('.garment-card').forEach(card => {
                    card.style.display = '';
                });
            }
        }

        // Bind chip click handlers
        chipContainer.querySelectorAll('.chip').forEach(chip => {
            chip.addEventListener('click', function () {
                chipContainer.querySelectorAll('.chip').forEach(c => c.classList.remove('active'));
                this.classList.add('active');
                const brand = this.dataset.brand;
                document.querySelectorAll('.garment-card').forEach(card => {
                    card.style.display = (brand === 'all' || card.dataset.brand === brand) ? '' : 'none';
                });
                // Auto-select first visible garment of this brand
                const firstVisible = brand === 'all'
                    ? items[0]
                    : items.find(g => g.brand === brand);
                if (firstVisible) selectGarment(firstVisible);
            });
        });

        // Auto-select first garment when category is populated
        if (items.length) selectGarment(items[0]);

        syncBrandChipVisibility();

        // Re-init scroll-fade on newly built chip bar
        initScrollFade(chipContainer);
    }

    // Floating step nav inside color picker bar
    document.getElementById('floatingBack').addEventListener('click', function () { goToStep(state.currentStep - 1); });
    document.getElementById('floatingNext').addEventListener('click', function () { goToStep(state.currentStep + 1); });

    function updateFloatingStepNav() {
        var label = document.getElementById('floatingNavLabel');
        var backBtn = document.getElementById('floatingBack');
        var nextBtn = document.getElementById('floatingNext');
        if (label) label.textContent = 'Step ' + state.currentStep + ' of ' + state.totalSteps;
        if (backBtn) backBtn.style.display = state.currentStep <= 1 ? 'none' : '';
        if (nextBtn) nextBtn.style.display = state.currentStep >= state.totalSteps ? 'none' : '';
    }

    function selectGarment(garment, options) {
        options = options || {};
        state.garment = garment;
        state.color = null;
        state.colorName = null;

        // Invalidate any stale live prices from a prior garment, then kick off
        // a fresh fetch. Status starts as 'loading' so the size matrix shows
        // "Price loading…" on upsize rows until the API responds.
        garment._livePrices = null;
        garment._livePriceColor = '';
        garment._livePriceStatus = 'loading';
        fetchLivePricingForGarment();

        // Visual selection
        document.querySelectorAll('.garment-card').forEach(c => {
            c.classList.toggle('selected', c.dataset.garmentId === garment.id);
        });

        // Show color picker
        const section = document.getElementById('colorPickerSection');
        const container = document.getElementById('colorSwatches');
        section.style.display = 'block';
        updatePreviewFabOffset();
        container.innerHTML = '';
        container.scrollLeft = 0;

        var defaultColorName = getDefaultColorName(garment);
        markVisibleColorsChecking(garment);

        garment.colors.forEach((colorObj) => {
            var hasPreviewImages = colorHasRenderableImages(garment, colorObj);
            if (!hasPreviewImages) return;

            const swatch = document.createElement('button');
            swatch.className = 'color-swatch';
            swatch.style.backgroundColor = colorObj.hex;
            swatch.dataset.color = colorObj.hex;
            swatch.dataset.name = colorObj.name;
            swatch.title = colorObj.name;
            var swUrl = colorObj.swatch && colorObj.swatch.indexOf('sw.jpg') === -1 ? colorObj.swatch : colorObj.front;
            if (swUrl) {
                swatch.style.backgroundImage = 'url(' + swUrl + ')';
                swatch.style.backgroundSize = 'cover';
                swatch.style.backgroundPosition = 'center';
            }
            applyAvailabilityToColorControl(swatch, colorObj);

            swatch.addEventListener('click', () => {
                if (swatch.disabled || isColorKnownOutOfStock(colorObj)) return;
                selectColor(colorObj.name);
            });

            container.appendChild(swatch);
        });

        // Auto-select immediately only when this color is not waiting on live
        // inventory. Live inventory responses auto-select the first available
        // color if no color has been selected yet.
        var defaultColorObj = garment.colors.find(function (c) { return c.name === defaultColorName; });
        if (!defaultColorObj || !defaultColorObj._inventoryLoading) {
            selectColor(defaultColorName);
        }
        fetchInventoryForVisibleColors(garment);

        // Update view tab availability for this garment's images
        updateViewAvailability();

        // Refresh arrow visibility after swatches rendered
        requestAnimationFrame(updateColorArrows);

        // Rebuild size matrix for this garment's available sizes,
        // preserving existing quantities if same size list.
        rebuildSizeMatrix(garment.sizes);

        updatePreview();
        updateNextBtn();

        if (options.userInitiated && isMobile() && state.currentStep === 2 && !previewPanel.classList.contains('preview-open')) {
            openPreviewDrawer();
        }

        // Deep-link from homepage slider: open preview after step transition settles
        if (options.deepLink && isMobile()) {
            setTimeout(function() {
                if (state.currentStep === 2 && !previewPanel.classList.contains('preview-open')) {
                    openPreviewDrawer();
                }
            }, 150);
        }
    }

    function selectColor(colorName) {
        // Look up by unique name to avoid duplicate-hex issues
        var colorObj = state.garment && state.garment.colors.find(c => c.name === colorName);
        if (!colorObj) return;
        if (colorObj._inventoryLoading) {
            showBuilderToast('Checking availability for ' + colorObj.name + '...');
            return;
        }
        if (isColorKnownOutOfStock(colorObj)) {
            refreshColorControlAvailability(state.garment, colorObj.name);
            showBuilderToast(colorObj.name + ' is out of stock.');
            return;
        }
        state.color = colorObj ? colorObj.hex : colorName;
        state.colorName = colorName;
        document.querySelectorAll('.color-swatch').forEach(s => {
            s.classList.toggle('selected', s.dataset.name === colorName);
        });
        // Invalidate live prices when the color changes — supplier APIs are
        // priced per-color and we do not want the previous color's prices to
        // linger while the new fetch is in flight.
        if (state.garment && state.garment._livePriceColor && state.garment._livePriceColor !== colorName) {
            state.garment._livePrices = null;
            state.garment._livePriceColor = '';
            state.garment._livePriceStatus = 'loading';
        }
        updateStockBadges();
        updateViewAvailability();
        updatePreview();
        updateNextBtn();
        fetchSanmarInventoryForSelection();
        fetchSSInventoryForSelection();
        fetchLivePricingForGarment();
    }

    // ==========================================
    // Color bar scroll arrows (desktop)
    // ==========================================
    const colorSwatchesEl = document.getElementById('colorSwatches');
    const colorArrowLeft = document.getElementById('colorArrowLeft');
    const colorArrowRight = document.getElementById('colorArrowRight');

    function updateColorArrows() {
        if (!colorSwatchesEl) return;
        const el = colorSwatchesEl;
        const overflows = el.scrollWidth > el.clientWidth + 2;
        el.classList.toggle('can-scroll', overflows);
        const canScrollLeft = el.scrollLeft > 2;
        const canScrollRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 2;
        colorArrowLeft.classList.toggle('visible', canScrollLeft);
        colorArrowRight.classList.toggle('visible', canScrollRight);
    }

    colorSwatchesEl.addEventListener('scroll', updateColorArrows);

    colorArrowLeft.addEventListener('click', () => {
        colorSwatchesEl.scrollBy({ left: -200, behavior: 'smooth' });
    });
    colorArrowRight.addEventListener('click', () => {
        colorSwatchesEl.scrollBy({ left: 200, behavior: 'smooth' });
    });

    // Brand filter chips — handled dynamically inside populateGarments()

    // ==========================================
    // Step 3: Decoration + Location
    // ==========================================
    // Default decoration pill highlight
    syncDecorationCards(state.decoration);

    // Decoration pill bar (above preview) — per-location method selection
    document.querySelectorAll('.deco-pill').forEach(pill => {
        pill.addEventListener('click', function () {
            const method = this.dataset.method;
            document.querySelectorAll('.deco-pill').forEach(p => p.classList.remove('active'));
            this.classList.add('active');
            state.decoration = method;

            // Save to active location
            const loc = activeLoc();
            loc.decoration = method;

            // Re-render active location's logo with new method
            updatePreview();
            updateLocFileBar();
            updateLocationStatus();
            updateLocationSummary();
            updateNextBtn();
        });
    });

    // Print Size toggle (Standard / Oversized) — per DTF location
    document.querySelectorAll('.print-size-btn').forEach(function (btn) {
        btn.addEventListener('click', function () {
            var size = this.dataset.size;
            var loc = activeLoc();
            loc.printSize = size;
            updatePrintSizeSelector();
            updatePreview();
        });
    });

    // ==========================================
    // Location Tabs (on preview panel)
    // ==========================================
    function switchToLocation(loc) {
        if (loc === state.activeLocation) return;

        // Switch
        state.activeLocation = loc;
        state.viewSide = locationViewSide[loc];

        // Restore per-location decoration if this location has one
        const locData = state.locations[loc];
        if (locData.decoration) {
            state.decoration = locData.decoration;
            syncDecorationCards(state.decoration);
        } else {
            // Default to first available decoration method
            var firstPill = document.querySelector('.deco-pill');
            var defaultMethod = firstPill ? firstPill.dataset.method : 'dtf';
            state.decoration = defaultMethod;
            locData.decoration = defaultMethod;
            syncDecorationCards(defaultMethod);
        }

        // Update tab UI
        document.querySelectorAll('.loc-tab').forEach(t =>
            t.classList.toggle('active', t.dataset.location === loc)
        );

        // Update status bar
        document.querySelectorAll('.loc-status-item').forEach(s =>
            s.classList.toggle('active', s.dataset.loc === loc)
        );

        // Force garment image refresh for new view side
        garmentImg.dataset.currentKey = '';

        // Load this location's logo state onto the preview
        loadLocationOntoPreview();
        updatePreview();
        updateLocFileBar();
        updateLocationSummary();
        updateNextBtn();
    }

    function loadLocationOntoPreview() {
        const loc = activeLoc();
        const method = loc.decoration || state.decoration;
        if (loc.logoDataUrl) {
            setLogoPreviewImage(loc.renderedLogos[method] || loc.logoDataUrl, state.activeLocation + '_' + method);
            logoImg.style.display = '';
            logoPlaceholder.style.display = 'none';
            if (!previewPanel.classList.contains('preview-locked')) logoOverlay.classList.add('draggable');
            logoOverlay.style.setProperty('--logo-url', 'url(' + loc.logoDataUrl + ')');
            if (loc.logoMaskUrl) {
                logoOverlay.style.setProperty('--logo-mask-url', 'url(' + loc.logoMaskUrl + ')');
            }
            document.getElementById('dragHint').style.display = '';
            // If we already know this logo's aspect ratio, apply it synchronously
            // so the overlay doesn't flash/resize when switching front ↔ back.
            if (loc.aspectRatio) {
                logoOverlay.style.aspectRatio = loc.aspectRatio;
                logoOverlay.style.height = 'auto';
            }
            // Probe to measure aspect ratio (stores it for future synchronous use)
            const probe = new Image();
            probe.onload = function () {
                var ar = probe.naturalWidth / probe.naturalHeight;
                loc.aspectRatio = ar;
                logoOverlay.style.aspectRatio = ar;
                logoOverlay.style.height = 'auto';
            };
            probe.src = loc.logoDataUrl;
            // Restore transforms
            state.logoScale = loc.scale;
            state.logoRotation = loc.rotation;
            applyLogoTransform();
        } else {
            logoImg.src = '';
            logoImg.style.display = 'none';
            logoPlaceholder.style.display = '';
            logoOverlay.classList.remove('draggable');
            logoOverlay.classList.remove('active');
            syncControlsVisibility();
            logoOverlay.style.removeProperty('--logo-url');
            logoOverlay.style.removeProperty('--logo-mask-url');
            logoOverlay.style.aspectRatio = '';
            logoOverlay.style.height = '';
            document.getElementById('dragHint').style.display = 'none';
            state.logoScale = 1;
            state.logoRotation = 0;
            logoOverlay.style.transform = '';
        }
        updateLogoPosition();
    }

    function setLogoPreviewImage(src, keyPrefix) {
        if (!src) return;
        var key = (keyPrefix || state.activeLocation || 'logo') + '|' + src;
        if (logoImg.dataset.currentKey === key) {
            logoImg.src = src;
            logoImg.classList.remove('loading');
            return;
        }

        var loadToken = String(Date.now()) + Math.random().toString(16).slice(2);
        logoImg.dataset.pendingKey = loadToken;

        var preload = new Image();
        preload.onload = function () {
            if (logoImg.dataset.pendingKey !== loadToken) return;
            logoImg.classList.add('loading');
            logoImg.src = src;
            logoImg.dataset.currentKey = key;
            requestAnimationFrame(function () {
                if (logoImg.dataset.pendingKey !== loadToken) return;
                logoImg.classList.remove('loading');
                delete logoImg.dataset.pendingKey;
            });
        };
        preload.onerror = function () {
            if (logoImg.dataset.pendingKey !== loadToken) return;
            logoImg.src = src;
            logoImg.dataset.currentKey = key;
            logoImg.classList.remove('loading');
            delete logoImg.dataset.pendingKey;
        };
        preload.src = src;
    }

    // File info bar for current location
    function updateLocFileBar() {
        const bar = document.getElementById('locFileBar');
        const editAiEl = document.getElementById('locFileEditAi');
        const loc = activeLoc();
        if (loc.logoFile) {
            bar.style.display = 'flex';
            document.getElementById('locFileName').textContent = loc.logoFile.name;
            document.getElementById('locFileSize').textContent = formatBytes(loc.logoFile.size);
            const thumb = document.getElementById('locFileThumb');
            if (loc.logoDataUrl) {
                thumb.innerHTML = '<img src="' + loc.logoDataUrl + '" alt="Logo">';
            } else {
                thumb.innerHTML = '<i class="fas fa-file" style="font-size:1rem;color:var(--accent);display:flex;align-items:center;justify-content:center;width:100%;height:100%;"></i>';
            }
            // Show decoration method badge
            const methodEl = document.getElementById('locFileMethod');
            if (methodEl) {
                methodEl.textContent = loc.decoration ? (decoShortLabels[loc.decoration] || loc.decoration) : '';
                methodEl.style.display = loc.decoration ? '' : 'none';
            }
            // Show AI edit button when we have a data URL
            if (editAiEl) editAiEl.style.display = loc.logoDataUrl ? '' : 'none';
        } else if (loc.logoDataUrl && loc.aiMeta) {
            // AI-generated image (no File object)
            bar.style.display = 'flex';
            document.getElementById('locFileName').textContent = 'AI Generated Concept';
            document.getElementById('locFileSize').textContent = '';
            const thumb = document.getElementById('locFileThumb');
            thumb.innerHTML = '<img src="' + loc.logoDataUrl + '" alt="AI Generated">';
            const methodEl = document.getElementById('locFileMethod');
            if (methodEl) {
                methodEl.textContent = loc.decoration ? (decoShortLabels[loc.decoration] || loc.decoration) : '';
                methodEl.style.display = loc.decoration ? '' : 'none';
            }
            if (editAiEl) editAiEl.style.display = '';
        } else {
            bar.style.display = 'none';
            if (editAiEl) editAiEl.style.display = 'none';
        }
        updatePrintSizeSelector();
    }

    // ── Print Size Selector (Standard vs Oversized) ──────────────
    // Auto-detects from logo scale; user can override.
    // Only shows for DTF locations that have artwork.
    function autoDetectPrintSize(loc) {
        // Scale ≤ 1.0 → Standard (up to 12×12"); > 1.0 → Oversized (up to 16×16")
        var s = loc.scale || 1;
        return s <= 1.0 ? 'sm' : 'lg';
    }

    function getEffectivePrintSize(loc) {
        if (!loc || !locHasArtwork(loc) || loc.decoration !== 'dtf') return 'sm';
        return loc.printSize || autoDetectPrintSize(loc);
    }

    function updatePrintSizeSelector() {
        var el = document.getElementById('printSizeSelector');
        if (!el) return;
        var loc = activeLoc();
        // Only show for DTF locations with artwork
        if (locHasArtwork(loc) && loc.decoration === 'dtf') {
            el.style.display = '';
            var detected = autoDetectPrintSize(loc);
            var current = loc.printSize || detected;
            // Update toggle state
            el.querySelectorAll('.print-size-btn').forEach(function (btn) {
                btn.classList.toggle('active', btn.dataset.size === current);
            });
            // Show auto-detect hint
            var hint = el.querySelector('.print-size-hint');
            if (hint) {
                if (!loc.printSize) {
                    hint.textContent = 'Auto-detected from logo size';
                    hint.style.display = '';
                } else if (loc.printSize !== detected) {
                    hint.textContent = 'Suggested: ' + (detected === 'sm' ? 'Standard' : 'Oversized');
                    hint.style.display = '';
                } else {
                    hint.textContent = '';
                    hint.style.display = 'none';
                }
            }
        } else {
            el.style.display = 'none';
        }
    }

    // Update location status bar
    function updateLocationStatus() {
        var availability = getLocationAvailability();
        document.querySelectorAll('.loc-status-item').forEach(item => {
            const loc = item.dataset.loc;
            const data = state.locations[loc];
            item.classList.toggle('has-art', !!(availability[loc] && locHasArtwork(data)));
            item.classList.toggle('active', loc === state.activeLocation);
        });
        // Update tab indicators + decoration badges
        document.querySelectorAll('.loc-tab').forEach(tab => {
            const loc = tab.dataset.location;
            const data = state.locations[loc];
            tab.classList.toggle('has-art', !!(availability[loc] && locHasArtwork(data)));
            // Show decoration method badge on tab
            let badge = tab.querySelector('.loc-tab-method');
            if (!badge) {
                badge = document.createElement('span');
                badge.className = 'loc-tab-method';
                tab.appendChild(badge);
            }
            if (availability[loc] && locHasArtwork(data) && data.decoration) {
                badge.textContent = decoShortLabels[data.decoration] || '';
                badge.style.display = '';
            } else {
                badge.style.display = 'none';
            }
        });
    }

    // Update location artwork summary (step 3 left panel)
    function updateLocationSummary() {
        var availability = getLocationAvailability();
        document.querySelectorAll('.loc-summary-item').forEach(item => {
            const loc = item.dataset.loc;
            const data = state.locations[loc];
            const statusEl = item.querySelector('.loc-summary-status');
            const badgeEl = item.querySelector('.loc-summary-badge');

            if (!availability[loc]) {
                item.classList.remove('has-art');
                statusEl.textContent = 'Not available for this garment';
                badgeEl.textContent = '';
            } else if (locHasArtwork(data)) {
                item.classList.add('has-art');
                statusEl.textContent = (data.logoFile ? data.logoFile.name : 'AI Generated Concept');
                badgeEl.textContent = data.decoration ? (decoShortLabels[data.decoration] || data.decoration) : '';
            } else {
                item.classList.remove('has-art');
                statusEl.textContent = 'No artwork yet';
                badgeEl.textContent = '';
            }
            item.classList.toggle('active', loc === state.activeLocation);
        });
    }

    // Location summary click → switch location
    document.querySelectorAll('.loc-summary-item').forEach(item => {
        item.addEventListener('click', () => {
            if (state.currentStep === state.totalSteps) return;
            switchToLocation(item.dataset.loc);
            updateLocationSummary();
        });
    });

    // Location tab click handlers
    document.querySelectorAll('.loc-tab').forEach(tab => {
        tab.addEventListener('click', function () {
            switchToLocation(this.dataset.location);
        });
    });

    // Logo placeholder click → trigger file upload
    document.getElementById('logoPlaceholder').addEventListener('click', function () {
        if (state.currentStep === state.totalSteps) return;
        if (!state.decoration) {
            alert('Please select a decoration method first.');
            return;
        }
        fileInput.click();
    });

    // Remove artwork from current location
    document.getElementById('locFileRemove').addEventListener('click', function () {
        const loc = activeLoc();
        loc.logoFile = null;
        loc.logoDataUrl = null;
        loc.logoMaskUrl = null;
        loc.renderedLogos = {};
        loc.scale = 1;
        loc.rotation = 0;
        loc.decoration = null;
        loc.posLeft = null;
        loc.posTop = null;
        removeLogoFromPreview();
        updateLocFileBar();
        updateLocationStatus();
        updateLocationSummary();
        updateSelectionsBar();
        updateNextBtn();
    });

    // ==========================================
    // File Upload (routes to active location)
    // ==========================================
    const fileInput = document.getElementById('logoUpload');

    // Logo rights acknowledgment tracking
    var _rightsAcknowledged = false;

    fileInput.addEventListener('change', () => {
        if (fileInput.files.length) {
            var pendingFile = fileInput.files[0];
            if (_rightsAcknowledged) {
                handleFile(pendingFile);
            } else {
                showRightsModal(pendingFile);
            }
        }
    });

    // Rights modal logic
    function showRightsModal(file) {
        var modal = document.getElementById('logoRightsModal');
        var checkbox = document.getElementById('rightsCheckbox');
        var confirmBtn = document.getElementById('rightsConfirm');
        var cancelBtn = document.getElementById('rightsCancel');

        checkbox.checked = false;
        confirmBtn.disabled = true;
        modal.classList.add('active');

        function cleanup() {
            modal.classList.remove('active');
            checkbox.removeEventListener('change', onCheck);
            confirmBtn.removeEventListener('click', onConfirm);
            cancelBtn.removeEventListener('click', onCancel);
        }

        function onCheck() {
            confirmBtn.disabled = !checkbox.checked;
        }
        function onConfirm() {
            _rightsAcknowledged = true;
            cleanup();
            handleFile(file);
        }
        function onCancel() {
            cleanup();
            fileInput.value = '';
        }

        checkbox.addEventListener('change', onCheck);
        confirmBtn.addEventListener('click', onConfirm);
        cancelBtn.addEventListener('click', onCancel);
    }

    let handleFile = function (file) {
        // Validate type
        const ext = file.name.split('.').pop().toLowerCase();
        const allowedExts = ['png', 'jpg', 'jpeg', 'webp', 'svg'];

        if (!allowedExts.includes(ext)) {
            alert('Please upload a PNG, JPG, WebP, or SVG logo file.');
            return;
        }

        // Validate size (25MB)
        if (file.size > 25 * 1024 * 1024) {
            alert('File is too large. Maximum size is 25MB.');
            return;
        }

        const loc = activeLoc();
        loc.logoFile = file;
        loc.scale = 1;
        loc.rotation = 0;
        loc.decoration = state.decoration;  // Save current decoration to this location

        // Generate data URL if it's an image
        if (file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onload = function (e) {
                loc.logoDataUrl = e.target.result;
                updateLogoOnPreview();
                updateLocFileBar();
                updateLocationStatus();
                updateLocationSummary();
                updateNextBtn();
            };
            reader.readAsDataURL(file);
        } else {
            loc.logoDataUrl = null;
            updateLocFileBar();
            updateLocationStatus();
            updateLocationSummary();
            updateNextBtn();
        }
        fileInput.value = '';
    }

    // ==========================================
    // AI-generated image hook (used by builder-ai.js)
    // Mirrors handleFile but receives a data URL directly
    // ==========================================
    window.__BUILDER_AI_HOOK = function (dataUrl, meta) {
        if (!dataUrl) return;
        const loc = activeLoc();
        loc.logoFile = null;                   // no File object for AI images
        loc.logoDataUrl = dataUrl;
        loc.scale = 1;
        loc.rotation = 0;
        loc.decoration = state.decoration;
        loc.renderedLogos = {};
        loc.posLeft = null;
        loc.posTop = null;
        if (meta) loc.aiMeta = meta;           // { sessionId, resultId, source }
        updateLogoOnPreview();
        updateLocFileBar();
        updateLocationStatus();
        updateLocationSummary();
        updateNextBtn();
    };

    // ==========================================
    // AI Edit button — opens the edit modal with current artwork
    // ==========================================
    var editAiBtn = document.getElementById('locFileEditAi');
    if (editAiBtn) {
        editAiBtn.addEventListener('click', function () {
            var loc = activeLoc();
            if (loc && loc.logoDataUrl && window.__BUILDER_AI && window.__BUILDER_AI.openEditModal) {
                window.__BUILDER_AI.openEditModal(loc.logoDataUrl);
            }
        });
    }

    function formatBytes(bytes) {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    }

    // ==========================================
    // Step 4: Size Matrix (dynamic — rebuilt per garment)
    // ==========================================
    function rebuildSizeMatrix(sizes) {
        var matrix = document.getElementById('sizeMatrix');
        var header = matrix.querySelector('.matrix-header');
        matrix.innerHTML = '';
        matrix.appendChild(header);

        // Preserve previous quantities for sizes that still exist
        var prevSizes = Object.assign({}, state.sizes);
        var prevDesired = Object.assign({}, state.desiredSizes);
        state.sizes = {};
        state.desiredSizes = {};
        (sizes || []).forEach(function (sizeName) {
            var restored = prevDesired.hasOwnProperty(sizeName) ? prevDesired[sizeName]
                         : prevSizes.hasOwnProperty(sizeName) ? prevSizes[sizeName] : 0;
            state.sizes[sizeName] = restored;
            state.desiredSizes[sizeName] = restored;
            var row = document.createElement('div');
            row.className = 'matrix-row';
            row.dataset.size = sizeName;
            row.innerHTML =
                '<div class="matrix-label">' + sizeName + '<span class="stock-badge"></span></div>' +
                '<div class="matrix-input-cell">' +
                    '<div class="qty-control">' +
                        '<button class="qty-minus"><i class="fas fa-minus"></i></button>' +
                        '<input type="number" class="qty-input" value="' + restored + '" min="0" max="999" data-size="' + sizeName + '">' +
                        '<button class="qty-plus"><i class="fas fa-plus"></i></button>' +
                    '</div>' +
                '</div>';
            matrix.appendChild(row);
        });
        updateStockBadges();
        updateTotals();
    }

    /** Show a brief toast notification in the builder */
    function showBuilderToast(msg) {
        var existing = document.querySelector('.builder-toast');
        if (existing) existing.remove();
        var toast = document.createElement('div');
        toast.className = 'builder-toast';
        toast.textContent = msg;
        document.body.appendChild(toast);
        requestAnimationFrame(function () { toast.classList.add('show'); });
        setTimeout(function () {
            toast.classList.remove('show');
            setTimeout(function () { toast.remove(); }, 300);
        }, 3500);
    }

    /** Refresh stock badges for the current garment + color selection */
    function updateStockBadges() {
        if (!state.garment || !state.colorName) return;
        var colorObj = state.garment.colors.find(c => c.name === state.colorName);
        var inv = colorObj && colorObj._inventoryLiveLoaded ? colorObj.inventory : null; // { size: qty } or undefined
        var clamped = [];   // sizes reduced
        var restored = [];  // sizes restored from desiredSizes

        document.querySelectorAll('#sizeMatrix .matrix-row').forEach(function (row) {
            var sz = row.dataset.size;
            if (!sz) return;
            var badge = row.querySelector('.stock-badge');
            if (!badge) return;
            var input = row.querySelector('.qty-input');
            var minusBtn = row.querySelector('.qty-minus');
            var plusBtn = row.querySelector('.qty-plus');
            var desired = state.desiredSizes[sz] || 0;

            if (!inv) {
                // No inventory data (e.g. SanMar) — hide badge, restore desired
                badge.className = 'stock-badge';
                badge.textContent = '';
                row.classList.remove('oos');
                if (input) {
                    input.disabled = false; input.max = 999;
                    var cur = parseInt(input.value) || 0;
                    if (desired > cur) {
                        input.value = desired; state.sizes[sz] = desired;
                        restored.push(sz + ' restored to ' + desired);
                    }
                }
                if (minusBtn) minusBtn.disabled = false;
                if (plusBtn) plusBtn.disabled = false;
                return;
            }

            var qty = inv[sz];
            if (qty === undefined || qty === null) qty = 0;

            if (qty === 0) {
                badge.className = 'stock-badge oos';
                badge.textContent = 'Out of Stock';
                row.classList.add('oos');
                if (input) {
                    var prev = parseInt(input.value) || 0;
                    if (prev > 0) clamped.push(sz + ' reduced to 0 (out of stock)');
                    input.value = 0; input.disabled = true; state.sizes[sz] = 0;
                }
                if (minusBtn) minusBtn.disabled = true;
                if (plusBtn) plusBtn.disabled = true;
            } else {
                var label = qty <= 50 ? (qty + ' left') : 'In Stock';
                badge.className = qty <= 50 ? 'stock-badge low' : 'stock-badge in-stock';
                badge.textContent = label;
                row.classList.remove('oos');
                if (input) {
                    input.disabled = false; input.max = qty;
                    var cur = parseInt(input.value) || 0;
                    // Restore desired qty if stock now allows it
                    var target = Math.min(desired, qty);
                    if (target > cur) {
                        input.value = target; state.sizes[sz] = target;
                        restored.push(sz + ' restored to ' + target);
                    } else if (cur > qty) {
                        clamped.push(sz + ' reduced to ' + qty + ' (only ' + qty + ' available)');
                        input.value = qty; state.sizes[sz] = qty;
                    }
                }
                if (minusBtn) minusBtn.disabled = false;
                if (plusBtn) plusBtn.disabled = false;
            }
        });

        // ── Price-availability overlay ──
        // If live per-size pricing is not loaded for this size, disable the
        // row so the user cannot accidentally check out with a fallback
        // (silently-undercharged) price. Only applies to upsize rows
        // (L / XL / 2XL / 3XL / …) — base-safe sizes (XS/S/M + single-size
        // garments + S/M + M/L dual-slot base tier) still use catalog.
        var priceStatus = state.garment._livePriceStatus || null;
        document.querySelectorAll('#sizeMatrix .matrix-row').forEach(function (row) {
            var sz = row.dataset.size;
            if (!sz) return;
            if (isBaseSafeSize(sz)) return;
            if (hasLivePriceForSize(sz)) return;
            // Upsize with no live price yet — disable until it loads or mark failed.
            var badge = row.querySelector('.stock-badge');
            var input = row.querySelector('.qty-input');
            var minusBtn = row.querySelector('.qty-minus');
            var plusBtn = row.querySelector('.qty-plus');
            if (priceStatus === 'failed') {
                if (badge) { badge.className = 'stock-badge oos'; badge.textContent = 'Price unavailable'; }
            } else {
                if (badge) { badge.className = 'stock-badge low'; badge.textContent = 'Price loading…'; }
            }
            row.classList.add('oos');
            if (input) {
                var prev = parseInt(input.value) || 0;
                if (prev > 0) clamped.push(sz + ' cleared (live price not loaded)');
                input.value = 0; input.disabled = true; state.sizes[sz] = 0;
            }
            if (minusBtn) minusBtn.disabled = true;
            if (plusBtn) plusBtn.disabled = true;
        });

        // Notify user of changes
        var msgs = [];
        if (restored.length) msgs.push(restored.join('; '));
        if (clamped.length) msgs.push(clamped.join('; '));
        if (msgs.length) {
            showBuilderToast('Stock updated: ' + msgs.join(' · '));
            updateTotals();
        }
    }

    // Event delegation for dynamic size rows
    document.getElementById('sizeMatrix').addEventListener('click', function (e) {
        var btn = e.target.closest('.qty-minus, .qty-plus');
        if (!btn) return;
        var input = btn.parentElement.querySelector('.qty-input');
        var val = parseInt(input.value) || 0;
        if (btn.classList.contains('qty-minus') && val > 0) {
            input.value = val - 1;
            state.sizes[input.dataset.size] = val - 1;
            state.desiredSizes[input.dataset.size] = val - 1;
        } else if (btn.classList.contains('qty-plus')) {
            var max = parseInt(input.max) || 999;
            if (val < max) {
                input.value = val + 1;
                state.sizes[input.dataset.size] = val + 1;
                state.desiredSizes[input.dataset.size] = val + 1;
            }
        }
        updateTotals();
    });

    document.getElementById('sizeMatrix').addEventListener('input', function (e) {
        if (!e.target.classList.contains('qty-input')) return;
        var val = parseInt(e.target.value) || 0;
        var max = parseInt(e.target.max) || 999;
        if (val < 0) val = 0;
        if (val > max) val = max;
        e.target.value = val;
        state.sizes[e.target.dataset.size] = val;
        state.desiredSizes[e.target.dataset.size] = val;
        updateTotals();
    });

    function getTotalUnits() {
        return Object.values(state.sizes).reduce((sum, v) => sum + v, 0);
    }

    let updateTotals = function () {
        const total = getTotalUnits();
        document.getElementById('totalUnits').textContent = total;

        // Minimum order hint
        const minHint = document.getElementById('minOrderHint');
        if (minHint) minHint.classList.toggle('met', total >= 1);

        // Volume hint — show next price break tier
        const hint = document.getElementById('volumeHint');
        const hasNonDtf = Object.values(state.locations).some(function (l) { return locHasArtwork(l) && l.decoration && l.decoration !== 'dtf'; });
        const breakpoints = hasNonDtf ? [12, 24, 48, 100, 250] : [12, 24, 48, 99];
        const topTier = breakpoints[breakpoints.length - 1];
        const nextBreak = breakpoints.find(b => total < b);
        if (!nextBreak) {
            hint.classList.add('unlocked');
            hint.innerHTML = '<i class="fas fa-check-circle"></i> <span><strong>Best pricing unlocked!</strong> You\'re at ' + topTier + '+ units.</span>';
        } else if (total >= 12) {
            hint.classList.add('unlocked');
            const remaining = nextBreak - total;
            hint.innerHTML = `<i class="fas fa-check-circle"></i> <span><strong>Volume discount active!</strong> Add <strong>${remaining} more</strong> to reach ${nextBreak}+ for even lower pricing.</span>`;
        } else {
            hint.classList.remove('unlocked');
            const remaining = 12 - total;
            hint.innerHTML = `<i class="fas fa-tags"></i> <span>Order <strong>${remaining} more unit${remaining !== 1 ? 's' : ''}</strong> to start unlocking volume discounts!</span>`;
        }

        updatePreview();
        updateNextBtn();
    }

    // ==========================================
    // Notes capture
    // ==========================================
    const notesEl = document.getElementById('orderNotes');
    if (notesEl) {
        notesEl.addEventListener('input', function () {
            state.notes = this.value;
            autoSave();
        });
    }

    // Garment images are now loaded on-demand from S&S CDN via getGarmentImageUrl()

    // ==========================================
    // Live Preview Updates
    // ==========================================
    const garmentImg = document.getElementById('garmentImage');
    const garmentPlaceholder = document.getElementById('garmentPlaceholder');
    const garmentContainer = document.getElementById('garmentContainer');
    garmentContainer.classList.add('hide-guides'); // Guides hidden until zone mapping is complete
    const logoOverlay = document.getElementById('logoOverlay');
    const logoPlaceholder = document.getElementById('logoPlaceholder');
    const logoImg = document.getElementById('logoImage');
    const logoControlsBar = document.getElementById('logoControls');

    // Print zone elements
    const printZoneEls = {
        'front-center': document.getElementById('zoneFrontCenter'),
        'left-chest':   document.getElementById('zoneLeftChest'),
        'back':         document.getElementById('zoneBack'),
        'sleeve':       document.getElementById('zoneSleeve'),
        'right-sleeve': document.getElementById('zoneRightSleeve'),
    };

    let updatePreview = function () {
        const loc = activeLoc();

        // Swap garment photo (respects viewSide) — images from S&S CDN
        if (state.garment && state.color) {
            const src = getGarmentImageUrl(state.garment, state.color, state.viewSide);
            const key = state.garment.id + '_' + state.color + '_' + (state.viewSide || 'front');
            if (src && garmentImg.dataset.currentKey !== key) {
                garmentImg.classList.add('loading');
                garmentImg.onload = () => { garmentImg.classList.remove('loading'); };
                garmentImg.onerror = () => { garmentImg.classList.remove('loading'); };
                if (src.indexOf('ssactivewear.com') !== -1) {
                    garmentImg.crossOrigin = 'anonymous';
                } else {
                    garmentImg.removeAttribute('crossorigin');
                }
                garmentImg.src = src;
                garmentImg.dataset.currentKey = key;
            }
            garmentContainer.style.display = '';
            garmentPlaceholder.style.display = 'none';
            logoOverlay.style.display = '';
        } else if (state.garment) {
            const firstHex = state.garment.colors[0].hex;
            const src = getGarmentImageUrl(state.garment, firstHex, state.viewSide);
            const key = state.garment.id + '_' + firstHex + '_' + (state.viewSide || 'front');
            if (src && garmentImg.dataset.currentKey !== key) {
                garmentImg.classList.add('loading');
                garmentImg.onload = () => { garmentImg.classList.remove('loading'); };
                garmentImg.onerror = () => { garmentImg.classList.remove('loading'); };
                if (src.indexOf('ssactivewear.com') !== -1) {
                    garmentImg.crossOrigin = 'anonymous';
                } else {
                    garmentImg.removeAttribute('crossorigin');
                }
                garmentImg.src = src;
                garmentImg.dataset.currentKey = key;
            }
            garmentContainer.style.display = '';
            garmentPlaceholder.style.display = 'none';
            logoOverlay.style.display = '';
        } else {
            garmentContainer.style.display = 'none';
            garmentPlaceholder.style.display = '';
            logoOverlay.style.display = 'none';
        }

        // Flip garment image horizontally for right-sleeve view OR mirrored beanie back
        var flipForSleeve = shouldFlipSleeve(state.garment, state.viewSide);
        var flipForBeanieBack = state.viewSide === 'back' && isBackMirrored(state.garment, state.color);
        garmentImg.style.transform = (flipForSleeve || flipForBeanieBack) ? 'scaleX(-1)' : '';

        // Show mirrored-view notice on right-sleeve or beanie back (image is flipped)
        var mirrorNotice = document.getElementById('mirrorNotice');
        if (mirrorNotice) {
            var showMirror = flipForSleeve || flipForBeanieBack;
            mirrorNotice.classList.toggle('visible', showMirror);
            if (showMirror) {
                var span = document.getElementById('mirrorSideName');
                if (span) {
                    if (flipForBeanieBack) {
                        span.textContent = 'back';
                    } else {
                        var sideLabel = (locationDisplayLabels['right-sleeve'] || 'sleeve').toLowerCase().replace('right ', '');
                        span.textContent = sideLabel;
                    }
                }
            }
        }

        // Position the logo overlay & update print zones
        updatePrintZones();

        // Apply decoration method visual effect to logo
        const activeMethod = loc.decoration || state.decoration || '';
        logoOverlay.dataset.method = activeMethod;

        // Blend logo into fabric texture based on garment color
        updateLogoBlendMode();

        // Swap logo src: use canvas-rendered version when available
        if (loc.logoDataUrl) {
            var rendered = loc.renderedLogos[activeMethod];
            if (rendered) {
                setLogoPreviewImage(rendered, state.activeLocation + '_' + activeMethod);
            } else {
                setLogoPreviewImage(loc.logoDataUrl, state.activeLocation + '_' + activeMethod);
            }
        }

        // Update preview meta
        if (state.garment) {
            setMetaValue('previewMetaGarment', state.garment.name);
        }
        if (state.color) {
            setMetaValue('previewMetaColor', getColorName(state.color));
        }
        if (state.decoration) {
            const activeMethod = loc.decoration || state.decoration;
            setMetaValue('previewMetaMethod', methodLabels[activeMethod]);
        }

        const total = getTotalUnits();
        setMetaValue('previewMetaQty', total.toString());

        // Update pricing
        updatePricing();
    }

    // ==========================================
    // Print Zone Positions (% of garment image)
    // ==========================================
    // These are calibrated for the flat-lay t-shirt mockup images (600×720px).
    // The shirt body sits roughly within a bounding box of:
    //   x: 13%–87%, y: 8%–95%  (of the image)
    // Chest neckline is at ~18% from top.
    //
    // Positions: { top, left, width, height } — all % of the garment container
    const printZonePositions = {
        'tshirts': {
            front: {
                'front-center': { top: '30%', left: '28%', width: '44%', height: '30%' },
                'left-chest':   { top: '24%', left: '24%', width: '22%', height: '18%' },
                'back':         { top: '22%', left: '22%', width: '56%', height: '45%' },
            },
            back: {
                'back': { top: '22%', left: '22%', width: '56%', height: '45%' },
            },
            'left-sleeve': {},
            'right-sleeve': {},
        },
    };

    // Logo overlay positions (same structure, but can be offset slightly from zone)
    const logoPositions = {
        'tshirts': {
            front: {
                'front-center': { top: '32%', left: '30%', width: '40%', height: '26%' },
                'left-chest':   { top: '26%', left: '26%', width: '18%', height: '14%' },
                'back':         { top: '26%', left: '25%', width: '50%', height: '38%' },
            },
            back: {
                'back':         { top: '26%', left: '25%', width: '50%', height: '38%' },
            },
            'left-sleeve': {
                'sleeve':       { top: '32%', left: '25%', width: '45%', height: '28%' },
            },
            'right-sleeve': {
                'right-sleeve': { top: '32%', left: '30%', width: '45%', height: '28%' },
            },
        },
    };

    function updateLogoPosition() {
        const cat = state.category || 'tshirts';
        const side = state.viewSide || 'front';
        const zoneKey = locationZoneKey[state.activeLocation] || 'front-center';
        const catPositions = logoPositions[cat] || logoPositions['tshirts'];
        const sidePositions = catPositions[side] || catPositions['front'];
        // On back view only show logo if location is 'back', otherwise hide
        const pos = sidePositions[zoneKey];
        if (!pos) {
            logoOverlay.style.display = 'none';
            return;
        }
        logoOverlay.style.display = '';
        logoOverlay.style.width = pos.width;
        // Only use the zone default height when aspect ratio isn't known yet (first load).
        // Once loc.aspectRatio is stored, keep height:auto so the image proportions control sizing
        // and iOS resize events can't snap the logo to the wrong fixed height.
        const loc = activeLoc();
        logoOverlay.style.height = (loc.logoDataUrl && loc.aspectRatio) ? 'auto' : pos.height;

        // Restore saved drag position, or use default from zone map
        if (loc.posLeft != null && loc.posTop != null) {
            logoOverlay.style.left = loc.posLeft;
            logoOverlay.style.top = loc.posTop;
        } else {
            logoOverlay.style.left = pos.left;
            logoOverlay.style.top = pos.top;
        }
    }

    // Save current drag position back to active location state
    function saveLogoPosition() {
        const loc = activeLoc();
        const containerW = garmentContainer.offsetWidth;
        const containerH = garmentContainer.offsetHeight;
        // Store as percentages so position scales with container resize
        loc.posLeft = (logoOverlay.offsetLeft / containerW * 100) + '%';
        loc.posTop = (logoOverlay.offsetTop / containerH * 100) + '%';
        // Write % back to CSS immediately — drag sets px which won't scale on resize
        logoOverlay.style.left = loc.posLeft;
        logoOverlay.style.top = loc.posTop;
        autoSave();
    }

    // ==========================================
    // Print Zones — blue highlight boxes
    // ==========================================
    function updatePrintZones() {
        const cat = state.category || 'tshirts';
        const side = state.viewSide || 'front';
        const zoneKey = locationZoneKey[state.activeLocation] || 'front-center';
        const catZones = printZonePositions[cat] || printZonePositions['tshirts'];
        const zones = catZones[side] || catZones['front'];

        const hasAnyZone = Object.keys(zones).length > 0;

        Object.entries(printZoneEls).forEach(([key, el]) => {
            const pos = zones[key];
            if (!pos) {
                el.style.display = 'none';
                return;
            }
            // Position the zone overlay
            el.style.display = '';
            el.style.top = pos.top;
            el.style.left = pos.left;
            el.style.width = pos.width;
            el.style.height = pos.height;

            // Active = currently selected location
            el.classList.toggle('active', key === zoneKey);
            // Remove focused (hover) state for non-hovered
            if (key !== zoneKey) {
                el.classList.remove('focused');
            }
        });

        // Hide / show Guide buttons when current view has no print zones
        const desktopGuideBtn = document.getElementById('toggleGuidelines');
        const mobileGuideBtn = document.querySelector('.mlc-btn[data-action="guidelines"]');
        if (desktopGuideBtn) {
            desktopGuideBtn.style.display = hasAnyZone ? '' : 'none';
            // Also hide the divider right before the Guide button
            const prevDiv = desktopGuideBtn.previousElementSibling;
            if (prevDiv && prevDiv.classList.contains('controls-divider')) prevDiv.style.display = hasAnyZone ? '' : 'none';
        }
        if (mobileGuideBtn) {
            mobileGuideBtn.style.display = hasAnyZone ? '' : 'none';
            const prevDiv = mobileGuideBtn.previousElementSibling;
            if (prevDiv && prevDiv.classList.contains('mlc-divider')) prevDiv.style.display = hasAnyZone ? '' : 'none';
        }
    }

    function updateLogoOnPreview() {
        const loc = activeLoc();
        if (loc.logoDataUrl) {
            setLogoPreviewImage(loc.logoDataUrl, state.activeLocation + '_' + (loc.decoration || state.decoration || 'logo'));
            logoImg.style.display = '';
            logoPlaceholder.style.display = 'none';
            if (!previewPanel.classList.contains('preview-locked')) logoOverlay.classList.add('draggable');
            logoOverlay.style.setProperty('--logo-url', 'url(' + loc.logoDataUrl + ')');
            document.getElementById('dragHint').style.display = '';

            // Fit overlay aspect ratio to image dimensions
            const probe = new Image();
            probe.onload = function () {
                const ratio = probe.naturalWidth / probe.naturalHeight;
                logoOverlay.style.aspectRatio = ratio;
                // Keep width from zone, let height auto-derive
                logoOverlay.style.height = 'auto';
                // Ensure corner handles are sized for current scale
                applyLogoTransform();
            };
            probe.src = loc.logoDataUrl;

            // Render all decoration method effects in background
            var methods = ['embroidery', 'leather-patch', 'dtf'];
            methods.forEach(function (m) {
                renderDecorationEffect(loc.logoDataUrl, m, function (rendUrl, maskUrl) {
                    loc.renderedLogos[m] = rendUrl;
                    if (m === 'embroidery') {
                        loc.logoMaskUrl = maskUrl;
                        logoOverlay.style.setProperty('--logo-mask-url', 'url(' + maskUrl + ')');
                    }
                    if ((loc.decoration || state.decoration) === m) {
                        setLogoPreviewImage(rendUrl, state.activeLocation + '_' + m);
                    }
                });
            });
        }
    }

    function removeLogoFromPreview() {
        logoImg.src = '';
        logoImg.dataset.currentKey = '';
        delete logoImg.dataset.pendingKey;
        logoImg.style.display = 'none';
        logoImg.style.mixBlendMode = '';
        logoImg.style.opacity = '';
        logoImg.classList.remove('loading');
        logoPlaceholder.style.display = '';
        logoOverlay.classList.remove('draggable');
        logoOverlay.classList.remove('active');
        syncControlsVisibility();
        logoOverlay.style.removeProperty('--logo-url');
        logoOverlay.style.removeProperty('--logo-mask-url');
        logoOverlay.style.removeProperty('--logo-transform');
        logoOverlay.style.aspectRatio = '';
        logoOverlay.style.height = '';
        document.getElementById('dragHint').style.display = 'none';
        // Reset transforms + position back to zone defaults
        state.logoScale = 1;
        state.logoRotation = 0;
        logoOverlay.style.transform = '';
        logoOverlay.style.removeProperty('left');
        logoOverlay.style.removeProperty('top');
        logoOverlay.style.removeProperty('width');
        updateLogoPosition();
    }

    // Per-unit price (backward-compat wrapper)
    function calcUnitPrice(total) {
        var p = calcOrderPricing();
        return p.avgUnit;
    }

    function updatePricing() {
        const p = calcOrderPricing();
        const perUnitEl = document.getElementById('pricePerUnit');
        const totalEl = document.getElementById('priceTotal');
        const methodNoteEl = document.getElementById('priceMethodNote');
        const activeLoc = state.locations && state.activeLocation ? state.locations[state.activeLocation] : null;
        const activeMethod = (activeLoc && activeLoc.decoration) || state.decoration || '';

        if (p.totalQty > 0) {
            perUnitEl.textContent = '$' + p.unitPrice.toFixed(2);
        } else {
            perUnitEl.textContent = '$0.00';
        }

        var displayTotal = p.total;
        totalEl.textContent = '$' + displayTotal.toFixed(2);

        if (methodNoteEl) {
            methodNoteEl.hidden = activeMethod !== 'embroidery';
        }
    }

    function setMetaValue(id, text) {
        const el = document.getElementById(id);
        if (el) {
            el.querySelector('.meta-value').textContent = text;
        }
    }

    // ==========================================
    // Logo Dragging — Clamped to Container
    // ==========================================
    (function initLogoDrag() {
        let dragging = false;
        let startX, startY, origLeft, origTop;

        // Multi-touch gesture state
        let gesture = null;
        const MIN_GESTURE_DIST = 30;   // px — ignore pinch start if fingers are closer than this
        const MIN_SCALE = 0.2;
        const MAX_SCALE = 3;
        const MAX_ROTATION = 15; // degrees — only slight adjustments allowed

        // Flag to prevent deselect right after a control button tap
        let protectActive = false;
        function protectActiveState() {
            protectActive = true;
            setTimeout(function () { protectActive = false; }, 300);
        }
        // Expose so reset/button handlers can call it
        window._protectLogoActive = protectActiveState;

        function clampPosition(newLeft, newTop) {
            const containerW = garmentContainer.offsetWidth;
            const containerH = garmentContainer.offsetHeight;
            const logoW = logoOverlay.offsetWidth;
            const logoH = logoOverlay.offsetHeight;

            const minX = 0;
            const minY = 0;
            const maxX = containerW - logoW;
            const maxY = containerH - logoH;

            return {
                left: Math.max(minX, Math.min(maxX, newLeft)),
                top:  Math.max(minY, Math.min(maxY, newTop)),
            };
        }

        function getTouchDistance(t0, t1) {
            const dx = t1.clientX - t0.clientX;
            const dy = t1.clientY - t0.clientY;
            return Math.sqrt(dx * dx + dy * dy) || 1;
        }

        function getTouchAngle(t0, t1) {
            return Math.atan2(t1.clientY - t0.clientY, t1.clientX - t0.clientX) * (180 / Math.PI);
        }

        function cancelAllGestures() {
            dragging = false;
            gesture = null;
            logoOverlay.classList.remove('dragging');
            garmentContainer.classList.remove('show-guides');
        }

        function initGestureFromTouches(touches) {
            const dist = getTouchDistance(touches[0], touches[1]);
            if (dist < MIN_GESTURE_DIST) return false;
            dragging = false;
            logoOverlay.classList.remove('dragging');
            gesture = {
                startDist: dist,
                startAngle: getTouchAngle(touches[0], touches[1]),
                startScale: state.logoScale,
                startRotation: state.logoRotation || 0,
                rotationUnlocked: false,
            };
            logoOverlay.classList.add('active');
            syncControlsVisibility();
            garmentContainer.classList.add('show-guides');
            return true;
        }

        function isMobile() { return !window.matchMedia('(pointer: fine)').matches; }

        // Can we manipulate the logo from this touch?
        function canManipulateLogo() {
            if (!logoOverlay.classList.contains('draggable')) return false;
            // On desktop, only direct logo touches work
            if (!isMobile()) return false;
            // On mobile, logo must be active (selected)
            return logoOverlay.classList.contains('active');
        }

        function startDrag(e) {
            e.preventDefault();
            dragging = true;
            gesture = null;
            logoOverlay.classList.add('dragging');
            garmentContainer.classList.add('show-guides');
            startX = e.touches[0].clientX;
            startY = e.touches[0].clientY;
            origLeft = logoOverlay.offsetLeft;
            origTop = logoOverlay.offsetTop;
        }

        // --- Mouse handlers (desktop) ---
        logoOverlay.addEventListener('mousedown', function (e) {
            if (!logoOverlay.classList.contains('draggable')) return;
            e.preventDefault();
            dragging = true;
            logoOverlay.classList.add('dragging');
            garmentContainer.classList.add('show-guides');
            startX = e.clientX;
            startY = e.clientY;
            origLeft = logoOverlay.offsetLeft;
            origTop = logoOverlay.offsetTop;
        });

        document.addEventListener('mousemove', function (e) {
            if (!dragging) return;
            const dx = e.clientX - startX;
            const dy = e.clientY - startY;
            const clamped = clampPosition(origLeft + dx, origTop + dy);
            logoOverlay.style.left = clamped.left + 'px';
            logoOverlay.style.top = clamped.top + 'px';
        });

        document.addEventListener('mouseup', function () {
            if (dragging) { dragging = false; logoOverlay.classList.remove('dragging'); garmentContainer.classList.remove('show-guides'); saveLogoPosition(); }
        });

        // --- Touch: direct logo touches (always work if draggable) ---
        let touchedLogo = false;       // did the current touch start ON the logo?
        let touchStartedOnCanvas = false; // did the touch start on the garment container?
        let didManipulate = false;      // did fingers actually move (drag/pinch)?

        logoOverlay.addEventListener('touchstart', function (e) {
            if (!logoOverlay.classList.contains('draggable')) return;
            touchedLogo = true;
            touchStartedOnCanvas = true; // logo is inside canvas
            didManipulate = false;
            // Auto-select on touch
            logoOverlay.classList.add('active');
            syncControlsVisibility();

            if (e.touches.length === 1) {
                startDrag(e);
            } else if (e.touches.length === 2) {
                e.preventDefault();
                initGestureFromTouches(e.touches);
            } else {
                e.preventDefault();
                cancelAllGestures();
            }
        }, { passive: false });

        // --- Touch: canvas-wide touches when logo is selected (mobile) ---
        // Track start position for deferred drag (only begin drag if finger moves)
        var canvasTouchPending = false;
        var canvasTouchStartE = null;

        garmentContainer.addEventListener('touchstart', function (e) {
            if (e.target.closest('#logoOverlay')) return; // handled above
            touchedLogo = false;
            touchStartedOnCanvas = true;
            didManipulate = false;

            if (canManipulateLogo()) {
                // Don't start drag immediately — wait for movement so a tap can deselect
                canvasTouchPending = true;
                canvasTouchStartE = e;
                if (e.touches.length === 2) {
                    e.preventDefault();
                    initGestureFromTouches(e.touches);
                    canvasTouchPending = false;
                }
            }
        }, { passive: false });

        // Tap anywhere in the preview panel (outside logo) to deselect
        var previewPanel = document.getElementById('previewPanel');
        if (previewPanel) {
            previewPanel.addEventListener('touchstart', function (e) {
                if (e.target.closest('#logoOverlay')) return;
                if (e.target.closest('#garmentContainer')) return; // handled above
                if (e.target.closest('#mobileLogoControls')) return;
                touchedLogo = false;
                touchStartedOnCanvas = true;
                didManipulate = false;
            }, { passive: true });
        }

        // Global touchmove for drag + gesture
        document.addEventListener('touchmove', function (e) {
            if (e.touches.length >= 3) {
                cancelAllGestures();
                canvasTouchPending = false;
                return;
            }

            // Deferred canvas drag: start drag once finger actually moves
            if (canvasTouchPending && e.touches.length === 1 && !dragging) {
                startDrag(canvasTouchStartE);
                canvasTouchPending = false;
            }

            if (e.touches.length === 2 && (dragging || gesture)) {
                e.preventDefault();
                didManipulate = true;
                if (dragging && !gesture) {
                    if (!initGestureFromTouches(e.touches)) return;
                }
                if (gesture) {
                    const newDist = getTouchDistance(e.touches[0], e.touches[1]);
                    const scaleFactor = newDist / gesture.startDist;
                    state.logoScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, gesture.startScale * scaleFactor));

                    var newAngle = getTouchAngle(e.touches[0], e.touches[1]);
                    var angleDelta = newAngle - gesture.startAngle;
                    if (angleDelta > 180) angleDelta -= 360;
                    if (angleDelta < -180) angleDelta += 360;
                    if (!gesture.rotationUnlocked && Math.abs(angleDelta) > 3) gesture.rotationUnlocked = true;
                    if (gesture.rotationUnlocked) {
                        state.logoRotation = gesture.startRotation + angleDelta;
                    }

                    applyLogoTransform();
                }
            } else if (dragging && e.touches.length === 1) {
                e.preventDefault();
                didManipulate = true;
                const dx = e.touches[0].clientX - startX;
                const dy = e.touches[0].clientY - startY;
                const clamped = clampPosition(origLeft + dx, origTop + dy);
                logoOverlay.style.left = clamped.left + 'px';
                logoOverlay.style.top = clamped.top + 'px';
            }
        }, { passive: false });

        document.addEventListener('touchend', function (e) {
            if (e.touches.length === 0) {
                // All fingers lifted
                canvasTouchPending = false;
                canvasTouchStartE = null;
                if (dragging) saveLogoPosition();
                dragging = false;
                gesture = null;
                logoOverlay.classList.remove('dragging');
                garmentContainer.classList.remove('show-guides');

                if (!protectActive && isMobile() && touchStartedOnCanvas) {
                    if (didManipulate) {
                        // Finished a drag/pinch/rotate → auto-deselect so page can scroll
                        logoOverlay.classList.remove('active');
                        syncControlsVisibility();
                    } else if (!touchedLogo) {
                        // Simple tap on canvas outside logo → deselect
                        logoOverlay.classList.remove('active');
                        syncControlsVisibility();
                    }
                    // Simple tap ON the logo with no movement → stays selected
                }

                touchedLogo = false;
                touchStartedOnCanvas = false;
                didManipulate = false;
            } else if (e.touches.length === 1 && gesture) {
                // Went from 2 fingers → 1: end gesture, begin drag from current finger
                gesture = null;
                dragging = true;
                logoOverlay.classList.add('dragging');
                garmentContainer.classList.add('show-guides');
                startX = e.touches[0].clientX;
                startY = e.touches[0].clientY;
                origLeft = logoOverlay.offsetLeft;
                origTop = logoOverlay.offsetTop;
            }
        });

        // Prevent default gestures on the garment container (prevents page zoom on the preview)
        garmentContainer.addEventListener('touchmove', function (e) {
            if (e.touches.length >= 2) e.preventDefault();
        }, { passive: false });
    })();

    // ==========================================
    // Logo Active State (click to select) + Scale/Rotate
    // ==========================================
    state.logoScale = 1;
    state.logoRotation = 0;

    function applyLogoTransform() {
        const t = `scale(${state.logoScale}) rotate(${state.logoRotation}deg)`;
        logoOverlay.style.transform = t;
        logoOverlay.style.setProperty('--logo-transform', t);
        // Counter-rotation for child UI elements (resize handles, rotate handle)
        logoOverlay.style.setProperty('--counter-rotate', `rotate(${-state.logoRotation}deg)`);
        // Keep outline visually consistent regardless of scale
        logoOverlay.style.outlineWidth = Math.max(1, Math.round(2 / state.logoScale)) + 'px';
        // Hide rotation handle when logo is too small (would overlap)
        logoOverlay.classList.toggle('scale-small', state.logoScale < 0.5);
        // Sync back to active location
        const loc = activeLoc();
        loc.scale = state.logoScale;
        loc.rotation = state.logoRotation;
        // Update corner handle positions/sizes to compensate for scale
        updateCornerPositions();
        // Update Figma-style corner rotation cursors
        updateRotationCursors();
        // Update print size auto-detection when scale changes
        updatePrintSizeSelector();
    }

    // Keep corner handles at a consistent visual size & offset regardless of parent scale.
    // Because the overlay is scaled via CSS transform, child positions/sizes are in the
    // *unscaled* coordinate space but rendered multiplied by the parent scale.
    // To get a fixed visual size, divide desired px values by the current scale.
    function updateCornerPositions() {
        var s = state.logoScale || 1;

        // --- Resize handles: 10px visual size, clamped so they stay usable at extreme scales ---
        var rSize = Math.max(4, Math.min(14, Math.round(10 / s)));
        var rHalf = Math.round(rSize / 2);
        var rBorder = Math.max(1, Math.min(3, Math.round(2 / s)));
        logoOverlay.querySelectorAll('.logo-resize-handle').forEach(function (h) {
            h.style.width = rSize + 'px';
            h.style.height = rSize + 'px';
            h.style.borderWidth = rBorder + 'px';
            var c = h.dataset.corner;
            // Reset all sides
            h.style.top = h.style.bottom = h.style.left = h.style.right = '';
            if (c === 'nw') { h.style.top = -rHalf + 'px'; h.style.left = -rHalf + 'px'; }
            if (c === 'ne') { h.style.top = -rHalf + 'px'; h.style.right = -rHalf + 'px'; }
            if (c === 'sw') { h.style.bottom = -rHalf + 'px'; h.style.left = -rHalf + 'px'; }
            if (c === 'se') { h.style.bottom = -rHalf + 'px'; h.style.right = -rHalf + 'px'; }
        });

        // --- Rotation zones: 20px visual size, clamped for consistency ---
        var zSize = Math.max(8, Math.min(28, Math.round(20 / s)));
        // Zone starts right at the resize handle edge (rHalf) with no gap
        var zEdge = rHalf + zSize;
        rotateZones.forEach(function (z) {
            z.style.width = zSize + 'px';
            z.style.height = zSize + 'px';
            var c = z.dataset.corner;
            z.style.top = z.style.bottom = z.style.left = z.style.right = '';
            if (c === 'nw') { z.style.top = -zEdge + 'px'; z.style.left = -zEdge + 'px'; }
            if (c === 'ne') { z.style.top = -zEdge + 'px'; z.style.right = -zEdge + 'px'; }
            if (c === 'sw') { z.style.bottom = -zEdge + 'px'; z.style.left = -zEdge + 'px'; }
            if (c === 'se') { z.style.bottom = -zEdge + 'px'; z.style.right = -zEdge + 'px'; }
        });

        // --- Mobile rotation handle: bottom offset ---
        var rotHandle = document.getElementById('logoRotateHandle');
        if (rotHandle) {
            var handleOff = Math.round(36 / s);
            rotHandle.style.bottom = -handleOff + 'px';
            var handleSize = Math.round(28 / s);
            rotHandle.style.width = handleSize + 'px';
            rotHandle.style.height = handleSize + 'px';
            rotHandle.style.fontSize = (0.6 / s) + 'rem';
        }
    }

    // ── Figma-style rotation cursors ──
    // Generates a small curved-arrow SVG rotated to match each corner + element rotation
    function buildRotateCursorSvg(angleDeg) {
        // A small curved rotation arrow, 24×24, rotated to the correct orientation
        return `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'%3E%3Cg transform='rotate(${angleDeg} 12 12)'%3E%3Cpath d='M14.5 3.5L12 1v2C7.58 3 4 6.58 4 11h2c0-3.31 2.69-6 6-6v2l2.5-2.5z' fill='%23222' stroke='%23fff' stroke-width='0.8'/%3E%3C/g%3E%3C/svg%3E") 12 12, pointer`;
    }

    // Base angles for each corner (pointing away from center, Figma style)
    var cornerBaseAngles = { nw: 0, ne: 90, se: 180, sw: 270 };
    var rotateZones = logoOverlay.querySelectorAll('.logo-rotate-zone');

    function updateRotationCursors() {
        var rot = state.logoRotation || 0;
        rotateZones.forEach(function (zone) {
            var corner = zone.dataset.corner;
            var base = cornerBaseAngles[corner] || 0;
            zone.style.cursor = buildRotateCursorSvg(base + rot);
        });
    }
    updateRotationCursors(); // initial set

    // Helper: show controls whenever a logo is uploaded (draggable), hide when removed
    function syncControlsVisibility() {
        var mobileControlsEl = document.getElementById('mobileLogoControls');
        var hasDraggableLogo = logoOverlay.classList.contains('draggable');

        if (logoOverlay.classList.contains('draggable')) {
            logoControlsBar.classList.add('visible');
        } else {
            logoControlsBar.classList.remove('visible');
        }

        if (mobileControlsEl) {
            mobileControlsEl.classList.toggle('visible', hasDraggableLogo);
        }
    }

    // Toggle active (selected) state on click/tap
    logoOverlay.addEventListener('click', function (e) {
        if (!logoOverlay.classList.contains('draggable')) return;
        if (logoOverlay.classList.contains('dragging')) return;
        e.stopPropagation();
        logoOverlay.classList.add('active');
        syncControlsVisibility();
    });

    // Dismiss active state when clicking outside overlay AND controls
    document.addEventListener('click', function (e) {
        if (e.target.closest('#logoOverlay') || e.target.closest('#logoControls') || e.target.closest('#mobileLogoControls')) return;
        // On mobile, garmentContainer tap-out is handled by touchend instead
        if (isMobile() && e.target.closest('#garmentContainer')) return;
        logoOverlay.classList.remove('active');
        syncControlsVisibility();
    });

    // Scale controls
    document.getElementById('logoScaleUp').addEventListener('click', function (e) {
        e.stopPropagation();
        state.logoScale = Math.min(3, state.logoScale + 0.1);
        applyLogoTransform();
    });

    document.getElementById('logoScaleDown').addEventListener('click', function (e) {
        e.stopPropagation();
        state.logoScale = Math.max(0.2, state.logoScale - 0.1);
        applyLogoTransform();
    });

    // Rotate controls (clamped to ±15°)
    document.getElementById('logoRotateCW').addEventListener('click', function (e) {
        e.stopPropagation();
        state.logoRotation += 15;
        applyLogoTransform();
    });

    document.getElementById('logoRotateCCW').addEventListener('click', function (e) {
        e.stopPropagation();
        state.logoRotation -= 15;
        applyLogoTransform();
    });

    // Reset
    document.getElementById('logoReset').addEventListener('click', function (e) {
        e.stopPropagation();
        state.logoScale = 1;
        state.logoRotation = 0;
        var loc = activeLoc();
        loc.posLeft = null;
        loc.posTop = null;
        applyLogoTransform();
        updateLogoPosition();
        // Re-apply aspect ratio from current logo (clearing it collapses the overlay)
        if (loc.logoDataUrl) {
            var probe = new Image();
            probe.onload = function () {
                logoOverlay.style.aspectRatio = probe.naturalWidth / probe.naturalHeight;
                logoOverlay.style.height = 'auto';
            };
            probe.src = loc.logoDataUrl;
        }
        // Protect active state from competing event handlers
        if (window._protectLogoActive) window._protectLogoActive();
        logoOverlay.classList.add('active');
        syncControlsVisibility();
    });

    // ── Rotation Handle (drag circle to rotate) + Figma-style corner rotation zones ──
    (function () {
        const handle = document.getElementById('logoRotateHandle');
        const cornerZones = logoOverlay.querySelectorAll('.logo-rotate-zone');
        let rotating = false;
        let centerX, centerY, prevAngle;

        function getAngle(cx, cy, px, py) {
            return Math.atan2(py - cy, px - cx) * (180 / Math.PI);
        }

        function getCenterOfOverlay() {
            // Use untransformed position to avoid skewed center from scale/rotate
            const parent = logoOverlay.offsetParent || logoOverlay.parentElement;
            const parentRect = parent.getBoundingClientRect();
            const cx = parentRect.left + logoOverlay.offsetLeft + logoOverlay.offsetWidth / 2;
            const cy = parentRect.top + logoOverlay.offsetTop + logoOverlay.offsetHeight / 2;
            return { x: cx, y: cy };
        }

        let activeCorner = null; // which corner zone initiated rotation
        let rotateCommitted = false; // has movement exceeded dead-zone threshold?
        var ROTATE_DEAD_ZONE = 0; // degrees of movement before rotation kicks in
        var rotateAccum = 0; // accumulated delta while in dead zone

        function beginRotate(clientX, clientY, corner) {
            rotating = true;
            rotateCommitted = false;
            rotateAccum = 0;
            activeCorner = corner || null;
            const c = getCenterOfOverlay();
            centerX = c.x;
            centerY = c.y;
            prevAngle = getAngle(centerX, centerY, clientX, clientY);
            // Lock cursor on body so it persists while dragging outside the zone
            if (activeCorner) {
                var base = cornerBaseAngles[activeCorner] || 0;
                document.body.style.cursor = buildRotateCursorSvg(base + (state.logoRotation || 0));
            } else {
                document.body.style.cursor = 'grabbing';
            }
        }

        function doRotate(clientX, clientY) {
            if (!rotating) return;
            const current = getAngle(centerX, centerY, clientX, clientY);
            // Compute the smallest angular delta (handles the ±180° wrap correctly)
            let delta = current - prevAngle;
            if (delta > 180) delta -= 360;
            if (delta < -180) delta += 360;
            prevAngle = current;

            // Dead-zone: ignore tiny movements so accidental jitter doesn't rotate
            if (!rotateCommitted) {
                rotateAccum += delta;
                if (Math.abs(rotateAccum) < ROTATE_DEAD_ZONE) return;
                // Commit with the full accumulated delta
                delta = rotateAccum;
                rotateCommitted = true;
            }

            state.logoRotation = Math.round(state.logoRotation + delta);
            applyLogoTransform();
            // Update body cursor as rotation changes
            if (activeCorner) {
                var base = cornerBaseAngles[activeCorner] || 0;
                document.body.style.cursor = buildRotateCursorSvg(base + state.logoRotation);
            }
        }

        function endRotate() {
            if (!rotating) return;
            rotating = false;
            activeCorner = null;
            document.body.style.cursor = '';
        }

        // Mouse
        handle.addEventListener('mousedown', function (e) {
            e.preventDefault();
            e.stopPropagation();
            beginRotate(e.clientX, e.clientY);
        });
        document.addEventListener('mousemove', function (e) {
            if (rotating) doRotate(e.clientX, e.clientY);
        });
        document.addEventListener('mouseup', endRotate);

        // Corner rotation zones (Figma-style — pointer devices only)
        cornerZones.forEach(function (zone) {
            zone.addEventListener('mousedown', function (e) {
                e.preventDefault();
                e.stopPropagation();
                beginRotate(e.clientX, e.clientY, zone.dataset.corner);
            });
        });

        // Touch
        handle.addEventListener('touchstart', function (e) {
            if (e.touches.length !== 1) return;
            e.preventDefault();
            e.stopPropagation();
            beginRotate(e.touches[0].clientX, e.touches[0].clientY);
        }, { passive: false });
        document.addEventListener('touchmove', function (e) {
            if (rotating && e.touches.length === 1) {
                e.preventDefault();
                doRotate(e.touches[0].clientX, e.touches[0].clientY);
            }
        }, { passive: false });
        document.addEventListener('touchend', function () {
            endRotate();
        });
    })();

    // ── Corner Resize Handles (desktop drag-to-scale) ──
    (function () {
        const handles = logoOverlay.querySelectorAll('.logo-resize-handle');
        let resizing = false;
        let startDist, startScale;

        function getCenterOfOverlayForResize() {
            const parent = logoOverlay.offsetParent || logoOverlay.parentElement;
            const parentRect = parent.getBoundingClientRect();
            return {
                x: parentRect.left + logoOverlay.offsetLeft + logoOverlay.offsetWidth / 2,
                y: parentRect.top + logoOverlay.offsetTop + logoOverlay.offsetHeight / 2
            };
        }

        function beginResize(clientX, clientY) {
            resizing = true;
            const center = getCenterOfOverlayForResize();
            const dx = clientX - center.x;
            const dy = clientY - center.y;
            startDist = Math.sqrt(dx * dx + dy * dy) || 1;
            startScale = state.logoScale;
        }

        function doResize(clientX, clientY) {
            if (!resizing) return;
            const center = getCenterOfOverlayForResize();
            const dx = clientX - center.x;
            const dy = clientY - center.y;
            const dist = Math.sqrt(dx * dx + dy * dy) || 1;
            const factor = dist / startDist;
            state.logoScale = Math.max(0.3, Math.min(3, startScale * factor));
            applyLogoTransform();
        }

        function endResize() {
            resizing = false;
        }

        handles.forEach(function (h) {
            h.addEventListener('mousedown', function (e) {
                e.preventDefault();
                e.stopPropagation();
                beginResize(e.clientX, e.clientY);
            });
        });
        document.addEventListener('mousemove', function (e) {
            if (resizing) doResize(e.clientX, e.clientY);
        });
        document.addEventListener('mouseup', endResize);
    })();

    // Toggle guidelines (button removed — guides hidden by default)

    // ==========================================
    // Mobile Logo Controls (always-visible bar)
    // ==========================================
    const mobileControls = document.getElementById('mobileLogoControls');
    if (mobileControls) {
        mobileControls.addEventListener('click', function (e) {
            const btn = e.target.closest('.mlc-btn');
            if (!btn) return;
            e.stopPropagation();

            switch (btn.dataset.action) {
                case 'scale-up':
                    state.logoScale = Math.min(3, state.logoScale + 0.1);
                    applyLogoTransform();
                    break;
                case 'scale-down':
                    state.logoScale = Math.max(0.2, state.logoScale - 0.1);
                    applyLogoTransform();
                    break;
                case 'rotate-cw':
                    state.logoRotation = (state.logoRotation || 0) + 15;
                    applyLogoTransform();
                    break;
                case 'rotate-ccw':
                    state.logoRotation = (state.logoRotation || 0) - 15;
                    applyLogoTransform();
                    break;
                case 'reset':
                    state.logoScale = 1;
                    state.logoRotation = 0;
                    var rloc = activeLoc();
                    rloc.posLeft = null;
                    rloc.posTop = null;
                    applyLogoTransform();
                    updateLogoPosition();
                    // Re-apply aspect ratio from current logo
                    if (rloc.logoDataUrl) {
                        var rprobe = new Image();
                        rprobe.onload = function () {
                            logoOverlay.style.aspectRatio = rprobe.naturalWidth / rprobe.naturalHeight;
                            logoOverlay.style.height = 'auto';
                        };
                        rprobe.src = rloc.logoDataUrl;
                    }
                    // Protect active state from competing event handlers
                    if (window._protectLogoActive) window._protectLogoActive();
                    logoOverlay.classList.add('active');
                    syncControlsVisibility();
                    break;
                case 'guidelines':
                    // Guide button removed — guides hidden by default
                    break;
            }
        });
    }

    // ==========================================
    // Step 5: Populate Review
    // ==========================================
    function populateReview() {
        const catLabels = {
            tshirts: 'T-Shirts', longsleeves: 'Long Sleeves', hoodies: 'Hoodies',
            crewnecks: 'Crewnecks', polos: 'Polos', tanks: 'Tanks',
            hats: 'Hats & Caps', jackets: 'Jackets & Outerwear',
            sweatpants: 'Sweatpants & Joggers', workpants: 'Work Pants',
            beanies: 'Beanies', shorts: 'Shorts',
            vests: 'Vests', bags: 'Bags', quarterzips: 'Quarter Zips'
        };

        document.getElementById('reviewCategory').textContent = catLabels[state.category] || '—';
        document.getElementById('reviewGarment').textContent = state.garment ? state.garment.name : '—';
        document.getElementById('reviewColor').textContent = state.color ? getColorName(state.color) : '—';
        const activeLocationEntries = getActiveLocationEntries();

        // Decoration: list unique methods used across locations
        const usedMethods = [...new Set(activeLocationEntries
            .map(function (entry) { return entry[1]; })
            .filter(function (l) { return locHasArtwork(l) && l.decoration; })
            .map(function (l) { return methodLabels[l.decoration]; }))];
        document.getElementById('reviewDecoration').textContent = usedMethods.join(', ') || '—';

        // Print size (DTF only)
        var psRow = document.getElementById('reviewPrintSizeRow');
        var psVal = document.getElementById('reviewPrintSize');
        var hasDtfInReview = activeLocationEntries.some(function (e) { return e[1].decoration === 'dtf'; });
        if (psRow && psVal) {
            if (hasDtfInReview) {
                var tier = getDtfPrintSizeTier();
                psVal.textContent = tier === 'lg' ? 'Oversized (up to 16×16\")' : 'Standard (up to 12×12\")';
                psRow.style.display = '';
            } else {
                psRow.style.display = 'none';
            }
        }

        // Multi-location artwork thumbnails
        const artEntries = activeLocationEntries;
        const reviewLocEl = document.getElementById('reviewLocations');
        if (artEntries.length) {
            reviewLocEl.innerHTML = artEntries.map(([loc, data]) => {
                const meth = data.decoration ? (methodLabels[data.decoration] || data.decoration) : '';
                const src = data.logoDataUrl || '';
                return '<div class="review-artwork-item">'
                    + (src ? '<img class="review-artwork-thumb" src="' + src + '" alt="' + locationDisplayLabels[loc] + '">' : '')
                    + '<div class="review-artwork-meta">'
                    + '<span class="review-artwork-loc">' + locationDisplayLabels[loc] + '</span>'
                    + (meth ? '<span class="review-artwork-method">' + meth + '</span>' : '')
                    + '</div></div>';
            }).join('');
        } else {
            reviewLocEl.textContent = 'No artwork uploaded';
        }
        document.getElementById('reviewNotes').textContent = state.notes || 'None';

        // Size breakdown
        const sizeStr = Object.entries(state.sizes)
            .filter(([, v]) => v > 0)
            .map(([s, v]) => `${s}: ${v}`)
            .join(', ') || 'None';
        document.getElementById('reviewSizes').textContent = sizeStr;

        const total = getTotalUnits();
        document.getElementById('reviewTotal').textContent = total;

        // Pricing breakdown
        var p = calcOrderPricing();
        var bdEl = document.getElementById('reviewBreakdown');
        var bdLines = document.getElementById('breakdownLines');
        var bdFooter = document.getElementById('breakdownFooter');
        var activeLocations = activeLocationEntries.map(function (entry) { return entry[1]; }).filter(function (l) { return locHasArtwork(l) && l.decoration; });
        var activeLocationsByKey = activeLocationEntries.reduce(function (acc, entry) {
            var loc = entry[0], d = entry[1];
            if (locHasArtwork(d) && d.decoration) acc[loc] = d;
            return acc;
        }, {});
        var hasDtf = activeLocations.some(function (l) { return l.decoration === 'dtf'; });
        var hasNonDtf = activeLocations.some(function (l) { return l.decoration && l.decoration !== 'dtf'; });

        function bdLine(label, value, bold) {
            return '<div class="breakdown-line' + (bold ? ' subtotal' : '') + '"><span class="bl-label">' + label + '</span><span class="bl-value">' + value + '</span></div>';
        }

        if (state.garment && p.totalQty > 0 && activeLocations.length > 0) {
            bdEl.style.display = '';
            var lines = '';

            // Base garment
            lines += bdLine('Base garment', '$' + p.garmentPrice.toFixed(2) + ' / unit');

            // DTF decoration
            var dtfCombo = getDtfCombo();
            if (hasDtf && dtfCombo !== 'none') {
                var comboLabel = dtfCombo === 'both' ? 'Front + Back' : dtfCombo.charAt(0).toUpperCase() + dtfCombo.slice(1);
                var dtfFee = lookupTier(DTF_TIERS[p.printSizeTier][dtfCombo], p.totalQty);
                var sizeLabel = p.printSizeTier === 'lg' ? 'Oversized' : 'Standard';
                lines += bdLine(comboLabel + ' DTF <span class="bl-hint">(' + sizeLabel + ')</span>', '$' + dtfFee.toFixed(2) + ' / unit');
            }

            // Non-DTF front/back locations
            activeLocationEntries.forEach(function (e) {
                var loc = e[0], d = e[1];
                if ((loc === 'front' || loc === 'back') && locHasArtwork(d) && d.decoration && d.decoration !== 'dtf') {
                    var fee = getDecoFee(d.decoration, p.totalQty);
                    lines += bdLine(locationDisplayLabels[loc] + ' ' + (methodLabels[d.decoration] || d.decoration), '$' + fee.toFixed(2) + ' / unit');
                }
            });

            // Addon locations (sleeves — base fee + surcharge)
            activeLocationEntries.forEach(function (e) {
                var loc = e[0], d = e[1];
                if (loc !== 'front' && loc !== 'back' && locHasArtwork(d) && d.decoration) {
                    if (d.decoration !== 'dtf') {
                        var baseFee = getDecoFee(d.decoration, p.totalQty);
                        lines += bdLine(locationDisplayLabels[loc] + ' ' + (methodLabels[d.decoration] || d.decoration), '$' + baseFee.toFixed(2) + ' / unit');
                    } else if (dtfCombo === 'none') {
                        var baseDtfFee = lookupTier(DTF_TIERS[p.printSizeTier]['front'], p.totalQty);
                        var sizeLabel = p.printSizeTier === 'lg' ? 'Oversized' : 'Standard';
                        lines += bdLine(locationDisplayLabels[loc] + ' DTF <span class="bl-hint">(' + sizeLabel + ')</span>', '$' + baseDtfFee.toFixed(2) + ' / unit');
                    }
                    var fee = getAddonFee(loc, d.decoration);
                    if (fee > 0) lines += bdLine(locationDisplayLabels[loc] + ' add-on', '+$' + fee.toFixed(2) + ' / unit');
                }
            });

            // Back add-on (for non-DTF back decoration)
            var backLoc = activeLocationsByKey.back;
            if (backLoc && locHasArtwork(backLoc) && backLoc.decoration && backLoc.decoration !== 'dtf') {
                var backFee = getAddonFee('back', backLoc.decoration);
                if (backFee > 0) lines += bdLine(locationDisplayLabels.back + ' add-on', '+$' + backFee.toFixed(2) + ' / unit');
            }

            // Divider + per-unit subtotal
            lines += '<div class="breakdown-divider"></div>';
            lines += bdLine('Per unit', '$' + p.unitPrice.toFixed(2), true);
            bdLines.innerHTML = lines;

            // Footer: qty × price math
            var fhtml = '';
            var subtotalBeforeFees = p.total - p.tapeCharge;
            fhtml += bdLine(p.totalQty + ' units @ $' + p.unitPrice.toFixed(2), '$' + subtotalBeforeFees.toFixed(2));
            var hasEmbForFee = activeLocationEntries.some(function(e) { return locHasArtwork(e[1]) && e[1].decoration === 'embroidery'; });
            if (hasEmbForFee && total < EMBROIDERY_TAPE_WAIVE_QTY) {
                if (p.tapeCharge > 0) {
                    fhtml += bdLine('Embroidery digitizing fee', '$' + p.tapeCharge.toFixed(2));
                    fhtml += bdLine('Per unit (incl. fees)', '$' + p.avgUnit.toFixed(2));
                } else {
                    fhtml += bdLine('Embroidery digitizing fee', '<s style="opacity:.45">$' + EMBROIDERY_TAPE_FEE.toFixed(2) + '</s> &nbsp;$0.00 <span class="bl-hint">(waived)</span>');
                }
            }
            fhtml += '<div class="breakdown-total"><span class="bl-label">Estimated Total</span><span class="bl-value">$' + p.total.toFixed(2) + '</span></div>';
            bdFooter.innerHTML = fhtml;

            // Waive digitizing fee checkbox (visible only when embroidery is in the order AND qty < 24)
            var waiveWrap = document.getElementById('waiveTapeFeeWrap');
            if (waiveWrap) {
                var hasEmbInBreakdown = activeLocationEntries.some(function(e) { return locHasArtwork(e[1]) && e[1].decoration === 'embroidery'; });
                waiveWrap.style.display = (hasEmbInBreakdown && total < EMBROIDERY_TAPE_WAIVE_QTY) ? '' : 'none';
                var waiveCb = document.getElementById('waiveTapeFee');
                if (waiveCb) waiveCb.checked = state.waiveTapeFee;
            }
        } else {
            bdEl.style.display = 'none';
            var waiveWrap2 = document.getElementById('waiveTapeFeeWrap');
            if (waiveWrap2) waiveWrap2.style.display = 'none';
        }

        // Price break table
        var breakSection = document.getElementById('priceBreakSection');
        var breakBody = document.getElementById('priceBreakBody');
        if (state.garment && activeLocations.length > 0) {
            breakSection.style.display = '';
            breakBody.innerHTML = '';
            var tierQtys = hasNonDtf ? [1, 12, 24, 48, 100, 250] : [1, 12, 24, 48, 99];
            var dtfC = getDtfCombo();
            var hasEmbInOrder = activeLocationEntries.some(function(e) { return locHasArtwork(e[1]) && e[1].decoration === 'embroidery'; });
            tierQtys.forEach(function (q) {
                var tr = document.createElement('tr');
                var nextTier = tierQtys.find(function (t) { return t > q; });
                if (total >= q && (nextTier === undefined || total < nextTier)) {
                    tr.classList.add('current-tier');
                }

                var deco = 0;

                // DTF fee at the selected print size tier
                if (hasDtf && dtfC !== 'none') {
                    var psTier = getDtfPrintSizeTier();
                    deco += lookupTier(DTF_TIERS[psTier][dtfC], q);
                }

                // Non-DTF front/back fees
                activeLocationEntries.forEach(function (e) {
                    var loc = e[0], d = e[1];
                    if ((loc === 'front' || loc === 'back') && locHasArtwork(d) && d.decoration && d.decoration !== 'dtf') {
                        deco += getDecoFee(d.decoration, q);
                        if (loc === 'back') {
                            deco += getAddonFee('back', d.decoration);
                        }
                    }
                });

                // Add-on locations (sleeves — base fee + surcharge)
                activeLocationEntries.forEach(function (e) {
                    if (e[0] !== 'front' && e[0] !== 'back' && locHasArtwork(e[1]) && e[1].decoration) {
                        if (e[1].decoration !== 'dtf') {
                            deco += getDecoFee(e[1].decoration, q);
                        } else if (dtfC === 'none') {
                            var psTier = getDtfPrintSizeTier();
                            deco += lookupTier(DTF_TIERS[psTier]['front'], q);
                        }
                        deco += getAddonFee(e[0], e[1].decoration);
                    }
                });

                var rowP = weightedGarmentPrice() + deco;
                if (hasEmbInOrder && q < EMBROIDERY_TAPE_WAIVE_QTY && !state.waiveTapeFee) {
                    var tapePerUnit = EMBROIDERY_TAPE_FEE / (tr.classList.contains('current-tier') ? total : q);
                    rowP += tapePerUnit;
                }
                tr.innerHTML = '<td>' + q + '+</td><td colspan="2">$' + rowP.toFixed(2) + '</td>';
                breakBody.appendChild(tr);
            });
            var breakHead = breakSection.querySelector('thead tr');
            var feeSuffix = (hasEmbInOrder && !state.waiveTapeFee) ? ' (incl. fees)' : '';
            if (breakHead) {
                breakHead.innerHTML = '<th>Qty</th><th colspan="2">Price / unit' + feeSuffix + '</th>';
            }
        } else {
            breakSection.style.display = 'none';
        }
    }

    // Waive digitizing fee checkbox handler
    var waiveCbEl = document.getElementById('waiveTapeFee');
    if (waiveCbEl) {
        waiveCbEl.addEventListener('change', function () {
            state.waiveTapeFee = this.checked;
            populateReview();
        });
    }

    // ==========================================
    // Order Validation
    // ==========================================
    function validateOrder() {
        var errors = [];
        if (!state.category) errors.push('Please select a category.');
        if (!state.garment) errors.push('Please select a garment.');
        if (!state.color) errors.push('Please select a color.');

        var locsWithArt = getActiveLocationEntries().map(function (entry) { return entry[1]; });
        if (locsWithArt.length === 0) {
            errors.push('Please upload artwork for at least one location.');
        } else {
            var missingDeco = locsWithArt.filter(function (l) { return !l.decoration; });
            if (missingDeco.length > 0) {
                errors.push('Please select a decoration method for all artwork locations.');
            }
        }

        var total = getTotalUnits();
        if (total < 1) errors.push('Please add at least 1 unit.');

        // Live-price guard: prevent checkout for any selected upsize whose
        // live per-size cost has not loaded yet. Without this, 2XL/3XL/etc.
        // would silently fall back to the S/M base price and undercharge.
        // Uses isBaseSafeSize so the guard matches garmentPriceForSize's
        // actual fallback logic (handles single-size + S/M + M/L garments).
        if (state.garment && total > 0) {
            var missing = [];
            Object.keys(state.sizes).forEach(function (sz) {
                var qty = state.sizes[sz] || 0;
                if (qty <= 0) return;
                if (isBaseSafeSize(sz)) return;
                if (!hasLivePriceForSize(sz)) missing.push(sz);
            });
            if (missing.length) {
                var status = state.garment._livePriceStatus || '';
                if (status === 'failed') {
                    errors.push('Live pricing is unavailable for ' + missing.join(', ') + '. Please try again in a moment or contact us.');
                    // Trigger one more refetch so it may recover before the next click
                    fetchLivePricingForGarment();
                } else {
                    errors.push('Still loading live pricing for ' + missing.join(', ') + '. Please wait a moment and try again.');
                }
            }
        }

        return errors;
    }

    // ==========================================
    // Submit → Upload Logos → Add to Cart
    // ==========================================
    var _isSubmitting = false;

    btnSubmit.addEventListener('click', async function () {
        if (_isSubmitting) return;

        // Validate
        var errors = validateOrder();
        if (errors.length > 0) {
            alert('Please fix the following:\n\n• ' + errors.join('\n• '));
            return;
        }

        _isSubmitting = true;
        var submitText = btnSubmit.querySelector('.btn-label') || btnSubmit;
        var origText = submitText.textContent;
        submitText.textContent = 'Uploading artwork…';
        btnSubmit.disabled = true;

        try {
            // 1. Upload logos for all locations that have artwork
            var logoUrls = {};
            var uploadClient = window.__BUILDER_UPLOAD;

            if (uploadClient && uploadClient.upload) {
                var locKeys = Object.keys(getActiveLocationsObject());
                for (var i = 0; i < locKeys.length; i++) {
                    var locKey = locKeys[i];
                    var locData = state.locations[locKey];
                    var fileToUpload = locData.logoFile;
                    // AI-generated images have dataURL but no File — convert
                    if (!fileToUpload && locData.logoDataUrl && locData.aiMeta) {
                        fileToUpload = dataUrlToFile(locData.logoDataUrl, 'ai-concept.png', 'image/png');
                    }
                    if (fileToUpload) {
                        try {
                            submitText.textContent = 'Uploading image…';
                            var result = await uploadClient.uploadWithRetry(fileToUpload);
                            logoUrls[locKey] = result.url;
                        } catch (uploadErr) {
                            console.warn('[Builder] Upload failed for ' + locKey + ':', uploadErr);
                            // Continue without URL — order will still go through,
                            // logo file name will be in the properties
                        }
                    }
                }
            }

            // 2. Generate preview mockup images for all unique garment views.
            //    Sides with artwork get garment+logo composite; others get plain garment.
            var previewUrl = '';
            try {
                submitText.textContent = 'Generating previews…';

                // Determine which unique view sides this garment has images for
                var allViewSides = ['front', 'back', 'left-sleeve', 'right-sleeve'];
                var availableSides = allViewSides.filter(function (vs) {
                    return !!getGarmentImageUrl(state.garment, state.color, vs);
                });
                // De-duplicate sides that resolve to the same image,
                // but keep both sleeves (right-sleeve is flipped during render)
                // and keep mirrored beanie backs (also flipped).
                var seenUrls = {};
                var uniqueSides = availableSides.filter(function (vs) {
                    var url = getGarmentImageUrl(state.garment, state.color, vs);
                    var key = url + (vs === 'right-sleeve' ? '::right' : '') + (vs === 'back' && isBackMirrored(state.garment, state.color) ? '::back-mirror' : '');
                    if (seenUrls[key]) return false;
                    seenUrls[key] = true;
                    return true;
                });

                // Map view side → location key that has artwork (if any)
                var sideToArtLoc = {};
                getActiveLocationEntries().forEach(function (entry) {
                    var k = entry[0];
                    var vs = locationViewSide[k];
                    if (!sideToArtLoc[vs]) sideToArtLoc[vs] = k;
                });

                var previewUrls = [];
                for (var pi = 0; pi < uniqueSides.length; pi++) {
                    var side = uniqueSides[pi];
                    var previewBlob;
                    if (sideToArtLoc[side]) {
                        previewBlob = await generatePreviewBlob(sideToArtLoc[side]);
                    } else {
                        previewBlob = await generateGarmentOnlyBlob(side);
                    }
                    if (previewBlob && uploadClient && uploadClient.upload) {
                        var previewFile = new File([previewBlob], 'mockup-' + side + '.jpg', { type: 'image/jpeg' });
                        var previewResult = await uploadClient.uploadWithRetry(previewFile);
                        if (previewResult.url) previewUrls.push(previewResult.url);
                    }
                }
                previewUrl = previewUrls.join(' | ');
            } catch (previewErr) {
                console.warn('[Builder] Preview upload failed:', previewErr);
            }

            // 3. Build cart items via variant map
            var variants = window.__BUILDER_VARIANTS;
            var total = getTotalUnits();
            var unitPrice = calcUnitPrice(total);

            if (variants && variants.findTierVariant) {
                // Real Shopify cart integration
                submitText.textContent = 'Adding to cart…';

                // Build per-location logo URL map. Each location becomes its own
                // cart line-item property with the bare URL as the value so Shopify
                // admin auto-renders it as a clickable link (value must be a pure
                // URL — mixing label + URL breaks Shopify's auto-linking).
                // Append ?download=1 (or &download=1) so tapping the link in the
                // Shopify admin triggers a proper Save dialog instead of opening
                // the file inline — critical for mobile Shopify admin users.
                var forceDownload = function (u) {
                    if (!u) return u;
                    return u + (u.indexOf('?') === -1 ? '?' : '&') + 'download=1';
                };
                var logoUrlMap = {};
                Object.keys(logoUrls).forEach(function (k) {
                    var d = state.locations[k];
                    var method = d.decoration ? ' (' + (methodLabels[d.decoration] || d.decoration) + ')' : '';
                    // Property name shown in admin; value is bare URL so Shopify makes it clickable.
                    var propName = locationDisplayLabels[k] + ' Artwork' + method;
                    logoUrlMap[propName] = forceDownload(logoUrls[k]);
                });

                // Ensure every location with artwork has a decoration stamped
                // (guards against any state corruption between review and submit)
                var allowed = getAllowedMethods(state.category);
                if (!allowed.includes(state.decoration)) {
                    state.decoration = allowed[0];
                }
                Object.keys(state.locations).forEach(function (k) {
                    var loc = state.locations[k];
                    if (locHasArtwork(loc) && !loc.decoration) {
                        loc.decoration = state.decoration;
                    }
                    if (locHasArtwork(loc) && loc.decoration && !allowed.includes(loc.decoration)) {
                        loc.decoration = allowed[0];
                    }
                });

                // Save builder state to server for edit-from-cart / admin / sharing
                var editStateId = '';
                try {
                    submitText.textContent = 'Saving build state…';
                    editStateId = await uploadClient.saveState(serializeState());
                } catch (e) {
                    console.warn('[Builder] State save failed:', e);
                }

                var cartItems = variants.buildCartItems({
                    unitPrice: unitPrice,
                    totalQty: total,
                    garment: state.garment,
                    colorName: state.colorName || getColorName(state.color),
                    colorHex: state.color,
                    sizes: state.sizes,
                    locations: getActiveLocationsObject(),
                    decoration: state.decoration,
                    locationLabels: Object.assign({}, locationDisplayLabels),
                    notes: state.notes,
                    logoUrlMap: logoUrlMap,
                    // Cart drawer slider: pipe-joined URLs for multi-side preview
                    previewSlides: previewUrls.length ? previewUrls.join('|') : undefined,
                    // Cart thumbnail + admin preview link: first (front) mockup only.
                    // Do NOT join multiple URLs with ' | ' here — the value is used
                    // directly as an <img src> and must be a single valid URL.
                    previewUrl: previewUrls[0] || undefined,
                    editKey: editStateId || undefined,
                    waiveTapeFee: state.waiveTapeFee || false,
                    printSizeTier: getDtfPrintSizeTier(),
                });

                var success = await variants.addToShopifyCart(cartItems);
                if (!success) throw new Error('Failed to add items to cart');

                // Load fresh cart HTML directly (bypass merge animations that cause blank space)
                var cartDrawer = document.querySelector('.js-cart-drawer');
                var cartForm = cartDrawer && cartDrawer.querySelector('cart-form');
                if (cartForm && cartForm.dataset.sectionId) {
                    var sectionResp = await fetch(
                        (window.Shopify.routes.root || '/') + '?section_id=' + cartForm.dataset.sectionId
                    );
                    if (sectionResp.ok) {
                        var html = await sectionResp.text();
                        var tmp = document.createElement('div');
                        tmp.innerHTML = html;
                        tmp.querySelectorAll('[data-cc-animate]').forEach(function (el) {
                            el.removeAttribute('data-cc-animate');
                        });
                        var freshForm = tmp.querySelector('cart-form');
                        if (freshForm) cartForm.innerHTML = freshForm.innerHTML;
                    }
                }

                // Open cart drawer after content is fully loaded
                if (cartDrawer && typeof cartDrawer.open === 'function') {
                    cartDrawer.open();
                } else {
                    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
                }
            }

            // 3. Show success modal
            var modal = document.getElementById('successModal');
            modal.classList.add('active');

            // Update cart badge
            var badge = document.querySelector('.cart-count');
            if (badge) {
                var current = parseInt(badge.textContent) || 0;
                badge.textContent = current + total;
            }

            // Clear saved state since order is submitted
            clearSave();

        } catch (err) {
            console.error('[Builder] Submit error:', err);
            alert('Something went wrong adding your order to the cart. Please try again.\n\n' + (err.message || ''));
        } finally {
            _isSubmitting = false;
            submitText.textContent = origText;
            btnSubmit.disabled = false;
        }
    });

    // Close modal on overlay click
    document.getElementById('successModal').addEventListener('click', function (e) {
        if (e.target === this) {
            this.classList.remove('active');
        }
    });

    // ==========================================
    // Header (basic — from main script)
    // ==========================================
    const headerWrapper = document.querySelector('.header-wrapper');

    // Mobile Drawer
    const menuToggle = document.querySelector('.mobile-menu-toggle');
    const drawer = document.querySelector('.mobile-drawer');
    const overlay = document.querySelector('.mobile-menu-overlay');
    const drawerClose = document.querySelector('.drawer-close');

    if (menuToggle) {
        // Drawer disabled for mockup
    }

    function closeDrawer() { /* disabled for mockup */ }

    if (drawerClose) drawerClose.addEventListener('click', closeDrawer);
    if (overlay) overlay.addEventListener('click', closeDrawer);

    document.querySelectorAll('.drawer-nav a').forEach(link => {
        link.addEventListener('click', closeDrawer);
    });

    // ==========================================
    // Sticky Bottom Selections Bar
    // ==========================================
    const selectionsBar = document.getElementById('selectionsBar');

    function updateSelectionsBar() {
        if (!selectionsBar) return;
        const catLabels = {
            tshirts: 'T-Shirts', hoodies: 'Hoodies', hats: 'Hats',
            longsleeves: 'Long Sleeves', crewnecks: 'Crewnecks',
            polos: 'Polos', jackets: 'Jackets', tanks: 'Tanks',
            sweatpants: 'Sweatpants', workpants: 'Work Pants',
            beanies: 'Beanies', shorts: 'Shorts',
            vests: 'Vests', bags: 'Bags', quarterzips: '1/4 Zips'
        };

        // Category
        setChip('chipCategory', state.category ? catLabels[state.category] : null, 'fa-layer-group');

        // Garment
        setChip('chipGarment', state.garment ? state.garment.name : null, 'fa-shirt');

        // Color
        const chipColor = document.getElementById('chipColor');
        const colorDot = document.getElementById('chipColorDot');
        if (state.color) {
            chipColor.classList.remove('placeholder');
            chipColor.classList.add('filled');
            chipColor.querySelector('span:last-child').textContent = getColorName(state.color);
            colorDot.style.background = state.color;
        } else {
            chipColor.classList.add('placeholder');
            chipColor.classList.remove('filled');
            chipColor.querySelector('span:last-child').textContent = 'Color';
            colorDot.style.background = '';
        }

        // Decoration
        setChip('chipDecoration', state.decoration ? methodLabels[state.decoration] : null, 'fa-paint-roller');

        // Artwork count
        const count = artworkCount();
        const chipArtwork = document.getElementById('chipArtwork');
        chipArtwork.querySelector('span').textContent = 'Artwork: ' + count;
        if (count > 0) {
            chipArtwork.classList.remove('placeholder');
            chipArtwork.classList.add('filled');
        } else {
            chipArtwork.classList.add('placeholder');
            chipArtwork.classList.remove('filled');
        }

        // Qty
        const total = getTotalUnits();
        const chipQty = document.getElementById('chipQty');
        chipQty.querySelector('span').textContent = 'Qty: ' + total;
        if (total > 0) {
            chipQty.classList.remove('placeholder');
            chipQty.classList.add('filled');
        } else {
            chipQty.classList.add('placeholder');
            chipQty.classList.remove('filled');
        }

        // Price
        const unitPrice = calcUnitPrice(total);
        const totalPrice = unitPrice * total;
        const chipPrice = document.getElementById('chipPrice');
        chipPrice.querySelector('span').textContent = '$' + totalPrice.toFixed(2);
        if (totalPrice > 0) {
            chipPrice.classList.remove('placeholder', 'filled');
            chipPrice.classList.add('highlight');
        } else {
            chipPrice.classList.add('placeholder');
            chipPrice.classList.remove('filled', 'highlight');
        }

        // Show bar once at least one selection exists
        const hasAnySelection = state.category || state.garment || state.decoration || artworkCount() > 0 || total > 0;
        quickSelectBar.classList.toggle('visible', !!hasAnySelection);

        // Highlight chip matching current step
        highlightCurrentStepChip();


    }

    // Highlight the chip(s) that belong to the current step
    function highlightCurrentStepChip() {
        document.querySelectorAll('.selection-chip[data-step]').forEach(chip => {
            chip.classList.toggle('current-step', parseInt(chip.dataset.step) === state.currentStep);
        });
    }

    // Click chip → navigate to that step
    document.querySelectorAll('.selection-chip[data-step]').forEach(chip => {
        chip.addEventListener('click', () => {
            const targetStep = parseInt(chip.dataset.step);
            goToStep(targetStep);
            highlightCurrentStepChip();
        });
    });

    function setChip(id, value, iconClass) {
        const chip = document.getElementById(id);
        const span = chip.querySelector('span');
        if (value) {
            chip.classList.remove('placeholder');
            chip.classList.add('filled');
            span.textContent = value;
        } else {
            chip.classList.add('placeholder');
            chip.classList.remove('filled');
            // Reset to default label
            const defaults = {
                chipCategory: 'Category', chipGarment: 'Garment',
                chipDecoration: 'Decoration', chipArtwork: 'Artwork'
            };
            span.textContent = defaults[id] || '';
        }
    }

    // Patch updatePreview to also update the bar
    const _origUpdatePreview = updatePreview;
    updatePreview = function () {
        _origUpdatePreview();
        updateSelectionsBar();
    };

    // Also patch updateTotals
    const _origUpdateTotals = updateTotals;
    updateTotals = function () {
        _origUpdateTotals();
        updateSelectionsBar();
    };

    // File upload also needs to trigger bar update
    const _origHandleFile = handleFile;
    handleFile = function (file) {
        _origHandleFile(file);
        updateSelectionsBar();
    };

    // ==========================================
    // Quick-Select Toolbar
    // ==========================================
    const quickSelectBar = document.getElementById('quickSelectBar');
    const qsCategory = document.getElementById('qsCategory');
    const qsGarment = document.getElementById('qsGarment');
    const qsColors = document.getElementById('qsColors');
    const qsDecoration = document.getElementById('qsDecoration');

    // Show/hide toolbar with selections bar
    function updateQuickSelectVisibility() {
        const total = getTotalUnits();
        const hasAny = state.category || state.garment || state.decoration || artworkCount() > 0 || total > 0;
        quickSelectBar.classList.toggle('visible', !!hasAny);
    }

    // Sync toolbar selects FROM state
    function syncQuickSelectFromState() {
        qsCategory.value = state.category || '';

        // Rebuild garment dropdown if category exists
        rebuildQsGarments();

        if (state.garment) {
            qsGarment.value = state.garment.id;
        }

        rebuildQsColors();

        qsDecoration.value = state.decoration || '';
    }

    function rebuildQsGarments() {
        qsGarment.innerHTML = '';
        if (!state.category) {
            qsGarment.innerHTML = '<option value="">Pick category first</option>';
            qsGarment.disabled = true;
            return;
        }
        qsGarment.disabled = false;
        const items = garments[state.category] || [];
        qsGarment.innerHTML = '<option value="">Select...</option>' +
            items.map(g => `<option value="${g.id}">${g.name}</option>`).join('');
        if (state.garment) qsGarment.value = state.garment.id;
    }

    function rebuildQsColors() {
        qsColors.innerHTML = '';
        if (!state.garment) {
            qsColors.innerHTML = '<span class="qs-color-empty">\u2014</span>';
            return;
        }
        state.garment.colors.forEach(colorObj => {
            if (!colorHasRenderableImages(state.garment, colorObj)) return;
            const dot = document.createElement('button');
            dot.className = 'qs-color-dot' + (state.colorName === colorObj.name ? ' selected' : '');
            dot.style.backgroundColor = colorObj.hex;
            dot.dataset.color = colorObj.hex;
            dot.dataset.name = colorObj.name;
            applyAvailabilityToColorControl(dot, colorObj);
            dot.addEventListener('click', () => {
                if (dot.disabled || isColorKnownOutOfStock(colorObj)) return;
                selectColor(colorObj.name);
                rebuildQsColors();
            });
            qsColors.appendChild(dot);
        });
    }

    // Category change
    qsCategory.addEventListener('change', function () {
        const cat = this.value;
        if (!cat) return;
        state.category = cat;
        // Sync main step 1 cards
        document.querySelectorAll('.category-card').forEach(c =>
            c.classList.toggle('selected', c.dataset.category === cat)
        );
        populateGarments(cat);
        updateLocationLabels();
        updateDecoMethodsForCategory();
        rebuildQsGarments();
        rebuildQsColors();
        updateLogoPosition();
        updateViewAvailability();
        updatePreview();
    });

    // Garment change
    qsGarment.addEventListener('change', function () {
        const id = this.value;
        if (!id || !state.category) return;
        const items = garments[state.category] || [];
        const garment = items.find(g => g.id === id);
        if (!garment) return;
        selectGarment(garment, { userInitiated: true });
        rebuildQsColors();
    });

    // Decoration change
    qsDecoration.addEventListener('change', function () {
        const method = this.value;
        if (!method) return;
        state.decoration = method;
        // If active location has artwork, update its decoration
        const loc = activeLoc();
        if (locHasArtwork(loc)) {
            loc.decoration = method;
        }
        // Sync main step 3 cards
        syncDecorationCards(method);
        updatePreview();
        updateLocFileBar();
        updateLocationStatus();
        updateLocationSummary();
        updateNextBtn();
    });

    // Keep toolbar in sync whenever preview updates
    const _origUpdatePreview2 = updatePreview;
    updatePreview = function () {
        _origUpdatePreview2();
        syncQuickSelectFromState();
    };

    // ==========================================
    // Mobile Preview FAB / Drawer
    // ==========================================
    const previewPanel = document.getElementById('previewPanel');
    const previewFab = document.getElementById('previewFab');
    const previewClose = document.getElementById('previewClose');
    const previewBackdrop = document.getElementById('previewBackdrop');
    const inlinePreviewSlot = document.getElementById('inlinePreviewSlot');
    const inlinePreviewSlotStep3 = document.getElementById('inlinePreviewSlotStep3');
    const builderLayout = document.querySelector('.builder-layout');
    let previewScrollLockY = 0;

    function isMobile() {
        return window.matchMedia('(max-width: 1024px)').matches;
    }

    function lockPreviewScroll() {
        if (document.body.dataset.previewScrollLocked === 'true') return;

        previewScrollLockY = window.scrollY || window.pageYOffset || 0;
        document.body.dataset.previewScrollLocked = 'true';
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
        document.addEventListener('touchmove', _trapDrawerScroll, { passive: false });
    }

    /* Allow touch-scroll inside the open drawer; block everywhere else */
    function _trapDrawerScroll(e) {
        if (previewPanel.classList.contains('preview-open') && previewPanel.contains(e.target)) return;
        /* Allow horizontal scrollable strips (color swatches, filter chips, deco pills) */
        if (e.target.closest('#colorSwatches, .color-swatches-wrap, .filter-chips, .deco-pills-scroll')) return;
        e.preventDefault();
    }

    function unlockPreviewScroll(skipRestore) {
        if (document.body.dataset.previewScrollLocked !== 'true') return;

        delete document.body.dataset.previewScrollLocked;
        document.documentElement.style.overflow = '';
        document.body.style.overflow = '';
        document.removeEventListener('touchmove', _trapDrawerScroll);
        if (!skipRestore) window.scrollTo(0, previewScrollLockY);
    }

    function openPreviewDrawer() {
        updatePreviewDrawerOffset();
        previewPanel.classList.add('preview-open');
        previewBackdrop.classList.add('active');
        previewFab.style.display = 'none';
        lockPreviewScroll();
        syncControlsVisibility();
    }
    function closePreviewDrawer(skipScrollRestore) {
        var wasOpen = previewPanel.classList.contains('preview-open');
        previewPanel.classList.remove('preview-open');
        previewBackdrop.classList.remove('active');
        updatePreviewDrawerOffset();
        updatePreviewFabVisibility();
        if (wasOpen) unlockPreviewScroll(skipScrollRestore);
        syncControlsVisibility();
    }

    function handleInlinePreview(step) {
        closePreviewDrawer(true);
        if (isMobile() && step === 3) {
            // Move preview into step 3 slot
            previewPanel.classList.remove('preview-open');
            inlinePreviewSlotStep3.appendChild(previewPanel);
            previewPanel.classList.add('preview-inline');
            previewFab.style.display = 'none';
        } else if (isMobile() && step === 5) {
            // Move preview into step 5 review slot (read-only confirmation view)
            var slot5 = document.getElementById('inlinePreviewSlotStep5');
            if (slot5) {
                previewPanel.classList.remove('preview-open');
                slot5.appendChild(previewPanel);
                previewPanel.classList.add('preview-inline');
                previewFab.style.display = 'none';
            }
        } else {
            // Move preview back to grid layout
            previewPanel.classList.remove('preview-inline');
            builderLayout.appendChild(previewPanel);
            updatePreviewFabVisibility();
        }
        // After DOM move, refresh logo position once layout settles
        requestAnimationFrame(function () {
            updateLogoPosition();
            applyLogoTransform();
        });
    }

    // Re-evaluate inline preview placement on resize (e.g. DevTools breakpoint change)
    let resizeTimer;
    window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
            var isDrawerStep = isMobile() && state.currentStep !== 3 && state.currentStep !== 5;
            var drawerWasOpen = previewPanel.classList.contains('preview-open');

            if (drawerWasOpen && isDrawerStep) {
                updatePreviewFabOffset();
                updatePreviewFabVisibility();
                updatePreviewDrawerOffset();
                requestAnimationFrame(function () {
                    updateLogoPosition();
                    applyLogoTransform();
                });
                return;
            }

            handleInlinePreview(state.currentStep);
            updatePreviewFabOffset();
            updatePreviewFabVisibility();
            updatePreviewDrawerOffset();
        }, 150);
    });

    // Keep logo position & transform in sync when the garment container resizes
    // (CSS breakpoint changes, panel DOM moves, orientation changes)
    if (typeof ResizeObserver !== 'undefined') {
        var gcPrevW = garmentContainer.offsetWidth;
        var gcPrevH = garmentContainer.offsetHeight;
        new ResizeObserver(function () {
            var w = garmentContainer.offsetWidth;
            var h = garmentContainer.offsetHeight;
            if (w === gcPrevW && h === gcPrevH) return;
            gcPrevW = w;
            gcPrevH = h;
            requestAnimationFrame(function () {
                updateLogoPosition();
                applyLogoTransform();
            });
        }).observe(garmentContainer);
    }

    previewFab.addEventListener('click', openPreviewDrawer);
    previewClose.addEventListener('click', function () { closePreviewDrawer(); });
    previewBackdrop.addEventListener('click', function () { closePreviewDrawer(); });

    // ==========================================
    // Decoration Info Popup
    // ==========================================
    const decoInfoToggle = document.getElementById('decoInfoToggle');
    const decoInfoBackdrop = document.getElementById('decoInfoBackdrop');
    const decoInfoClose = document.getElementById('decoInfoClose');

    if (decoInfoToggle && decoInfoBackdrop) {
        decoInfoToggle.addEventListener('click', function (e) {
            e.preventDefault();
            decoInfoBackdrop.classList.add('open');
        });
        decoInfoClose.addEventListener('click', function () {
            decoInfoBackdrop.classList.remove('open');
        });
        decoInfoBackdrop.addEventListener('click', function (e) {
            if (e.target === decoInfoBackdrop) decoInfoBackdrop.classList.remove('open');
        });
    }

    // ==========================================
    // Digitizing Fee Info Popup
    // ==========================================
    const waiveInfoToggle = document.getElementById('waiveInfoToggle');
    const waiveInfoBackdrop = document.getElementById('waiveInfoBackdrop');
    const waiveInfoClose = document.getElementById('waiveInfoClose');

    if (waiveInfoToggle && waiveInfoBackdrop) {
        waiveInfoToggle.addEventListener('click', function (e) {
            e.preventDefault();
            e.stopPropagation(); // prevent checkbox label from toggling
            waiveInfoBackdrop.classList.add('open');
        });
        waiveInfoClose.addEventListener('click', function () {
            waiveInfoBackdrop.classList.remove('open');
        });
        waiveInfoBackdrop.addEventListener('click', function (e) {
            if (e.target === waiveInfoBackdrop) waiveInfoBackdrop.classList.remove('open');
        });
    }

    // ==========================================
    // Pill-bar scroll-fade hints + arrow buttons
    // ==========================================
    const pillsWrap = document.getElementById('decoPillsWrap');
    const pillArrowL = document.getElementById('pillArrowLeft');
    const pillArrowR = document.getElementById('pillArrowRight');
    if (pillsWrap) {
        const scroller = pillsWrap.querySelector('.deco-pills-scroll');
        function updatePillFade() {
            const sl = scroller.scrollLeft;
            const maxScroll = scroller.scrollWidth - scroller.clientWidth;
            const canLeft = sl > 4;
            const canRight = sl < maxScroll - 4;
            pillsWrap.classList.toggle('fade-left', canLeft);
            pillsWrap.classList.toggle('fade-right', canRight);
            if (pillArrowL) pillArrowL.classList.toggle('visible', canLeft);
            if (pillArrowR) pillArrowR.classList.toggle('visible', canRight);
        }
        scroller.addEventListener('scroll', updatePillFade, { passive: true });
        if (pillArrowL) pillArrowL.addEventListener('click', () => { scroller.scrollBy({ left: -120, behavior: 'smooth' }); });
        if (pillArrowR) pillArrowR.addEventListener('click', () => { scroller.scrollBy({ left: 120, behavior: 'smooth' }); });
        // Initial check (after layout settles)
        requestAnimationFrame(updatePillFade);
        new ResizeObserver(updatePillFade).observe(scroller);
    }

    // ==========================================
    // Generic scroll-fade edge hints
    // ==========================================
    function initScrollFade(el) {
        if (!el) return;
        el.classList.add('scroll-fade');
        function update() {
            var sl = el.scrollLeft;
            var max = el.scrollWidth - el.clientWidth;
            el.classList.toggle('fade-left', sl > 4);
            el.classList.toggle('fade-right', sl < max - 4);
        }
        el.addEventListener('scroll', update, { passive: true });
        new ResizeObserver(update).observe(el);
        requestAnimationFrame(update);
    }

    // Color swatch bar (uses wrapper pseudo-elements, not .scroll-fade class)
    (function () {
        var wrap = document.querySelector('.color-swatches-wrap');
        var strip = document.getElementById('colorSwatches');
        if (!wrap || !strip) return;
        function updateWrapFade() {
            var sl = strip.scrollLeft;
            var max = strip.scrollWidth - strip.clientWidth;
            wrap.classList.toggle('fade-left', sl > 4);
            wrap.classList.toggle('fade-right', sl < max - 4);
        }
        strip.addEventListener('scroll', updateWrapFade, { passive: true });
        new ResizeObserver(updateWrapFade).observe(strip);
        requestAnimationFrame(updateWrapFade);
    })();

    // Filter chips (brand pills)
    initScrollFade(document.querySelector('.filter-chips'));
    // Progress bar (mobile)
    initScrollFade(document.querySelector('.builder-progress'));
    // Location tabs (mobile)
    initScrollFade(document.querySelector('.location-tabs'));

    // ==========================================
    // Session Toolbar Buttons
    // ==========================================
    var btnExport = document.getElementById('btnExportState');
    var btnImport = document.getElementById('btnImportState');
    var btnStartOverToolbar = document.getElementById('btnStartOver');

    if (btnExport) btnExport.addEventListener('click', function () { closeOverflow(); exportState(); });
    if (btnImport) btnImport.addEventListener('click', function () { closeOverflow(); importState(); });
    var btnExportMockup = document.getElementById('btnExportMockup');
    if (btnExportMockup) btnExportMockup.addEventListener('click', function () { closeOverflow(); exportMockupImages(); });

    // Overflow menu toggle
    var btnMore = document.getElementById('btnToolbarMore');
    var overflowMenu = document.getElementById('toolbarOverflowMenu');
    function closeOverflow() {
        if (overflowMenu) overflowMenu.classList.remove('open');
        if (btnMore) btnMore.setAttribute('aria-expanded', 'false');
    }
    if (btnMore && overflowMenu) {
        btnMore.addEventListener('click', function (e) {
            e.stopPropagation();
            var isOpen = overflowMenu.classList.toggle('open');
            btnMore.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        });
        document.addEventListener('click', function (e) {
            if (!overflowMenu.contains(e.target) && e.target !== btnMore) closeOverflow();
        });
    }

    // Share Build — upload state to server and show shareable link popup
    var btnShare = document.getElementById('btnShareBuild');
    var shareModal = document.getElementById('shareModal');
    var shareModalIcon = document.getElementById('shareModalIcon');
    var shareModalTitle = document.getElementById('shareModalTitle');
    var shareModalDesc = document.getElementById('shareModalDesc');
    var shareLinkRow = document.getElementById('shareLinkRow');
    var shareLinkInput = document.getElementById('shareLinkInput');
    var shareCopyBtn = document.getElementById('shareCopyBtn');
    var shareModalBtns = document.getElementById('shareModalBtns');
    var shareModalDone = document.getElementById('shareModalDone');
    var shareModalClose = document.getElementById('shareModalClose');

    function closeShareModal() {
        if (shareModal) shareModal.classList.remove('active');
    }
    if (shareModalDone) shareModalDone.addEventListener('click', closeShareModal);
    if (shareModalClose) shareModalClose.addEventListener('click', closeShareModal);
    if (shareModal) shareModal.addEventListener('click', function (e) {
        if (e.target === shareModal) closeShareModal();
    });

    if (shareCopyBtn) shareCopyBtn.addEventListener('click', function () {
        if (shareLinkInput && shareLinkInput.value) {
            navigator.clipboard.writeText(shareLinkInput.value).then(function () {
                shareCopyBtn.innerHTML = '<i class="fas fa-check"></i> Copied!';
                setTimeout(function () { shareCopyBtn.innerHTML = '<i class="fas fa-copy"></i> Copy'; }, 2000);
            });
            shareLinkInput.select();
        }
    });

    if (btnShare) btnShare.addEventListener('click', async function () {
        var uploadClient = window.__BUILDER_UPLOAD;
        if (!uploadClient || !uploadClient.saveState) {
            alert('Share is not available — upload API not configured.');
            return;
        }
        var stateData = serializeState();
        if (!stateData.category && !stateData.garmentId) {
            alert('Nothing to share yet — select a garment first.');
            return;
        }
        // Reset modal to saving state and show it
        shareModalIcon.innerHTML = '<i class="fas fa-spinner fa-spin"></i>';
        shareModalIcon.className = 'modal-icon';
        shareModalTitle.textContent = 'Saving\u2026';
        shareModalDesc.textContent = 'Generating your shareable link';
        shareLinkRow.style.display = 'none';
        shareModalBtns.style.display = 'none';
        shareCopyBtn.innerHTML = '<i class="fas fa-copy"></i> Copy';
        shareModal.classList.add('active');

        try {
            var stateId = await uploadClient.saveState(stateData);
            var shareUrl = window.location.origin + '/pages/custom-order-builder?edit=' + stateId;

            // Switch to success state
            shareModalIcon.innerHTML = '<i class="fas fa-link"></i>';
            shareModalIcon.style.color = 'var(--accent, #009de0)';
            shareModalTitle.textContent = 'Share Your Build';
            shareModalDesc.textContent = 'Copy the link below to share this build with anyone.';
            shareLinkInput.value = shareUrl;
            shareLinkRow.style.display = 'flex';
            shareModalBtns.style.display = 'flex';
        } catch (err) {
            console.error('[Builder] Share failed:', err);
            shareModalIcon.innerHTML = '<i class="fas fa-circle-exclamation"></i>';
            shareModalIcon.style.color = '#ef4444';
            shareModalTitle.textContent = 'Share Failed';
            shareModalDesc.textContent = 'Could not generate a share link. Please try again.';
            shareModalBtns.style.display = 'flex';
        }
    });

    if (btnStartOverToolbar) btnStartOverToolbar.addEventListener('click', function () {
        closeOverflow();
        if (confirm('Start over? This will clear all your selections and uploaded artwork.')) {
            resetBuilderState();
        }
    });

    // ==========================================
    // Init — check for saved state
    // ==========================================
    var saved = getSavedState();
    if (saved && saved.v && (saved.category || saved.garmentId || Object.values(saved.locations || {}).some(l => l.logoDataUrl))) {
        // Show resume banner
        var banner = document.getElementById('resumeBanner');
        var detail = document.getElementById('resumeDetail');
        if (banner) {
            // Build a short summary
            var parts = [];
            if (saved.category) parts.push(saved.category);
            if (saved.garmentId) {
                var items = garments[saved.category] || [];
                var g = items.find(function (x) { return x.id === saved.garmentId; });
                if (g) parts[parts.length - 1] = g.name;
            }
            var artCount = Object.values(saved.locations || {}).filter(l => l.logoDataUrl).length;
            if (artCount) parts.push(artCount + ' artwork' + (artCount > 1 ? 's' : ''));
            if (saved.savedAt) {
                var d = new Date(saved.savedAt);
                parts.push('saved ' + d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
            }
            if (detail) detail.textContent = parts.join(' · ');
            banner.style.display = '';
            // Scroll so the resume banner is visible
            requestAnimationFrame(function () {
                banner.scrollIntoView({ behavior: 'smooth', block: 'center' });
            });

            document.getElementById('resumeResume').addEventListener('click', function () {
                banner.style.display = 'none';
                restoreFromSaved(saved);
                autoSave();
            });
            document.getElementById('resumeStartOver').addEventListener('click', function () {
                banner.style.display = 'none';
                clearSave();
                goToStep(1);
                updatePreview();
                updateSelectionsBar();
                syncQuickSelectFromState();
            });
        }
    }

    // Default init (start clean if no resume)
    goToStep(1);
    updatePreview();
    updateSelectionsBar();
    syncQuickSelectFromState();

    // ==========================================
    // URL Parameters: ?category=, ?garment=, ?color=
    // ==========================================
    (function () {
        var params = new URLSearchParams(window.location.search);

        // ?edit=<stateId> — restore full builder state from server (cart edit, admin, share)
        // Falls back to localStorage for backward compat with existing cart items.
        var editKey = params.get('edit');
        if (editKey) {
            var uploadClient = window.__BUILDER_UPLOAD;

            // Try server-side state first
            if (uploadClient && uploadClient.loadState) {
                uploadClient.loadState(editKey).then(function (editState) {
                    if (editState && editState.v) {
                        restoreFromSaved(editState);
                        autoSave();
                        window.history.replaceState(null, '', window.location.pathname);
                    }
                }).catch(function () {
                    // Server load failed — try localStorage fallback
                    tryLocalStorageFallback(editKey);
                });
                return; // async — skip category auto-select
            }

            // No upload client — try localStorage directly
            tryLocalStorageFallback(editKey);
            return;
        }

        function tryLocalStorageFallback(key) {
            try {
                var raw = localStorage.getItem(key);
                if (raw) {
                    var editState = JSON.parse(raw);
                    if (editState && editState.v) {
                        restoreFromSaved(editState);
                        autoSave();
                        window.history.replaceState(null, '', window.location.pathname);
                    }
                }
            } catch (e) { console.warn('[Builder] Failed to restore edit state:', e); }
        }

        var categoryAliases = {
            'tshirts': 'tshirts', 't-shirts': 'tshirts', 'tshirt': 'tshirts', 'shirts': 'tshirts',
            'longsleeves': 'longsleeves', 'long-sleeves': 'longsleeves', 'longsleeve': 'longsleeves',
            'hoodies': 'hoodies', 'hoodie': 'hoodies',
            'crewnecks': 'crewnecks', 'crewneck': 'crewnecks',
            'polos': 'polos', 'polo': 'polos',
            'tanks': 'tanks', 'tank': 'tanks', 'tanktops': 'tanks',
            'hats': 'hats', 'hat': 'hats', 'caps': 'hats',
            'quarterzips': 'quarterzips', 'quarter-zips': 'quarterzips', 'quarterzip': 'quarterzips',
            'jackets': 'jackets', 'jacket': 'jackets', 'outerwear': 'jackets',
            'sweatpants': 'sweatpants', 'joggers': 'sweatpants',
            'workpants': 'workpants', 'work-pants': 'workpants',
            'beanies': 'beanies', 'beanie': 'beanies',
            'vests': 'vests', 'vest': 'vests',
            'bags': 'bags', 'bag': 'bags', 'totes': 'bags', 'totebags': 'bags',
            'shorts': 'shorts', 'short': 'shorts',
            'scrubs': 'scrubs', 'medical': 'scrubs', 'healthcare': 'scrubs',
        };

        function normalizeParam(value) {
            return String(value || '')
                .trim()
                .toLowerCase()
                .replace(/^#/, '')
                .replace(/&/g, 'and')
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/^-+|-+$/g, '');
        }

        function resolveCategoryKey(rawValue) {
            if (!rawValue) return null;
            var normalized = String(rawValue).toLowerCase().replace(/\s+/g, '');
            return categoryAliases[normalized] || normalized;
        }

        function findGarmentMatch(list, rawValue) {
            if (!rawValue || !Array.isArray(list) || !list.length) return null;
            var target = normalizeParam(rawValue);
            return list.find(function (garment) {
                var candidates = [
                    garment.id,
                    garment.styleName,
                    garment.name,
                    garment.title,
                    garment.brand ? garment.brand + ' ' + garment.styleName : '',
                    garment.brand ? garment.brand + ' ' + garment.name : ''
                ];
                return candidates.some(function (candidate) {
                    return normalizeParam(candidate) === target;
                });
            }) || null;
        }

        function findColorMatch(garment, rawValue) {
            if (!garment || !rawValue) return null;
            var target = normalizeParam(rawValue);
            return (garment.colors || []).find(function (colorObj) {
                return [
                    colorObj.name,
                    colorObj.hex,
                    colorObj.hex2
                ].some(function (candidate) {
                    return normalizeParam(candidate) === target;
                });
            }) || null;
        }

        function resolveDeepLink(rawCategory, rawGarment) {
            var resolvedCategory = resolveCategoryKey(rawCategory);
            if (resolvedCategory && rawGarment) {
                var categoryMatch = findGarmentMatch(garments[resolvedCategory] || [], rawGarment);
                if (categoryMatch) {
                    return { category: resolvedCategory, garment: categoryMatch };
                }
            }

            if (!rawGarment) {
                return resolvedCategory ? { category: resolvedCategory, garment: null } : null;
            }

            var categoriesToSearch = resolvedCategory ? [resolvedCategory] : Object.keys(garments);
            for (var i = 0; i < categoriesToSearch.length; i++) {
                var catKey = categoriesToSearch[i];
                var garmentMatch = findGarmentMatch(garments[catKey] || [], rawGarment);
                if (garmentMatch) {
                    return { category: catKey, garment: garmentMatch };
                }
            }

            return resolvedCategory ? { category: resolvedCategory, garment: null } : null;
        }

        var deepLink = resolveDeepLink(
            params.get('category'),
            params.get('garment') || params.get('product') || params.get('style') || params.get('garment_id')
        );
        var requestedColor = params.get('color') || params.get('color_name');

        if (!deepLink || !deepLink.category) return;

        var catCard = document.querySelector('.category-card[data-category="' + deepLink.category + '"]');
        if (!catCard) return;

        var resumeBanner = document.getElementById('resumeBanner');
        if (resumeBanner) {
            resumeBanner.style.display = 'none';
        }

        catCard.click();

        if (deepLink.garment) {
            // Pick the brand pill filter so it's active when/if they go back
            if (deepLink.garment.brand) {
                var chipContainer = document.querySelector('.filter-chips');
                if (chipContainer) {
                    var allChips = chipContainer.querySelectorAll('.chip');
                    var targetChip = Array.from(allChips).find(function(c) {
                        return c.dataset.brand === deepLink.garment.brand;
                    });
                    if (targetChip) {
                        allChips.forEach(function(c) { c.classList.remove('active'); });
                        targetChip.classList.add('active');
                        document.querySelectorAll('.garment-card').forEach(function(card) {
                            card.style.display = (card.dataset.brand === targetChip.dataset.brand) ? '' : 'none';
                        });
                    }
                }
            }

            selectGarment(deepLink.garment, { deepLink: true });
            
            // Auto skip to step 2 (Garment + Color picker) via timeout to ensure DOM settles
            setTimeout(function() {
                if (typeof goToStep === 'function') {
                    goToStep(2);
                }
            }, 50);
        }

        if (requestedColor) {
            var matchedColor = findColorMatch(deepLink.garment || state.garment, requestedColor);
            if (matchedColor) {
                selectColor(matchedColor.name);
            }
        }
    })();

});
